import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";

import { useLocation } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap, LayersControl, Polyline, GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import Select, { components } from "react-select";
import { getDataSetNamePBI, useHttpClient } from "../modules/services/Bundle";
import { RequestMethod } from "../modules/services/core/_enums";
import { IAtivosModel, IGatewaysModel, ILocaisModel } from "../modules/services/core/_models";
import { TabulatorFull as Tabulator, ColumnComponent, CellComponent } from "tabulator-tables";
import "../../../node_modules/tabulator-tables/dist/css/tabulator_bootstrap4.css";
import "../../../node_modules/semantic-ui-css/components/icon.min.css";
import moment from "moment";
import { useAuth } from "../modules/auth/core/Auth";
import { FiltroGlobal, useFiltrosAtivos } from "../../components/FiltrosGlobais";

// Resposta do endpoint /organizacoes/resumo-locais (para QuantidadeAtivos por local na lista)
interface ResumoPerimetroItem {
  Perimetro: string;
  QuantidadeAtivos: number;
}
interface ResumoLocalItem {
  Organizacao: string;
  Local: string;
  Perimetros: ResumoPerimetroItem[];
}

// Resposta do endpoint /corporacoes/resumo-associacao (Disponível / Associado)
interface ResumoAssociacaoItem {
  IdOrganizacao: number;
  QtdAssociados: number;
  QtdNaoAssociados: number;
}

const dataSetNamePBI = getDataSetNamePBI();
const getMethodBorderColor = (metodo?: string): string => {
  if (!metodo) return '#3388ff'; // Cor padrão azul

  const method = metodo.toLowerCase();
  console.log(`🎨 Definindo cor para método: ${method}`);

  switch (method) {
    case 'trilateracao':
    case 'trilateração':
      // Cinza para trilateração (3 gateways)
      return '#4B4B4B';

    case 'centroide':
    case 'bilateracao':
    case 'bilateração':
      // Dourado para centroide/bilateração (2 gateways)
      return '#DAA520';

    case 'estimado':
    case 'estimativa':
      // Laranja para estimado (1 gateway)
      return '#FFA500';

    case 'gps':
      // Verde para GPS
      return '#32CD32';

    default:
      console.warn(`⚠️ Método desconhecido: ${method}, usando cor padrão`);
      return '#3388ff'; // Azul padrão para métodos desconhecidos
  }
};

// Função para criar ícone DIV a partir de SVG (para ícones personalizados de ativos)
const createDivIconFromSvg = (svgContent: string, metodo?: string): L.DivIcon => {
  // Sanitizar e decodificar o SVG se necessário
  let sanitizedSvg = (svgContent || '').trim();
  try {
    // Tentar decodificar se o SVG estiver encoded
    if (sanitizedSvg.includes('\\')) {
      sanitizedSvg = JSON.parse(`"${sanitizedSvg}"`);
    }
  } catch (e) {
    console.warn('Erro ao decodificar SVG:', e);
  }

  const borderColor = getMethodBorderColor(metodo);

  return L.divIcon({
    className: 'custom-svg-icon',
    html: `
      <div style="
        width: 28px; height: 28px; border-radius: 50%; background: #ffffff; border: 2px solid ${borderColor};
        display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 6px rgba(0,0,0,0.4);
        line-height: 0;
      ">
        <div style="
          width: 18px; height: 18px; display:flex; align-items:center; justify-content:center; overflow:hidden;
          line-height: 0; transform: translate(1px, 0.5px);
        ">
          ${sanitizedSvg}
        </div>
      </div>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 14]
  });
};

// Função para criar ícone DIV a partir de classe CSS (para ícones personalizados de ativos)
const createDivIconFromClass = (className: string, metodo?: string): L.DivIcon => {
  const safeClass = (className || '').trim();
  const borderColor = getMethodBorderColor(metodo);

  return L.divIcon({
    className: 'custom-class-icon',
    html: `
      <div style="
        width: 28px; height: 28px; border-radius: 50%; background: #ffffff; border: 2px solid ${borderColor};
        display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 6px rgba(0,0,0,0.4);
        color: ${borderColor};
        line-height: 0;
      ">
        <i class="ui icon ${safeClass}" style="
          font-size:16px; width:18px; height:18px; display:flex; align-items:center; justify-content:center;
          line-height: 0; transform: translate(1px, 1px);
        "></i>
      </div>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 14]
  });
};

const fetchIconsForAtivos = async (idsAtivos: number[], auth?: any, backendUrl?: string, iconCache?: Map<string, L.DivIcon | null>, metodo?: string, signal?: AbortSignal): Promise<Map<number, L.DivIcon | null>> => {
  // Mapa para armazenar os resultados
  const resultMap = new Map<number, L.DivIcon | null>();
  try {
    console.log(`🔍 Buscando ícones para ${idsAtivos.length} ativos:`, idsAtivos);

    if (!idsAtivos.length || !auth?.Token || !backendUrl) {
      console.log(`❌ Parâmetros inválidos:`, {
        quantidadeIds: idsAtivos.length,
        temToken: !!auth?.Token,
        temBackendUrl: !!backendUrl
      });
      return new Map();
    }
    const resultMap = new Map<number, L.DivIcon | null>();
    const idsParaBuscar: number[] = [];

    if (iconCache) {
      idsAtivos.forEach(id => {
        const key = `${id}_${metodo || 'default'}`;
        const cached = iconCache.get(key);
        if (cached !== undefined) {
          console.log(`📦 Ícone encontrado no cache para chave ${key}`);
          resultMap.set(id, cached);
        } else {
          idsParaBuscar.push(id);
        }
      });
    } else {
      idsParaBuscar.push(...idsAtivos);
    }

    // Se todos os ícones estão no cache, retornar
    if (!idsParaBuscar.length) {
      console.log(`✨ Todos os ícones encontrados no cache`);
      return resultMap;
    }

    console.log(`📡 Buscando ${idsParaBuscar.length} ícones na API:`, idsParaBuscar);

    const idApp = parseInt(process.env.REACT_APP_POWER_BI_APPLICATIONID || '0', 10) || 0;
    const url = `${backendUrl}/api/PrismTags/by-idsprism`;

    const resp = await fetch(url, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${auth.Token}`
      },
      body: JSON.stringify({ IdsPrism: idsParaBuscar, IdApp: idApp }),
      signal
    });

    if (!resp.ok) {
      console.warn(`❌ Erro na requisição:`, {
        status: resp.status,
        statusText: resp.statusText
      });
      return resultMap;
    }

    const response = await resp.json();
    console.log(`📥 Resposta da API:`, {
      sucesso: response?.IsRequestSuccessful,
      temData: !!response?.Data,
      quantidadeItens: response?.Data?.length || 0
    });

    if (!response?.Data || !Array.isArray(response.Data)) {
      console.warn(`⚠️ Resposta inválida da API`);
      return resultMap;
    }

    // Processar cada ícone retornado
    for (const item of response.Data) {
      const idAtivo = item.IdPrism;
      console.log(`🎨 Processando ícone para ID ${idAtivo}:`, {
        codigo: item?.Codigo,
        nome: item?.Name
      });

      let value = item?.Value;
      if (typeof value === 'string') {
        try {
          value = JSON.parse(value);
          console.log(`✅ Value parseado com sucesso para ID ${idAtivo}`);
        } catch (e) {
          console.warn(`⚠️ Erro ao parsear Value para ID ${idAtivo}:`, e);
          continue;
        }
      }

      const svg: string | undefined = value?.SVG;
      const klass: string | undefined = value?.Class;

      let icon: L.DivIcon | null = null;
      if (svg && svg.trim()) {
        icon = createDivIconFromSvg(svg, metodo);
        console.log(`✨ Ícone SVG criado para ID ${idAtivo}`);
      } else if (klass && klass.trim()) {
        icon = createDivIconFromClass(klass, metodo);
        console.log(`✨ Ícone Class criado para ID ${idAtivo}`);
      }

      // Atualizar cache e resultado
      if (iconCache) {
        iconCache.set(idAtivo, icon);
      }
      resultMap.set(idAtivo, icon);
    }

    return resultMap;
  } catch (e) {
    console.error('❌ Erro ao buscar ícones:', e);
    return resultMap;
  }
};

// Função para criar ícone padrão de ativo (fallback)
const createDefaultAtivoIcon = (metodo?: string): L.DivIcon => {
  const borderColor = getMethodBorderColor(metodo);

  // Definir cor de fundo baseada no método
  let backgroundColor = '#ff6b35'; // Cor padrão laranja
  let borderWidth = '3px';
  let opacity = '1';

  switch (metodo?.toLowerCase()) {
    case 'gps':
      backgroundColor = '#90EE90'; // Verde claro
      borderWidth = '2px';
      break;
    case 'trilateracao':
    case 'trilateração':
      backgroundColor = '#D3D3D3'; // Cinza claro
      borderWidth = '3px';
      break;
    case 'centroide':
    case 'bilateracao':
    case 'bilateração':
      backgroundColor = '#FFE4B5'; // Dourado claro
      borderWidth = '3px';
      break;
    case 'estimado':
    case 'estimativa':
      backgroundColor = '#FFDAB9'; // Pêssego
      borderWidth = '2px';
      opacity = '0.9';
      break;
  }

  console.log(`🎨 Criando ícone padrão:`, {
    metodo,
    borderColor,
    backgroundColor,
    borderWidth
  });

  return L.divIcon({
    className: 'custom-ativo-icon',
    html: `
      <div style="
        background-color: ${backgroundColor} !important; 
        width: 24px !important; 
        height: 24px !important; 
        border-radius: 50% !important; 
        border: ${borderWidth} solid ${borderColor} !important; 
        box-shadow: 0 2px 6px rgba(0,0,0,0.4) !important;
        position: relative !important;
        z-index: 1000 !important;
        display: block !important;
        visibility: visible !important;
        opacity: ${opacity} !important;
      "></div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 15]
  });
};

// ==================== FUNÇÕES PARA ÍCONES PERSONALIZADOS DE GATEWAYS ====================

// Função para buscar ícones de gateways em lote
const fetchIconsForGateways = async (
  idsGateways: number[], 
  auth?: any, 
  backendUrl?: string, 
  iconCache?: Map<number, L.DivIcon | null>,
  isOnline?: boolean,
  signal?: AbortSignal
): Promise<Map<number, L.DivIcon | null>> => {
  const resultMap = new Map<number, L.DivIcon | null>();
  
  try {
    console.log(`🔍 [fetchIconsForGateways] Buscando ícones para ${idsGateways.length} gateways:`, idsGateways);

    if (!idsGateways.length || !auth?.Token || !backendUrl) {
      console.log(`❌ [fetchIconsForGateways] Parâmetros inválidos:`, {
        quantidadeIds: idsGateways.length,
        temToken: !!auth?.Token,
        temBackendUrl: !!backendUrl
      });
      return resultMap;
    }

    // Verificar cache primeiro
    const idsParaBuscar: number[] = [];

    if (iconCache) {
      idsGateways.forEach(id => {
        const cached = iconCache.get(id);
        if (cached !== undefined) {
          console.log(`📦 [fetchIconsForGateways] Ícone de gateway encontrado no cache para ID ${id}`);
          resultMap.set(id, cached);
        } else {
          idsParaBuscar.push(id);
        }
      });
    } else {
      idsParaBuscar.push(...idsGateways);
    }

    // Se todos os ícones estão no cache, retornar
    if (!idsParaBuscar.length) {
      console.log(`✨ [fetchIconsForGateways] Todos os ícones de gateways encontrados no cache`);
      return resultMap;
    }

    console.log(`📡 [fetchIconsForGateways] Buscando ${idsParaBuscar.length} ícones de gateways na API:`, idsParaBuscar);

    const idApp = parseInt(process.env.REACT_APP_POWER_BI_APPLICATIONID || '0', 10) || 0;
    const url = `${backendUrl}/api/PlaceTags/by-idsplace`;

    const payload = { IdsPlaces: idsParaBuscar, IdApp: idApp };
    console.log(`📤 [fetchIconsForGateways] URL:`, url);
    console.log(`📤 [fetchIconsForGateways] IdApp do env:`, process.env.REACT_APP_POWER_BI_APPLICATIONID, '→', idApp);
    console.log(`📤 [fetchIconsForGateways] Payload:`, JSON.stringify(payload));
    console.log(`📤 [fetchIconsForGateways] Headers:`, {
      'accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${auth.Token.substring(0, 20)}...`
    });

    const resp = await fetch(url, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${auth.Token}`
      },
      body: JSON.stringify(payload),
      signal
    });

    console.log(`📡 [fetchIconsForGateways] Status da resposta:`, resp.status, resp.statusText);

    if (!resp.ok) {
      const errorText = await resp.text();
      console.warn(`❌ [fetchIconsForGateways] Erro na requisição de ícones de gateways:`, {
        status: resp.status,
        statusText: resp.statusText,
        errorBody: errorText
      });
      return resultMap;
    }

    const response = await resp.json();
    console.log(`📥 [fetchIconsForGateways] Resposta completa da API:`, response);
    console.log(`📥 [fetchIconsForGateways] Resumo da resposta:`, {
      sucesso: response?.IsRequestSuccessful,
      temData: !!response?.Data,
      quantidadeItens: response?.Data?.length || 0,
      primeiroItem: response?.Data?.[0]
    });

    if (!response?.Data || !Array.isArray(response.Data)) {
      console.warn(`⚠️ [fetchIconsForGateways] Resposta inválida da API`);
      return resultMap;
    }

    // Processar cada ícone retornado
    for (const item of response.Data) {
      const idGateway = item.IdPlace;
      console.log(`🎨 [fetchIconsForGateways] Processando item completo:`, item);
      console.log(`🎨 [fetchIconsForGateways] Processando ícone de gateway para ID ${idGateway}:`, {
        codigo: item?.Codigo,
        nome: item?.Name,
        valueType: typeof item?.Value,
        value: item?.Value
      });

      let value = item?.Value;
      if (typeof value === 'string') {
        try {
          value = JSON.parse(value);
          console.log(`✅ [fetchIconsForGateways] Value parseado com sucesso:`, value);
        } catch (e) {
          console.warn(`⚠️ [fetchIconsForGateways] Erro ao parsear Value para gateway ID ${idGateway}:`, e);
          console.warn(`⚠️ [fetchIconsForGateways] Value original:`, item?.Value);
          continue;
        }
      }

      const svg: string | undefined = value?.SVG;
      const klass: string | undefined = value?.Class;

      console.log(`🔍 [fetchIconsForGateways] Extraído do Value:`, {
        temSVG: !!svg,
        svgLength: svg?.length,
        temClass: !!klass,
        class: klass
      });

      let icon: L.DivIcon | null = null;
      if (svg && svg.trim()) {
        icon = createDivIconFromSvgGateway(svg, isOnline);
        console.log(`✨ [fetchIconsForGateways] Ícone SVG de gateway criado para ID ${idGateway}`);
      } else if (klass && klass.trim()) {
        icon = createDivIconFromClassGateway(klass, isOnline);
        console.log(`✨ [fetchIconsForGateways] Ícone Class de gateway criado para ID ${idGateway}`);
      } else {
        console.warn(`⚠️ [fetchIconsForGateways] Nenhum SVG ou Class encontrado para ID ${idGateway}`);
      }

      // Atualizar cache e resultado
      if (iconCache) {
        iconCache.set(idGateway, icon);
        console.log(`📦 [fetchIconsForGateways] Ícone armazenado no cache ref para ID ${idGateway}`);
      }
      resultMap.set(idGateway, icon);
      console.log(`📦 [fetchIconsForGateways] Ícone adicionado ao resultMap para ID ${idGateway}`);
    }

    console.log(`✅ [fetchIconsForGateways] Processamento completo. Total no resultMap:`, resultMap.size);
    return resultMap;
  } catch (e) {
    console.error('❌ [fetchIconsForGateways] Erro ao buscar ícones de gateways:', e);
    return resultMap;
  }
};

// Função para criar ícone DIV de gateway a partir de SVG
const createDivIconFromSvgGateway = (svgContent: string, isOnline?: boolean): L.DivIcon => {
  // Sanitizar e decodificar o SVG se necessário
  let sanitizedSvg = (svgContent || '').trim();
  try {
    if (sanitizedSvg.includes('\\')) {
      sanitizedSvg = JSON.parse(`"${sanitizedSvg}"`);
    }
  } catch (e) {
    console.warn('Erro ao decodificar SVG de gateway:', e);
  }

  const corFundo = isOnline ? '#FFFFFF' : '#fb1404';
  const corBorda = isOnline ? '#32CD32' : '#ff0000'; // Verde se online, vermelho se offline

  return L.divIcon({
    className: 'custom-gateway-svg-icon',
    html: `
      <div style="
        width: 32px; height: 32px; border-radius: 50%; background: ${corFundo}; border: 3px solid ${corBorda};
        display: flex; align-items: center; justify-content: center; box-shadow: 0 3px 8px rgba(0,0,0,0.5);
        line-height: 0;
      ">
        <div style="
          width: 22px; height: 22px; display:flex; align-items:center; justify-content:center; overflow:hidden;
          line-height: 0;
        ">
          ${sanitizedSvg}
        </div>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  });
};

// Função para criar ícone DIV de gateway a partir de classe CSS
const createDivIconFromClassGateway = (className: string, isOnline?: boolean): L.DivIcon => {
  const safeClass = (className || '').trim();
  const corFundo = isOnline ? '#FFFFFF' : '#fb1404';
  const corBorda = isOnline ? '#32CD32' : '#ff0000'; // Verde se online, vermelho se offline

  return L.divIcon({
    className: 'custom-gateway-class-icon',
    html: `
      <div style="
        width: 32px; height: 32px; border-radius: 50%; background: ${corFundo}; border: 3px solid ${corBorda};
        display: flex; align-items: center; justify-content: center; box-shadow: 0 3px 8px rgba(0,0,0,0.5);
        color: ${corBorda};
        line-height: 0;
      ">
        <i class="ui icon ${safeClass}" style="
          font-size:18px; width:22px; height:22px; display:flex; align-items:center; justify-content:center;
          line-height: 0;
        "></i>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  });
};

// Função para criar ícone padrão de gateway (fallback) - mantém o SVG original
const createDefaultGatewayIcon = (organizationColor: string, isOnline: boolean): L.Icon => {
  const corFundo = isOnline ? '#FFFFFF' : '#fb1404';
  const svg = `<svg version="1.1" id="Icons" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="-3.2 -3.2 38.40 38.40" xml:space="preserve" fill="${organizationColor}" stroke="${organizationColor}">
    <g id="SVGRepo_bgCarrier" stroke-width="0" transform="translate(0,0), scale(1)">
      <rect x="-3.2" y="-3.2" width="38.40" height="38.40" rx="19.2" fill="${corFundo}" strokewidth="0"></rect>
    </g>
    <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" stroke="#00000e" stroke-width="3.2">
      <style type="text/css"> .st0{fill:none;stroke:#000000;stroke-width:0.00032;stroke-linecap:round;stroke-linejoin:round;stroke-miterlimit:10;} </style>
      <path d="M16,3C10.5,3,6,7.5,6,13c0,8.4,9,15.5,9.4,15.8c0.2,0.1,0.4,0.2,0.6,0.2s0.4-0.1,0.6-0.2C17,28.5,26,21.4,26,13 C26,7.5,21.5,3,16,3z M16,17c-2.2,0-4-1.8-4-4s1.8-4,4-4s4,1.8,4,4S18.2,17,16,17z"></path>
    </g>
    <g id="SVGRepo_iconCarrier">
      <style type="text/css"> .st0{fill:none;stroke:#000000;stroke-width:0.00032;stroke-linecap:round;stroke-linejoin:round;stroke-miterlimit:10;} </style>
      <path d="M16,3C10.5,3,6,7.5,6,13c0,8.4,9,15.5,9.4,15.8c0.2,0.1,0.4,0.2,0.6,0.2s0.4-0.1,0.6-0.2C17,28.5,26,21.4,26,13 C26,7.5,21.5,3,16,3z M16,17c-2.2,0-4-1.8-4-4s1.8-4,4-4s4,1.8,4,4S18.2,17,16,17z"></path>
    </g>
  </svg>`;

  return new L.Icon({
    iconUrl: "data:image/svg+xml;base64," + btoa(svg),
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  });
};

const AtivoFilter: React.FC<{
  availableAtivos: IAtivosModel[];
  selectedAtivos: string[];
  onAtivoChange: (ativos: string[]) => void;
  selectedDate: string;
  onDateChange: (date: string) => void;
  selectedTime: string;
  onTimeChange: (time: string) => void;
  autoRefresh: boolean;
  onAutoRefreshToggle: () => void;
  incrementoValor: number;
  incrementoUnidade: 'segundos' | 'minutos' | 'horas';
  onIncrementoValorChange: (valor: number) => void;
  onIncrementoUnidadeChange: (unidade: 'segundos' | 'minutos' | 'horas') => void;
}> = ({ availableAtivos, selectedAtivos, onAtivoChange }) => {
  // Estado para controlar se o menu está aberto
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  // Lista de ativos únicos, removendo espaços
  const ativosUnicos = Array.from(
    new Set(availableAtivos.map((ativo) => ativo.Ativo && ativo.Ativo.trim()).filter(Boolean))
  );

  // "Selecionar Todos" está ativo se todos os ativos estão selecionados
  const allSelected = ativosUnicos.length > 0 && ativosUnicos.every(a => selectedAtivos.includes(a));

  // Handler para selecionar/desmarcar todos
  const handleSelectAll = () => {
    if (allSelected) {
      onAtivoChange([]);
    } else {
      onAtivoChange(ativosUnicos);
    }
  };

  // Efeito para fechar o menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!menuIsOpen) return;
      
      const target = event.target as Node;
      
      // Verificar se o clique foi no container do Select
      const isClickInSelect = selectRef.current?.contains(target);
      
      // Verificar se o clique foi no menu portal (que está no body)
      const menuPortal = document.querySelector('.react-select__menu-portal');
      const isClickInMenu = menuPortal?.contains(target);
      
      // Se o clique foi fora tanto do Select quanto do menu, fechar
      if (!isClickInSelect && !isClickInMenu) {
        setMenuIsOpen(false);
      }
    };

    if (menuIsOpen) {
      // Usar setTimeout para evitar fechar imediatamente ao abrir
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [menuIsOpen]);

  const Option = (props: any) => {
    const { isSelected, label } = props;
    return (
      <components.Option {...props}>
        <input
          type="checkbox"
          checked={isSelected}
          readOnly
          style={{ marginRight: 8 }}
        />
        {label}
      </components.Option>
    );
  };

  const ValueContainer = (props: any) => {
    return <components.ValueContainer {...props} />;
  };

  return (
    <div className="locais-ativo-filter" style={{ flex: 1, maxWidth: "800px", display: "flex", gap: "10px", alignItems: "flex-end", height: "60px", overflow: "hidden", flexShrink: 0 }}>
      <div ref={selectRef} style={{ flex: 1, maxWidth: "450px", minWidth: "400px", height: "60px", display: "flex", flexDirection: "column", flexShrink: 0 }}>
        <div style={{ marginBottom: 2, flexShrink: 0, height: "18px", lineHeight: "18px" }}>
          <label style={{ cursor: "pointer", fontSize: "11px", display: "flex", alignItems: "center" }}>
            <input
              type="checkbox"
              checked={allSelected}
              onChange={handleSelectAll}
              style={{ marginRight: 4, width: "14px", height: "14px" }}
            />
            Selecionar Todos
          </label>
        </div>
        <div style={{ flex: 1, minHeight: 0, height: "40px", maxHeight: "40px" }}>
          <Select
            isMulti
            options={ativosUnicos.map(ativo => ({ label: ativo, value: ativo }))}
            value={selectedAtivos.map(ativo => ({ label: ativo, value: ativo }))}
            onChange={(selectedOptions: any) => {
              const values = selectedOptions
                ? selectedOptions.map((opt: any) => opt.value.trim()).filter(Boolean)
                : [];
              onAtivoChange(values);
            }}
            placeholder="Filtrar por Ativo"
            isClearable
            classNamePrefix="react-select"
            closeMenuOnSelect={false}
            menuIsOpen={menuIsOpen}
            onMenuOpen={() => setMenuIsOpen(true)}
            onMenuClose={() => setMenuIsOpen(false)}
            components={{ Option, ValueContainer }}
            hideSelectedOptions={false} // <- importante para mostrar todos sempre
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
              control: (base: any, state: any) => ({
                ...base,
                minHeight: '40px',
                height: '40px',
                maxHeight: '40px',
                overflow: 'hidden',
                borderColor: state.isFocused ? '#2684ff' : base.borderColor,
              }),
              valueContainer: (base: any) => ({
                ...base,
                height: '38px',
                maxHeight: '38px',
                minHeight: '38px',
                overflowY: 'auto',
                overflowX: 'hidden',
                padding: '1px 6px',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                alignContent: 'flex-start',
                WebkitOverflowScrolling: 'touch'
              }),
              multiValue: (base: any) => ({
                ...base,
                margin: '1px',
                maxWidth: 'calc(50% - 2px)',
                flexShrink: 0,
                height: '18px',
                lineHeight: '18px'
              }),
              multiValueLabel: (base: any) => ({
                ...base,
                fontSize: '10px',
                padding: '0px 4px',
                maxWidth: '120px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: '18px'
              }),
              multiValueRemove: (base: any) => ({
                ...base,
                padding: '0px 2px',
                cursor: 'pointer',
                height: '18px',
                lineHeight: '18px',
                '&:hover': {
                  backgroundColor: '#ef4444',
                  color: 'white'
                }
              }),
              input: (base: any) => ({
                ...base,
                margin: '0px',
                padding: '0px',
                width: '100%',
                flex: '1 1 auto',
                fontSize: '12px'
              }),
              indicatorsContainer: (base: any) => ({
                ...base,
                flexShrink: 0,
                height: '100%',
                alignItems: 'center'
              }),
              clearIndicator: (base: any) => ({
                ...base,
                padding: '2px'
              }),
              dropdownIndicator: (base: any) => ({
                ...base,
                padding: '2px'
              }),
              placeholder: (base: any) => ({
                ...base,
                fontSize: '12px'
              })
            }}
          />
        </div>
      </div>
    </div>
  );
};



interface GatewayTableProps {
  selectedOrganizations: string[];
  onOrganizationChange: (orgs: string[]) => void;
  onRefreshMap?: () => void;
  onLimparMapaERecarregar?: () => void;
}
// Componente da Tabela de Gateways
const GatewayTable: React.FC<GatewayTableProps> = ({
  selectedOrganizations,
  onOrganizationChange,
  onRefreshMap,
  onLimparMapaERecarregar
}) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const configTableRef = useRef<HTMLDivElement>(null);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const mainTableRef = useRef<any>(null);
  const configTableRefInstance = useRef<any>(null);
  const dataSetNamePBI = getDataSetNamePBI();

  const httpClient = useHttpClient();
  const { auth } = useAuth();
  const userId = auth?.User?.IdUsuario ?? "";

  const SCREEN_CODE_MAPA_LOCAIS = "MAPA_LOCAIS";

  const extractCacheJson = useCallback((response: any): any => {
    const container = response?.payload ?? response?.data ?? response;
    if (container == null) return null;
    const dataStr = typeof container === "string" ? container : (container?.Data ?? container?.data);
    if (typeof dataStr === "string") {
      try {
        return JSON.parse(dataStr);
      } catch {
        return null;
      }
    }
    if (typeof container === "object" && container !== null) return container;
    return null;
  }, []);

  const getMapLocaisCache = useCallback(async (): Promise<any> => {
    try {
      const response = await httpClient.request({
        method: RequestMethod.GET,
        endpoint: `/api/Cache/${SCREEN_CODE_MAPA_LOCAIS}?userId=${userId}`,
      });
      const parsed = extractCacheJson(response);
      if (!parsed || typeof parsed !== "object") return {};

      // Back-compat: cache antigo era um map direto de visibilidade (field -> boolean)
      const values = Object.values(parsed);
      const looksLikeVisibilityMap = values.length > 0 && values.every((v) => typeof v === "boolean");
      if (looksLikeVisibilityMap) return { columnsVisibility: parsed };

      return parsed;
    } catch (e) {
      console.warn("Cache (MAPA_LOCAIS) não encontrado ou inválido, usando padrão.", e);
      return {};
    }
  }, [httpClient, extractCacheJson, userId]);

  const getColumnVisibilityFromCache = useCallback(async (): Promise<Record<string, boolean>> => {
    const cache = await getMapLocaisCache();
    return (cache?.columnsVisibility && typeof cache.columnsVisibility === "object") ? cache.columnsVisibility : {};
  }, [getMapLocaisCache]);

  const saveColumnVisibilityToCache = useCallback(
    async (visibility: Record<string, boolean>) => {
      try {
        const current = await getMapLocaisCache();
        const next = { ...(current || {}), columnsVisibility: visibility };
        await httpClient.request({
          method: RequestMethod.POST,
          endpoint: "/api/Cache",
          data: {
            UserId: userId,
            ScreenCode: SCREEN_CODE_MAPA_LOCAIS,
            Data: JSON.stringify(next),
          },
        });
      } catch (e) {
        console.error("Erro ao salvar visibilidade de colunas no cache:", e);
      }
    },
    [httpClient, getMapLocaisCache, userId]
  );

  const [listaGateway, setListaGateway] = useState<IGatewaysModel[]>([]);
  const [filteredRowCount, setFilteredRowCount] = useState<number>(0);
  const [selectedCorporation, setSelectedCorporation] = useState<string | null>(null);
  const [availableCorporations, setAvailableCorporations] = useState<string[]>([]);
  const [selectedAtivos, setSelectedAtivos] = useState<string[]>([]);
  const [availableAtivos, setAvailableAtivos] = useState<string[]>([]);
  const [initialOrganizations, setInitialOrganizations] = useState<string[]>([]);







  interface IUser {
    Id: number;
    Name: string;
    Email: string;
  }

  // Interface para Organização
  interface Organization {
    OrganizationId: string;
    OrganizationName: string;
  }

  const user = auth?.User;
  const emailUsuario = user?.Email;

  console.log("Email do usuário:", emailUsuario);






  async function exportTableToExcel() {
    const tableData = mainTableRef.current.getData();

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const endpoint = `${backendUrl}/Report/GatewayListExcel`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(tableData)
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Gateways.xlsx';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
    }
  }


  const obterListaGateway = async (orgs?: string[]) => {
    try {
      // Se não passaram organizações, usar as selecionadas globalmente
      const organizationsToUse = orgs && orgs.length > 0 ? orgs : selectedOrganizations;

      const endpoint = organizationsToUse.length > 0
        ? '/Gerenciamento/PowerBI/ObterGatewaysByOrg'
        : '/Gerenciamento/PowerBI/ObterGateways';

      const queryParams = new URLSearchParams();
      if (dataSetNamePBI) {
        queryParams.append('datasetName', dataSetNamePBI);
      }

      if (organizationsToUse.length > 0) {
        organizationsToUse.forEach(org => {
          queryParams.append('organization', org);
        });
      }

      const queryString = queryParams.toString();
      const fullUrl = `${endpoint}?${queryString}`;

      console.log('🔍 Buscando dados dos gateways...');
      console.log('📍 Endpoint:', fullUrl);
      console.log('🏢 Organizações:', organizationsToUse);

      const response = await httpClient.request({
        method: RequestMethod.GET,
        endpoint: fullUrl,
      });

      if (response.success && response.payload) {
        console.log('✅ Dados dos gateways recebidos:', response.payload);
        console.log('📊 Total de gateways:', response.payload.length);

        // Log detalhado do primeiro item para visualizar a estrutura
        if (response.payload.length > 0) {
          console.log('📋 Estrutura do primeiro gateway:', JSON.stringify(response.payload[0], null, 2));
        }

        setListaGateway(response.payload);
      } else {
        console.error('❌ Erro na resposta da API:', response);
        throw new Error("Falha ao obter dados dos gateways");
      }
    } catch (error: any) {
      console.error('❌ Erro ao obter lista de gateways:', error);

      if (mainTableRef.current) {
        mainTableRef.current.setData([listaGateway]);
        const footerEl = mainTableRef.current.element.querySelector(".tabulator-footer");
        if (footerEl) {
          footerEl.innerHTML = `<div style='color: red'>Erro ao carregar dados: ${error?.message || 'Erro desconhecido'}</div>`;
        }
      }
    }
  };


  useEffect(() => {
    // Atualiza a tabela quando as organizações selecionadas mudam
    console.log('🔄 GatewayTable: Organizações mudaram, carregando gateways...');
    obterListaGateway();
  }, [selectedOrganizations]);

  const loadConfigTable = () => {
    const cols = mainTableRef.current
      .getColumns()
      .map((col: ColumnComponent, index: number) => ({
        id: index + 1,
        title: col.getDefinition().title || col.getField(),
        field: col.getField(),
        visible: col.isVisible(),
      }));

    if (configTableRef.current) {
      if (configTableRefInstance.current) {
        configTableRefInstance.current.destroy();
      }

      configTableRefInstance.current = new Tabulator(configTableRef.current, {
        data: cols,
        layout: "fitDataFill",
        movableRows: true,
        headerVisible: false,
        columns: [
          {
            title: "Mover",
            formatter: "handle",
            headerSort: false,
            width: 30,
            minWidth: 30,
          },
          {
            title: "Coluna",
            field: "title",
            formatter: (cell) => ` ${cell.getValue()}`,
          },
          {
            title: "Visível",
            field: "visible",
            formatter: "tickCross",
            editor: true,
          },
        ],
      });

      // Forçar atualização da tabela principal
      mainTableRef.current.redraw();
    }
  };

  // Funções auxiliares
  const deltaTime = (minutes: number) => {
    const MINUTES_IN_HOUR = 60;
    const HOURS_IN_DAY = 24;
    const DAYS_IN_MONTH = 30;

    const months = Math.floor(minutes / (MINUTES_IN_HOUR * HOURS_IN_DAY * DAYS_IN_MONTH));
    minutes %= MINUTES_IN_HOUR * HOURS_IN_DAY * DAYS_IN_MONTH;

    const days = Math.floor(minutes / (MINUTES_IN_HOUR * HOURS_IN_DAY));
    minutes %= MINUTES_IN_HOUR * HOURS_IN_DAY;

    const hours = Math.floor(minutes / MINUTES_IN_HOUR);
    const remainingMinutes = minutes % MINUTES_IN_HOUR;

    const M = months ? `${months} M, ` : '';
    const D = days ? `${days} d, ` : '';
    const H = hours ? `${hours} h e ` : '';
    const I = `${remainingMinutes} min`;

    return `${M}${D}${H}${I}`;
  };

  // Editor personalizado para filtro de duração
  const durationFilterEditor = (cell: any, onRendered: any, success: any, cancel: any) => {
    const container = document.createElement("span");

    const start = document.createElement("input");
    start.setAttribute("type", "number");
    start.setAttribute("placeholder", "Min");
    start.style.padding = "4px";
    start.style.width = "32%";

    const end = start.cloneNode() as HTMLInputElement;
    end.setAttribute("placeholder", "Max");

    const lista = document.createElement('select');
    lista.style.width = "32%";
    lista.style.padding = "2px";

    ['Minutos', 'Horas', 'Dias'].forEach(opt => {
      const option = document.createElement('option');
      option.value = opt.toLowerCase();
      option.text = opt;
      lista.appendChild(option);
    });

    const buildValues = () => {
      success({
        start: start.value,
        end: end.value,
        unit: lista.value
      });
    };

    [start, end, lista].forEach(el => {
      el.addEventListener("change", buildValues);
      el.addEventListener("blur", buildValues);
    });

    container.appendChild(start);
    container.appendChild(end);
    container.appendChild(lista);

    return container;
  };

  // Editor personalizado para filtro de data
  const dateEditor = (cell: any, onRendered: any, success: any, cancel: any) => {
    const container = document.createElement("span");

    // Criar inputs de data
    const date2 = document.createElement("input");
    date2.setAttribute("type", "date");
    date2.style.padding = "3px";
    date2.style.width = "50%";
    date2.style.fontSize = "12px";
    date2.style.boxSizing = "border-box";

    const date1 = date2.cloneNode() as HTMLInputElement;

    // Configurar datas iniciais
    const hoje = new Date();
    date2.value = hoje.toISOString().split('T')[0];

    const dataAnterior = new Date();
    dataAnterior.setFullYear(dataAnterior.getFullYear() - 1);
    date1.value = dataAnterior.toISOString().split('T')[0];

    const buildValues = () => {
      success({
        date1: date1.value,
        date2: date2.value,
      });
    };

    const keypress = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        buildValues();
      }
      if (e.key === "Escape") {
        cancel();
      }
    };

    // Adicionar event listeners
    [date1, date2].forEach(el => {
      el.addEventListener("change", buildValues);
      el.addEventListener("blur", buildValues);
      el.addEventListener("keydown", keypress);
    });

    container.appendChild(date1);
    container.appendChild(date2);

    return container;
  };

  // Função de filtro para datas
  const dateFilterFunction = (headerValue: any, rowValue: any) => {
    // Se não há filtro definido, mostra todos os registros
    if (!headerValue || (!headerValue.date1 && !headerValue.date2)) return true;

    // Se o valor da linha é vazio/nulo, não mostra
    if (!rowValue) return false;

    // Função para normalizar qualquer formato de data para Date
    const normalizeDate = (dateValue: string | Date) => {
      // Se já é um objeto Date, retorna diretamente
      if (dateValue instanceof Date) return dateValue;

      // Tenta parsear formato DD/MM/YYYY HH:mm:ss
      const dateTimeMatch = String(dateValue).match(/^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}):(\d{2})$/);
      if (dateTimeMatch) {
        const [_, day, month, year, hours, minutes, seconds] = dateTimeMatch;
        return new Date(
          parseInt(year),
          parseInt(month) - 1,
          parseInt(day),
          parseInt(hours),
          parseInt(minutes),
          parseInt(seconds)
        );
      }

      // Tenta parsear como ISO
      const date = new Date(dateValue);
      return isNaN(date.getTime()) ? null : date;
    };

    // Normaliza a data da linha
    const rowDate = normalizeDate(rowValue);
    if (!rowDate) return false;

    // Normaliza as datas do filtro
    const startDate = headerValue.date1 ? new Date(headerValue.date1) : null;
    const endDate = headerValue.date2 ? new Date(headerValue.date2) : null;


    // Ajusta o endDate para incluir o dia inteiro pois o filtro funciona com base no intervalo entre as datas selecionadas.
    if (endDate) {
      endDate.setDate(endDate.getDate() + 1);
    }

    // Se não há datas no filtro, mostra tudo
    if (!startDate && !endDate) return true;

    // Ajusta o range do filtro
    const startOfDay = startDate ? new Date(startDate) : null;
    const endOfDay = endDate ? new Date(endDate) : null;

    if (startOfDay) startOfDay.setHours(0, 0, 0, 0);
    if (endOfDay) endOfDay.setHours(23, 59, 59, 999);

    // Caso 1: Mesma data em ambos os campos (filtro para um único dia)
    if (startDate && endDate && startDate.toDateString() === endDate.toDateString()) {
      return rowDate >= startOfDay! && rowDate <= endOfDay!;
    }

    // Caso 2: Apenas data inicial definida
    if (startDate && !endDate) {
      return rowDate >= startOfDay!;
    }

    // Caso 3: Apenas data final definida
    if (!startDate && endDate) {
      return rowDate <= endOfDay!;
    }

    // Caso 4: Intervalo de datas definido
    if (startDate && endDate) {
      return rowDate >= startOfDay! && rowDate <= endOfDay!;
    }

    return true;
  };

  // Adicione este editor personalizado para o filtro de temperatura após a função dateFilterFunction
  const temperatureFilterEditor = (cell: any, onRendered: any, success: any, cancel: any) => {
    const container = document.createElement("span");

    const min = document.createElement("input");
    min.setAttribute("type", "number");
    min.setAttribute("placeholder", "Min °C");
    min.style.padding = "4px";
    min.style.width = "48%";
    min.style.marginRight = "4%";

    const max = document.createElement("input");
    max.setAttribute("type", "number");
    max.setAttribute("placeholder", "Max °C");
    max.style.padding = "4px";
    max.style.width = "48%";

    const buildValues = () => {
      success({
        min: min.value ? parseFloat(min.value) : null,
        max: max.value ? parseFloat(max.value) : null,
      });
    };

    [min, max].forEach(el => {
      el.addEventListener("change", buildValues);
      el.addEventListener("blur", buildValues);
    });

    container.appendChild(min);
    container.appendChild(max);

    return container;
  };

  const createInputFilterEditor = (field: keyof IGatewaysModel) => (cell: any, onRendered: any, success: any, cancel: any) => {
    const container = document.createElement("div");
    container.style.position = "relative"; // Garante que a lista seja posicionada corretamente

    // Input para digitar o filtro
    const input = document.createElement("input");
    input.setAttribute("type", "text");
    input.setAttribute("placeholder", "Buscar...");
    input.classList.add("filter-input"); // Adiciona a classe do input

    // Lista de sugestões
    const suggestionList = document.createElement("div");
    suggestionList.classList.add("suggestion-list"); // Adiciona a classe da lista
    suggestionList.style.position = "absolute";
    suggestionList.style.top = "100%";
    suggestionList.style.left = "0";
    suggestionList.style.right = "0";
    suggestionList.style.zIndex = "1000"; // Aumente o z-index
    suggestionList.style.maxHeight = "200px";
    suggestionList.style.overflowY = "auto";
    suggestionList.style.display = "none";

    // Adiciona a lista de sugestões ao body
    document.body.appendChild(suggestionList);

    // Função para atualizar a lista de sugestões
    const updateSuggestions = (filterText: string) => {
      const tableData = mainTableRef.current.getData(); // Acessa os dados da tabela
      const uniqueValues = Array.from(new Set(tableData.map((item: any) => item[field])));
      const filteredValues = uniqueValues.filter((value: any) =>
        value?.toString().toLowerCase().includes(filterText.toLowerCase())
      );

      suggestionList.innerHTML = ""; // Limpa a lista atual
      filteredValues.forEach((value) => {
        const suggestionItem = document.createElement("div");
        suggestionItem.textContent = String(value);
        suggestionItem.classList.add("suggestion-item"); // Adiciona a classe do item
        suggestionItem.addEventListener("click", () => {
          input.value = String(value); // Preenche o input com o valor selecionado
          suggestionList.style.display = "none"; // Oculta a lista
          success(value); // Aplica o filtro
        });
        suggestionList.appendChild(suggestionItem);
      });

      if (filteredValues.length > 0) {
        suggestionList.style.display = "block"; // Mostra a lista
      } else {
        suggestionList.style.display = "none"; // Oculta a lista se não houver sugestões
      }
    };

    // Evento de digitação no input
    input.addEventListener("input", (e) => {
      const filterText = (e.target as HTMLInputElement).value;
      updateSuggestions(filterText);

      // Atualiza a posição da lista de sugestões
      const rect = input.getBoundingClientRect();
      suggestionList.style.top = `${rect.bottom + window.scrollY}px`;
      suggestionList.style.left = `${rect.left + window.scrollX}px`;
      suggestionList.style.width = `${rect.width}px`;
    });

    // Evento de foco no input
    input.addEventListener("focus", () => {
      updateSuggestions(input.value);

      // Atualiza a posição da lista de sugestões
      const rect = input.getBoundingClientRect();
      suggestionList.style.top = `${rect.bottom + window.scrollY}px`;
      suggestionList.style.left = `${rect.left + window.scrollX}px`;
      suggestionList.style.width = `${rect.width}px`;
    });

    // Evento de clique fora do input (para ocultar a lista)
    document.addEventListener("click", (e) => {
      if (!container.contains(e.target as Node)) {
        suggestionList.style.display = "none";
      }
    });

    container.appendChild(input);

    return container;
  };
  const createInputFilterFunction = (field: string) => (headerValue: string, rowValue: string) => {
    if (!headerValue) return true; // Se não houver filtro, mostra todas as linhas
    return rowValue?.toString().toLowerCase().includes(headerValue.toLowerCase());
  };


  useEffect(() => {
    if (!tableRef.current) return;
    let cancelled = false;

    (async () => {
      if (mainTableRef.current) {
        mainTableRef.current.destroy();
        mainTableRef.current = null;
      }
      let visibilityMap: Record<string, boolean> = {};
      try {
        visibilityMap = await getColumnVisibilityFromCache();
      } catch (e) {
        console.warn("Cache de colunas indisponível (backend pode não ter IServiceSysCache registrado), usando padrão.", e);
      }
      if (cancelled || !tableRef.current) return;

      console.log("Inicializando tabela (visibilidade de colunas do cache MAPA_LOCAIS)");
      mainTableRef.current = new Tabulator(tableRef.current, {
        data: [listaGateway],
        height: 650,
        layout: "fitData",
        movableColumns: true,
        resizableColumnFit: true,
        columnHeaderVertAlign: "top",
        footerElement: `<div>Carregando dados...</div>`,
        progressiveLoad: "scroll",
        progressiveLoadDelay: 200,
        progressiveLoadScrollMargin: 30,
        renderVerticalBuffer: 300,
        keybindings: false,
        sortMode: "local",
        filterMode: "local",
        layoutColumnsOnNewData: false,
        placeholder: "Nenhum dado encontrado",
        debugInvalidOptions: false,
        debugEventsExternal: false,
        debugEventsInternal: false,
        initialSort: [],
        columns: [
          {
            title: "Organização",
            field: "Organizacao",
            visible: visibilityMap["Organizacao"] ?? true,
            headerFilter: "list",
            headerFilterParams: {
              values: true,
              multiselect: true,
              clearable: true,
              sort: "asc"
            },
            headerFilterFunc: (headerValue: any, rowValue: any) => {
              if (!headerValue || headerValue.length === 0) return true;
              if (Array.isArray(headerValue)) {
                return headerValue.includes(rowValue);
              }
              return headerValue === rowValue;
            },
            headerFilterPlaceholder: "Selecione...",
            width: 200,
            sorter: "string"
          },
          {
            title: "Local",
            field: "Local",
            visible: visibilityMap["Local"] ?? true,
            headerFilter: createInputFilterEditor("Local"),
            headerFilterFunc: createInputFilterFunction("Local"),
            width: 200,
          },
          {
            title: "Local Fixo/Movel",
            field: "PlaceType",
            visible: visibilityMap["PlaceType"] ?? true,
            headerFilter: "list",
            headerFilterParams: {
              values: ["Fixo", "Móvel"],
              multiselect: true,
              clearable: true,
              sort: "asc"
            },
            headerFilterFunc: (headerValue: any, rowValue: any) => {
              if (!headerValue || headerValue.length === 0) return true;
              if (Array.isArray(headerValue)) {
                return headerValue.includes(rowValue);
              }
              return headerValue === rowValue;
            },
            formatter: (cell: CellComponent) =>
              cell.getValue() === "Fixo"
                ? `<i class='ui icon building'></i> ${cell.getValue()}`
                : `<i class='ui icon car'></i> ${cell.getValue()}`,
          },
          {
            title: "Tempo decorrido",
            field: "TempoDecorrido",
            visible: visibilityMap["TempoDecorrido"] ?? true,
            headerFilter: durationFilterEditor,
            headerFilterFunc: (headerValue: any, rowValue: any) => {
              if (!headerValue || !headerValue.start) return true;

              const minutes = parseInt(rowValue);
              if (isNaN(minutes)) return false;

              let compareValue = minutes;
              if (headerValue.unit === "horas") {
                compareValue = minutes / 60;
              } else if (headerValue.unit === "dias") {
                compareValue = minutes / (60 * 24);
              }

              const start = parseInt(headerValue.start);
              const end = parseInt(headerValue.end);

              if (start && end) {
                return compareValue >= start && compareValue <= end;
              } else if (start) {
                return compareValue >= start;
              } else if (end) {
                return compareValue <= end;
              }
              return true;
            },
            formatter: (cell: any) => deltaTime(cell.getValue()),
          },
          {
            title: "Gateway",
            field: "Gateway",
            visible: visibilityMap["Gateway"] ?? true,
            sorter: "string",
            headerFilter: createInputFilterEditor("Gateway"),
            headerFilterFunc: createInputFilterFunction("Gateway"),
            formatter: (cell: CellComponent) => {
              const gateway = cell.getValue();
              return `
                <a href='/detalhes-do-gateway?gateway=${gateway}' target='_blank' style='margin-right: 10px;'>
                  <i class='ui icon external alternate' style='cursor: pointer;'></i>
                </a>
                ${gateway}
              `;
            },
          },
          {
            title: "Número de Série",
            field: "SerialNumber",
            visible: visibilityMap["SerialNumber"] ?? true,
            sorter: "string",
            headerFilter: createInputFilterEditor("SerialNumber"),
            headerFilterFunc: createInputFilterFunction("SerialNumber"),
          },
          {
            title: "Descrição do Local",
            field: "LocalDescricao",
            visible: visibilityMap["LocalDescricao"] ?? true,
            sorter: "string",
            headerFilter: createInputFilterEditor("LocalDescricao"),
            headerFilterFunc: createInputFilterFunction("LocalDescricao"),
          },
          {
            title: "Situação",
            field: "LocalSituacao",
            visible: visibilityMap["LocalSituacao"] ?? true,
            headerFilter: "list",
            headerFilterParams: {
              values: ["Habilitado", "Desabilitado"],
              multiselect: true,
              clearable: true,
              sort: "asc"
            },
            headerFilterFunc: (headerValue: any, rowValue: any) => {
              if (!headerValue || headerValue.length === 0) return true;
              if (Array.isArray(headerValue)) {
                return headerValue.includes(rowValue);
              }
              return headerValue === rowValue;
            },
            formatter: (cell: CellComponent) => {
              const value = String(cell.getValue() || '').toLowerCase();
              return value.startsWith('habil')
                ? "<i class='ui green toggle on icon'></i> Habilitado"
                : "<i class='ui red toggle off icon'></i> Desabilitado";
            },
            hozAlign: "center",
          },
          {
            title: "Status",
            field: "Status",
            visible: visibilityMap["Status"] ?? true,
            headerFilter: "list",
            headerFilterParams: {
              values: ["online", "offline"],
              multiselect: true,
              clearable: true,
              sort: "asc"
            },
            headerFilterFunc: (headerValue: any, rowValue: any) => {
              if (!headerValue || headerValue.length === 0) return true;
              if (Array.isArray(headerValue)) {
                return headerValue.includes(rowValue);
              }
              return headerValue === rowValue;
            },
            formatter: (cell: CellComponent) =>
              cell.getValue() === "online"
                ? "<i class='ui green wifi icon'></i> Online"
                : "<i class='ui red wifi icon'></i> Offline",
            hozAlign: "center",
          },
          {
            title: "Ultima Atualização",
            field: "UltimaAtualizacao",
            visible: visibilityMap["UltimaAtualizacao"] ?? true,
            headerFilter: dateEditor,
            headerFilterFunc: dateFilterFunction,
            sorter: (a: string, b: string) => {
              if (!a && !b) return 0;
              if (!a) return 1;
              if (!b) return -1;

              const dateA = moment(a, "DD/MM/YYYY HH:mm:ss");
              const dateB = moment(b, "DD/MM/YYYY HH:mm:ss");

              if (!dateA.isValid() && !dateB.isValid()) return 0;
              if (!dateA.isValid()) return 1;
              if (!dateB.isValid()) return -1;

              return dateA.valueOf() - dateB.valueOf();
            },
            formatter: (cell: CellComponent) => {
              const value = cell.getValue();
              if (!value) return " - ";
              const date = moment(value, "DD/MM/YYYY HH:mm:ss");
              return date.isValid() ? date.format("DD/MM/YYYY HH:mm:ss") : " - ";
            },
          },
          {
            title: "Temperatura (°C)",
            field: "Temperature",
            visible: visibilityMap["Temperature"] ?? true,
            sorter: "number",
            headerFilter: temperatureFilterEditor,
            headerFilterFunc: (headerValue: any, rowValue: any) => {
              if (!headerValue || (!headerValue.min && !headerValue.max)) return true;
              if (!rowValue && rowValue !== 0) return false;

              const value = parseFloat(rowValue);
              const min = headerValue.min !== null ? parseFloat(headerValue.min) : null;
              const max = headerValue.max !== null ? parseFloat(headerValue.max) : null;

              if (min !== null && max !== null) {
                return value >= min && value <= max;
              } else if (min !== null) {
                return value >= min;
              } else if (max !== null) {
                return value <= max;
              }
              return true;
            },
            formatter: (cell: CellComponent) => {
              const value = cell.getValue();
              if (value == null) return " - ";
              const data = value.toFixed(1);
              const temperatura = parseInt(data, 10);
              let temp;

              if (temperatura > 80) {
                temp = `<span><i class="ui icon thermometer full" style="color: #C71585; padding: 2px"></i>${data} ºC</span>`;
              } else if (temperatura > 70 && temperatura < 80) {
                temp = `<span><i class="ui icon thermometer half" style="color: gold; padding: 2px"></i>${data} ºC</span>`;
              } else {
                temp = `<span><i class="ui icon thermometer empty" style="color: aqua; padding: 2px"></i>${data} ºC</span>`;
              }

              return temp;
            },
          },
          {
            title: "Latitude/Longitude",
            field: "Latitude",
            visible: visibilityMap["Latitude"] ?? true,
            formatter: (cell: CellComponent) => {
              const data = cell.getRow().getData();
              if (data.Latitude && data.Longitude) {
                return `${data.Latitude.toFixed(6)}, ${data.Longitude.toFixed(6)}`;
              }
              return " - ";
            },
          },
        ],
      });

      mainTableRef.current.on("dataLoaded", function (data: IGatewaysModel[]) {
        console.log("Dados carregados:", data.length, "registros");

        // Atualiza o footer primeiro
        const footerEl = mainTableRef.current.element.querySelector(".tabulator-footer");
        if (footerEl) {
          footerEl.innerHTML = `<div>${data.length} Gateways carregados</div>`;
        }

        // Configura a ordenação após os dados estarem carregados
        setTimeout(() => {
          mainTableRef.current.setSort([{ column: "Organizacao", dir: "asc" }]);
        }, 0);

        // Atualiza as opções dos filtros
        const uniqueOrganizacoes = Array.from(new Set(data.map(item => item.Organizacao)))
          .filter(Boolean)
          .sort();

        mainTableRef.current.updateColumnDefinition("Organizacao", {
          headerFilterParams: {
            values: uniqueOrganizacoes,
            multiselect: true,
            clearable: true,
          },
        });

        loadConfigTable();
      });
      mainTableRef.current.on("rowClick", function (e: any, row: any) {
        // Remove a classe 'selected' de todas as linhas
        const rows = mainTableRef.current.getRows();
        rows.forEach((r: any) => r.getElement().classList.remove('selected'));

        // Adiciona a classe 'selected' à linha clicada
        row.getElement().classList.add('selected');
      });

      mainTableRef.current.on("dataFiltered", function (filters: any, rows: any[]) {
        console.log("Dados filtrados:", rows.length, "registros");
        setFilteredRowCount(rows.length);

        // Atualizar footer após filtro
        const footerEl = mainTableRef.current.element.querySelector(".tabulator-footer");
        if (footerEl) {
          footerEl.innerHTML = `<div>${listaGateway.length} Gateways carregados (linhas filtradas: ${rows.length})</div>`;
        }
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, getColumnVisibilityFromCache]);





  useEffect(() => {
    if (mainTableRef.current) {
      // Atualizar dados da tabela
      mainTableRef.current.replaceData(listaGateway);

      // Atualizar as opções dos filtros dropdown
      const uniqueOrganizacoes = Array.from(new Set(listaGateway.map(item => item.Organizacao)))
        .filter(org => org)
        .sort();

      mainTableRef.current.updateColumnDefinition("Organizacao", {
        headerFilterParams: {
          values: uniqueOrganizacoes,
          multiselect: true,
          clearable: true,
        },
      });
    }
  }, [listaGateway]);

  // Efeito separado para atualizar o footer
  useEffect(() => {
    if (mainTableRef.current) {
      const footerEl = mainTableRef.current.element.querySelector(".tabulator-footer");
      if (footerEl) {
        footerEl.innerHTML = `<div>${listaGateway.length} Gateways carregados (linhas filtradas: ${filteredRowCount})</div>`;
      }
    }
  }, [listaGateway.length, filteredRowCount]);

  const handleSettingsClick = () => {
    setSidebarVisible(!sidebarVisible);
    if (!sidebarVisible) {
      setTimeout(loadConfigTable, 300);
    }
  };
  // ❌ REMOVIDO - aplicarFiltroOrganizacoes é obsoleta com filtros globais

  const applyConfigChanges = async () => {
    const updatedCols = configTableRefInstance.current.getData();

    const newColumns = updatedCols.map((col: any) => {
      const originalCol = mainTableRef.current.getColumn(col.field);
      if (originalCol) {
        const definition = originalCol.getDefinition();
        return {
          ...definition,
          visible: col.visible,
        };
      }
      return col;
    });

    mainTableRef.current.setColumns(newColumns);

    const visibility: Record<string, boolean> = {};
    mainTableRef.current.getColumns().forEach((col: ColumnComponent) => {
      const field = col.getField();
      if (field) visibility[field] = col.isVisible();
    });
    await saveColumnVisibilityToCache(visibility);
  };

  const handleDownloadCsv = () => {
    mainTableRef.current.download("csv", "gateways.csv");
  };

  // Função para limpar todos os filtros
  const limparFiltros = async () => {
    if (mainTableRef.current) {
      try {
        console.log('🧹 [limparFiltros] Iniciando limpeza de filtros...');
        
        // Limpar todos os filtros da tabela
        mainTableRef.current.clearHeaderFilter();
        console.log('✅ [limparFiltros] Filtros da tabela limpos');

        // Recarregar dados da tabela baseado nos filtros globais atuais
        console.log('🔄 [limparFiltros] Recarregando dados da tabela após limpar filtros...');
        await obterListaGateway();
        console.log('✅ [limparFiltros] Dados da tabela recarregados');

        // Forçar atualização visual da tabela
        mainTableRef.current.redraw(true);

        // Limpar mapa e recarregar dados via GatewayNred baseado nos filtros globais
        if (onLimparMapaERecarregar) {
          console.log('🧹 [limparFiltros] Chamando onLimparMapaERecarregar...');
          await onLimparMapaERecarregar();
          console.log('✅ [limparFiltros] onLimparMapaERecarregar concluído');
        } else if (onRefreshMap) {
          // Fallback: apenas recarregar dados do mapa
          console.log('🔄 [limparFiltros] Fallback: recarregando dados do mapa...');
          await onRefreshMap();
        } else {
          console.warn('⚠️ [limparFiltros] Nenhuma função de recarregamento disponível');
        }
        
        console.log('✅ [limparFiltros] Processo de limpeza concluído');
      } catch (error) {
        console.error("❌ [limparFiltros] Erro ao limpar filtros:", error);
      }
    } else {
      console.warn('⚠️ [limparFiltros] mainTableRef.current não está disponível');
    }
  };

  return (
    <div className="d-flex" style={{ position: "relative" }}>
      <div style={{ width: sidebarVisible ? "80%" : "100%", transition: "0.3s" }}>
        <h1 className="ui header">Tabela de Gateways</h1>
        <div style={{ marginBottom: "10px", display: "flex", alignItems: "center", gap: "10px" }}>
          {/* Filtro de Organizações */}
          <div style={{ flex: 1, maxWidth: "300px" }}>
          </div>
          {/* Botão de Configurações */}
          <button
            onClick={handleSettingsClick}
            style={{ padding: "10px 20px", cursor: "pointer" }}
            className="ui button primary"
          >
            ⚙️ Configurações
          </button>
          {/* Botão para Limpar Filtros */}
          <button
            onClick={limparFiltros}
            style={{ padding: "10px 20px", cursor: "pointer" }}
            className="ui button secondary"
          >
            🗑️ Limpar Filtros
          </button>
          {/* Botão de Download CSV */}
          <button
            onClick={handleDownloadCsv}
            style={{
              padding: "10px 20px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
            className="ui button secondary"
          >
            <i className="ui icon arrow alternate circle down outline"></i>
            Download CSV
          </button>
          <button
            onClick={exportTableToExcel}
            style={{
              padding: "10px 20px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
            className="ui button secondary"
          >
            <i className="ui icon file excel outline"></i>
            Download Excel
          </button>
        </div>
        {/* Tabela */}
        <div ref={tableRef}></div>
      </div>
      {/* Sidebar de Configurações */}
      {sidebarVisible && (
        <div className="sidebar-config">
          <h4 className="ui grey inverted header">
            <i className="ui cog icon"></i> Configurações da Tabela
          </h4>
          <div className="config-content" ref={configTableRef}></div>
          <button className="ui primary button" onClick={applyConfigChanges}>
            <i className="ui save icon"></i> Aplicar
          </button>
        </div>
      )}
    </div>
  );
};

interface IAssetBeacon {
  rn: number;
  Organizacao: string;
  Ativo: string;
  DeviceKey: string;
  LastSeen: string;
  Latitude: number;
  Longitude: number;
  lat_long_origin: string;
}

// Interface para configuração do popup de ativos
interface IPopupAtivoConfig {
  organizacao: boolean;
  beacon: boolean;
  ativo: boolean;
  data: boolean;
  timestampCaptura: boolean;
  coordenadas: boolean;
  origemCoordenada: boolean;
  metodo: boolean; // Apenas para beacons trilaterados
}

// Configuração padrão (todos os campos visíveis); os dados reais vêm da API Cache (GET /api/Cache/MAPA_LOCAIS)
const DEFAULT_POPUP_CONFIG: IPopupAtivoConfig = {
  organizacao: true,
  beacon: true,
  ativo: true,
  data: true,
  timestampCaptura: true,
  coordenadas: true,
  origemCoordenada: true,
  metodo: true,
};

// Função helper para renderizar popup de ativo baseado nas preferências
const renderPopupAtivo = (
  asset: IAssetBeacon | any,
  config: IPopupAtivoConfig,
  coordinates?: [number, number]
): React.ReactNode => {
  const items: React.ReactNode[] = [];

  if (config.organizacao && asset.Organizacao) {
    items.push(
      <React.Fragment key="org">
        <b>Organização:</b><span style={{ color: 'blue' }}> {asset.Organizacao || ' - '}</span><br />
      </React.Fragment>
    );
  }

  if (config.beacon && asset.DeviceKey) {
    items.push(
      <React.Fragment key="beacon">
        <b>Beacon:</b><span style={{ color: 'blue' }}> {asset.DeviceKey}</span><br />
      </React.Fragment>
    );
  }

  if (config.ativo && asset.Ativo) {
    items.push(
      <React.Fragment key="ativo">
        <b>Ativo:</b> {asset.Ativo || ' - '}<br />
      </React.Fragment>
    );
  }

  if (config.data && asset.LastSeen) {
    items.push(
      <React.Fragment key="data">
        <b>Data:</b> {asset.LastSeen}<br />
      </React.Fragment>
    );
  }

  if (config.timestampCaptura && (asset as any)._timestamp) {
    items.push(
      <React.Fragment key="timestamp">
        <b>Timestamp captura:</b> <span style={{ color: 'green' }}>{(asset as any)._timestamp}</span><br />
      </React.Fragment>
    );
  }

  if (config.coordenadas) {
    const lat = coordinates ? coordinates[0] : asset.Latitude;
    const lng = coordinates ? coordinates[1] : asset.Longitude;
    if (lat !== undefined && lng !== undefined) {
      items.push(
        <React.Fragment key="coords">
          <span><i className="globe icon"></i> {lat?.toFixed(5)} , {lng?.toFixed(5)}</span><br />
        </React.Fragment>
      );
    }
  }

  if (config.origemCoordenada) {
    // Para beacons GPS, usar lat_long_origin; para trilaterados, sempre GATEWAY
    const origem = asset.lat_long_origin === 'B' ? 'BEACON GPS' : 'GATEWAY';
    items.push(
      <React.Fragment key="origem">
        <b>Origem da coordenada:</b> {origem}<br />
      </React.Fragment>
    );
  }

  if (config.metodo && asset.Metodo) {
    items.push(
      <React.Fragment key="metodo">
        <b>Método:</b> {asset.Metodo ? asset.Metodo.toUpperCase() : 'TRILATERAÇÃO'}<br />
      </React.Fragment>
    );
  }

  if (items.length === 0) {
    return <div style={{ textAlign: 'left' }}>Nenhum campo selecionado para exibir</div>;
  }

  return <div style={{ textAlign: 'left' }}>{items}</div>;
};

// Interface para configuração do popup de gateway
interface IPopupGatewayConfig {
  organizacao: boolean;
  local: boolean;
  gateway: boolean;
  ultimaAtualizacao: boolean;
  latitude: boolean;
  longitude: boolean;
  status: boolean;
}

// Configuração padrão (todos os campos visíveis); os dados reais vêm da API Cache (GET /api/Cache/MAPA_LOCAIS)
const DEFAULT_POPUP_GATEWAY_CONFIG: IPopupGatewayConfig = {
  organizacao: true,
  local: true,
  gateway: true,
  ultimaAtualizacao: true,
  latitude: true,
  longitude: true,
  status: true,
};

/** Aplica valores da API ao config de popup Ativo – garante que false da API sobrescreva o default */
function applyPopupAtivoFromApi(api: Record<string, unknown>): IPopupAtivoConfig {
  const next: IPopupAtivoConfig = { ...DEFAULT_POPUP_CONFIG };
  (Object.keys(next) as (keyof IPopupAtivoConfig)[]).forEach((k) => {
    const v = api[k];
    if (typeof v === "boolean") next[k] = v;
  });
  return next;
}

/** Aplica valores da API ao config de popup Gateway – garante que false da API sobrescreva o default */
function applyPopupGatewayFromApi(api: Record<string, unknown>): IPopupGatewayConfig {
  const next: IPopupGatewayConfig = { ...DEFAULT_POPUP_GATEWAY_CONFIG };
  (Object.keys(next) as (keyof IPopupGatewayConfig)[]).forEach((k) => {
    const v = api[k];
    if (typeof v === "boolean") next[k] = v;
  });
  return next;
}

// Função helper para renderizar popup de gateway baseado nas preferências
const renderPopupGateway = (
  local: ILocaisModel,
  config: IPopupGatewayConfig
): string => {
  const items: string[] = [];

  if (config.organizacao && local.Organizacao) {
    items.push(`<b>Organização:</b><span style="color: blue;"> ${local.Organizacao}</span><br>`);
  }

  if (config.local && local.Local) {
    items.push(`<b>Local:</b> ${local.Local}<br>`);
  }

  if (config.gateway && local.Gateway) {
    items.push(`<b>Gateway:</b> ${local.Gateway}<br>`);
  }

  if (config.ultimaAtualizacao && local.UltimaAtualizacao) {
    items.push(`<b>Última Atualização:</b> ${local.UltimaAtualizacao}<br>`);
  }

  if (config.latitude && local.Latitude !== undefined && local.Latitude !== null) {
    items.push(`<b>Latitude:</b> ${local.Latitude}<br>`);
  }

  if (config.longitude && local.Longitude !== undefined && local.Longitude !== null) {
    items.push(`<b>Longitude:</b> ${local.Longitude}<br>`);
  }

  if (config.status && local.Status) {
    items.push(`<b>Status:</b> ${local.Status}<br>`);
  }

  if (items.length === 0) {
    return '<div style="text-align: left;">Nenhum campo selecionado para exibir</div>';
  }

  return items.join('');
};

// Interface para armazenar histórico de posições
interface IPosicaoHistorico {
  latitude: number;
  longitude: number;
  timestamp: string;
  metodo: string;
}

// Interface para o cache de histórico de ativos
interface IHistoricoAtivo {
  [ativoKey: string]: IPosicaoHistorico[];
}

// Função para gerar cores aleatórias por organização
const generateRandomColor = () => {
  return `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0')}`;
};

// Função para criar ícone de gateway personalizado (baseado no Grafana)
const createGatewayIcon = (organizationColor: string, isOnline: boolean) => {
  const corFundo = isOnline ? '#FFFFFF' : '#fb1404';
  const svg = `<svg version="1.1" id="Icons" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="-3.2 -3.2 38.40 38.40" xml:space="preserve" fill="${organizationColor}" stroke="${organizationColor}">
    <g id="SVGRepo_bgCarrier" stroke-width="0" transform="translate(0,0), scale(1)">
      <rect x="-3.2" y="-3.2" width="38.40" height="38.40" rx="19.2" fill="${corFundo}" strokewidth="0"></rect>
    </g>
    <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" stroke="#00000e" stroke-width="3.2">
      <style type="text/css"> .st0{fill:none;stroke:#000000;stroke-width:0.00032;stroke-linecap:round;stroke-linejoin:round;stroke-miterlimit:10;} </style>
      <path d="M16,3C10.5,3,6,7.5,6,13c0,8.4,9,15.5,9.4,15.8c0.2,0.1,0.4,0.2,0.6,0.2s0.4-0.1,0.6-0.2C17,28.5,26,21.4,26,13 C26,7.5,21.5,3,16,3z M16,17c-2.2,0-4-1.8-4-4s1.8-4,4-4s4,1.8,4,4S18.2,17,16,17z"></path>
    </g>
    <g id="SVGRepo_iconCarrier">
      <style type="text/css"> .st0{fill:none;stroke:#000000;stroke-width:0.00032;stroke-linecap:round;stroke-linejoin:round;stroke-miterlimit:10;} </style>
      <path d="M16,3C10.5,3,6,7.5,6,13c0,8.4,9,15.5,9.4,15.8c0.2,0.1,0.4,0.2,0.6,0.2s0.4-0.1,0.6-0.2C17,28.5,26,21.4,26,13 C26,7.5,21.5,3,16,3z M16,17c-2.2,0-4-1.8-4-4s1.8-4,4-4s4,1.8,4,4S18.2,17,16,17z"></path>
    </g>
  </svg>`;

  return new L.Icon({
    iconUrl: "data:image/svg+xml;base64," + btoa(svg),
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  });
};

// Função para criar ícone de beacon GPS (baseado no Grafana)
const createBeaconGPSIcon = () => {
  const borderColor = getMethodBorderColor('gps');
  const svg = `<svg width="202" height="202" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" xml:space="preserve" overflow="hidden">
    <g transform="translate(-551 -1638)">
      <path d="M560 1679.33C560 1662.58 573.581 1649 590.334 1649L711.666 1649C728.419 1649 742 1662.58 742 1679.33L742 1800.67C742 1817.42 728.419 1831 711.666 1831L590.334 1831C573.581 1831 560 1817.42 560 1800.67Z" fill="#DDDDDD" fill-rule="evenodd"/>
      <path d="M595.75 1739C610.669 1739 624.976 1744.93 635.525 1755.48 646.074 1766.02 652 1780.33 652 1795.25M595.75 1694C622.603 1694 648.356 1704.67 667.345 1723.66 686.333 1742.64 697 1768.4 697 1795.25M601.375 1789.62 601.488 1789.62M598 1829 706 1829C718.601 1829 724.903 1829 729.715 1826.55 733.949 1824.39 737.391 1820.95 739.548 1816.72 742 1811.9 742 1805.6 742 1793L742 1685C742 1672.4 742 1666.1 739.548 1661.29 737.391 1657.05 733.949 1653.61 729.715 1651.45 724.903 1649 718.601 1649 706 1649L598 1649C585.399 1649 579.098 1649 574.285 1651.45 570.052 1653.61 566.61 1657.05 564.452 1661.29 562 1666.1 562 1672.4 562 1685L562 1793C562 1805.6 562 1811.9 564.452 1816.72 566.61 1820.95 570.052 1824.39 574.285 1826.55 579.098 1829 585.399 1829 598 1829ZM607 1789.62C607 1792.73 604.482 1795.25 601.375 1795.25 598.269 1795.25 595.75 1792.73 595.75 1789.62 595.75 1786.52 598.269 1784 601.375 1784 604.482 1784 607 1786.52 607 1789.62Z" stroke="${borderColor}" stroke-width="22.2844" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" fill="none" fill-rule="evenodd"/>
    </g>
  </svg>`;

  return new L.Icon({
    iconUrl: "data:image/svg+xml;base64," + btoa(svg),
    iconSize: [18, 18],
    iconAnchor: [9, 18],
    popupAnchor: [0, -18]
  });
};

// Função para criar ícone de beacon baseado no método (adaptado do Grafana)
const createBeaconMethodIcon = (metodo: string) => {
  let svg: string;

  const borderColor = getMethodBorderColor(metodo);

  if (metodo === 'trilateracao') {
    // Ícone cinza para trilateração (3 gateways)
    svg = `<svg width="203" height="202" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" xml:space="preserve" overflow="hidden">
      <g transform="translate(-876 -1638)">
        <path d="M886.5 1678.67C886.5 1662.01 900.006 1648.5 916.667 1648.5L1038.33 1648.5C1054.99 1648.5 1068.5 1662.01 1068.5 1678.67L1068.5 1799.33C1068.5 1815.99 1054.99 1829.5 1038.33 1829.5L916.667 1829.5C900.006 1829.5 886.5 1815.99 886.5 1799.33Z" stroke="${borderColor}" stroke-width="20.625" stroke-miterlimit="8" fill="#DDDDDD" fill-rule="evenodd"/>
      </g>
    </svg>`;
  } else if (metodo === 'centroide') {
    // Ícone dourado para centroide (2 gateways)
    svg = `<svg width="203" height="202" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" xml:space="preserve" overflow="hidden">
      <g transform="translate(-876 -1638)">
        <path d="M886.5 1678.67C886.5 1662.01 900.006 1648.5 916.667 1648.5L1038.33 1648.5C1054.99 1648.5 1068.5 1662.01 1068.5 1678.67L1068.5 1799.33C1068.5 1815.99 1054.99 1829.5 1038.33 1829.5L916.667 1829.5C900.006 1829.5 886.5 1815.99 886.5 1799.33Z" stroke="${borderColor}" stroke-width="20.625" stroke-miterlimit="8" fill="#B8860B" fill-rule="evenodd"/>
      </g>
    </svg>`;
  } else {
    // Ícone amarelo para estimado (1 gateway) ou outros
    svg = `<svg width="203" height="202" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" xml:space="preserve" overflow="hidden">
      <g transform="translate(-876 -1638)">
        <path d="M886.5 1678.67C886.5 1662.01 900.006 1648.5 916.667 1648.5L1038.33 1648.5C1054.99 1648.5 1068.5 1662.01 1068.5 1678.67L1068.5 1799.33C1068.5 1815.99 1054.99 1829.5 1038.33 1829.5L916.667 1829.5C900.006 1829.5 886.5 1815.99 886.5 1799.33Z" stroke="${borderColor}" stroke-width="20.625" stroke-miterlimit="8" fill="#FFFF00" fill-rule="evenodd"/>
      </g>
    </svg>`;
  }

  return new L.Icon({
    iconUrl: "data:image/svg+xml;base64," + btoa(svg),
    iconSize: [18, 18],
    iconAnchor: [9, 18],
    popupAnchor: [0, -18]
  });
};

// Função para criar ícone de beacon triangulado (baseado no Grafana) - mantido para compatibilidade
const createBeaconTriangulatedIcon = () => {
  return createBeaconMethodIcon('trilateracao');
};

// Função para criar ícone de estrela (para destacar gateways)
const createStarIcon = () => {
  const svg = `<svg version="1.0" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 64 64" enable-background="new 0 0 64 64" xml:space="preserve" fill="#000000">
    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
    <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
    <g id="SVGRepo_iconCarrier">
      <path fill="#daa30b" d="M63.893,24.277c-0.238-0.711-0.854-1.229-1.595-1.343l-19.674-3.006L33.809,1.15 C33.479,0.448,32.773,0,31.998,0s-1.48,0.448-1.811,1.15l-8.815,18.778L1.698,22.935c-0.741,0.113-1.356,0.632-1.595,1.343 c-0.238,0.71-0.059,1.494,0.465,2.031l14.294,14.657L11.484,61.67c-0.124,0.756,0.195,1.517,0.822,1.957 c0.344,0.243,0.747,0.366,1.151,0.366c0.332,0,0.666-0.084,0.968-0.25l17.572-9.719l17.572,9.719c0.302,0.166,0.636,0.25,0.968,0.25 c0.404,0,0.808-0.123,1.151-0.366c0.627-0.44,0.946-1.201,0.822-1.957l-3.378-20.704l14.294-14.657 C63.951,25.771,64.131,24.987,63.893,24.277z"></path>
    </g>
  </svg>`;

  return new L.Icon({
    iconUrl: "data:image/svg+xml;base64," + btoa(svg),
    iconSize: [16, 16],
    iconAnchor: [8, 8],
    popupAnchor: [0, -8]
  });
};

// Componente para adicionar markersGroup_C ao mapa
const MapGroupInitializer: React.FC<{ markersGroupCRef: React.RefObject<L.FeatureGroup | null> }> = ({ markersGroupCRef }) => {
  const map = useMap();

  useEffect(() => {
    if (map && markersGroupCRef.current && !map.hasLayer(markersGroupCRef.current)) {
      map.addLayer(markersGroupCRef.current);
      console.log("🗺️ markersGroup_C adicionado ao mapa");
    }

    return () => {
      if (map && markersGroupCRef.current && map.hasLayer(markersGroupCRef.current)) {
        map.removeLayer(markersGroupCRef.current);
        console.log("🗺️ markersGroup_C removido do mapa");
      }
    };
  }, [map, markersGroupCRef]);

  return null;
};

// Componente para adicionar controle de escala ao mapa
const MapScaleControl: React.FC = () => {
  const map = useMap();

  useEffect(() => {
    const scaleControl = L.control.scale({
      position: 'bottomleft',
      metric: true,    // Mostra escala em metros/quilômetros
      imperial: false, // Não mostra escala em milhas/pés
      maxWidth: 200,   // Largura máxima da escala
      updateWhenIdle: false // Atualiza durante o movimento do mapa
    });

    map.addControl(scaleControl);
    console.log("🗺️ Controle de escala adicionado ao mapa");

    return () => {
      map.removeControl(scaleControl);
      console.log("🗺️ Controle de escala removido do mapa");
    };
  }, [map]);

  return null;
};



// Componente do Mapa
const LocaisMapPage: React.FC = () => {
  const { auth } = useAuth();
  const location = useLocation();
  const httpClient = useHttpClient();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const SCREEN_CODE_MAPA_LOCAIS = "MAPA_LOCAIS";
  const cacheUserId = auth?.User?.IdUsuario ?? "";

  const extractCacheJson = useCallback((response: any): any => {
    const container = response?.payload ?? response?.data ?? response;
    if (container == null) return null;
    // GET retorna { success, data: "<string>" } – extrair e parsear o JSON interno
    const dataStr = typeof container === "string" ? container : (container?.Data ?? container?.data);
    if (typeof dataStr === "string") {
      try {
        return JSON.parse(dataStr);
      } catch {
        return null;
      }
    }
    if (typeof container === "object" && container !== null) return container;
    return null;
  }, []);

  const getMapLocaisCache = useCallback(async (): Promise<any> => {
    try {
      const response = await httpClient.request({
        method: RequestMethod.GET,
        endpoint: `/api/Cache/${SCREEN_CODE_MAPA_LOCAIS}?userId=${cacheUserId}`,
      });
      const parsed = extractCacheJson(response);
      if (!parsed || typeof parsed !== "object") return {};

      // Back-compat: cache antigo era um map direto de visibilidade (field -> boolean)
      const values = Object.values(parsed);
      const looksLikeVisibilityMap = values.length > 0 && values.every((v) => typeof v === "boolean");
      if (looksLikeVisibilityMap) return { columnsVisibility: parsed };

      return parsed;
    } catch {
      return {};
    }
  }, [httpClient, extractCacheJson, cacheUserId]);

  const saveMapLocaisCache = useCallback(
    async (patch: Record<string, any>) => {
      let current: any = {};
      try {
        current = await getMapLocaisCache();
      } catch (e) {
        console.warn("Cache GET falhou ao salvar popup, enviando apenas o patch.", e);
      }
      const next = { ...(current || {}), ...(patch || {}) };
      const payload = {
        UserId: cacheUserId,
        ScreenCode: SCREEN_CODE_MAPA_LOCAIS,
        Data: JSON.stringify(next),
      };
      console.log("📤 POST /api/Cache – UserId:", cacheUserId, "ScreenCode: MAPA_LOCAIS, Data (JSON):", next);
      const response = await httpClient.request({
        method: RequestMethod.POST,
        endpoint: "/api/Cache",
        data: payload,
      }) as { success?: boolean; payload?: any };
      if (response && response.success === false) {
        throw new Error("Falha ao salvar no cache. Tente novamente.");
      }
      console.log("✅ Cache (MAPA_LOCAIS) salvo com sucesso.");
    },
    [httpClient, getMapLocaisCache, cacheUserId]
  );

  // Cache para ícones personalizados por ativo+metodo (chave: `${IdAtivo}_${metodo}`)
  const iconCacheRef = useRef<Map<string, L.DivIcon | null>>(new Map());
  // Cache para ícones personalizados de gateways (chave: IdGateway)
  const gatewayIconCacheRef = useRef<Map<number, L.DivIcon | null>>(new Map());
  const [filteredData, setFilteredData] = useState<ILocaisModel[]>([]);
  const [selectedLocal, setSelectedLocal] = useState<string[]>([]);
  const [selectedLocais, setSelectedLocais] = useState<string[]>([]);
  const [localFilter, setLocalFilter] = useState<string>("");
  // ✅ NOVO - Hook para acessar filtros globais
  const { selectedOrganizations, selectedCorporation, availableCorporations, availableOrganizations, isInitialized } = useFiltrosAtivos();
  const [userId, setUserId] = useState<string>("");
  const [resumoLocais, setResumoLocais] = useState<ResumoLocalItem[]>([]);
  const [resumoAssociacao, setResumoAssociacao] = useState<ResumoAssociacaoItem[]>([]);
  const [Locais, setLocais] = useState<ILocaisModel[]>([]);
  const [selectedGatewayData, setSelectedGatewayData] = useState(null);
  const [vercirculos, setVerCirculos] = useState(false);
  const mapRef = useRef<L.Map>(null);
  const markersGroupCRef = useRef<L.FeatureGroup | null>(null);

  // Inicializar markersGroup_C igual ao Grafana
  useEffect(() => {
    if (!markersGroupCRef.current) {
      markersGroupCRef.current = L.featureGroup();
      console.log("🎯 markersGroup_C inicializado");
    }
  }, []);

  // Referência para usar nos eventos (similar ao Grafana)
  const markersGroup_C = markersGroupCRef.current;
  const [selectedAtivos, setSelectedAtivos] = useState<string[]>([]);
  const [availableAtivos, setAvailableAtivos] = useState<string[]>([]);
  const [listaAtivo, setListaAtivo] = useState<IAtivosModel[]>([]);
  
  // Inicializar data e hora com valores atuais como padrão
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const hoje = new Date();
    const ano = hoje.getFullYear();
    const mes = String(hoje.getMonth() + 1).padStart(2, '0');
    const dia = String(hoje.getDate()).padStart(2, '0');
    const dataDefault = `${ano}-${mes}-${dia}`;
    console.log('📅 Data padrão definida:', dataDefault);
    return dataDefault;
  });
  
  const [selectedTime, setSelectedTime] = useState<string>(() => {
    const agora = new Date();
    const hora = String(agora.getHours()).padStart(2, '0');
    const minuto = String(agora.getMinutes()).padStart(2, '0');
    const segundo = String(agora.getSeconds()).padStart(2, '0');
    const horaDefault = `${hora}:${minuto}:${segundo}`;
    console.log('⏰ Hora padrão definida:', horaDefault);
    return horaDefault;
  });
  
  const [ativosUnicos, setAtivosUnicos] = useState<string[]>([]);
  const [beacons, setBeacons] = useState<string[]>([]);
  const [gateway, setGateway] = useState<string[]>([]);
  const [assetBeaconData, setAssetBeaconData] = useState<IAssetBeacon[]>([]);

  // Estados para ícones personalizados de ativos
  const [ativoIconsCache, setAtivoIconsCache] = useState<Map<string, L.DivIcon>>(new Map());
  const iconFetchAbortRef = useRef<AbortController | null>(null);

  // Estados para ícones personalizados de gateways
  const [gatewayIconsCache, setGatewayIconsCache] = useState<Map<number, L.DivIcon | L.Icon>>(new Map());

  const [autoRefresh, setAutoRefresh] = useState(false);
  
  // Estado para configuração do popup de ativos
  // Inicial com padrão; os valores reais vêm do GET /api/Cache (MAPA_LOCAIS) ao carregar a tela
  const [popupConfig, setPopupConfig] = useState<IPopupAtivoConfig>(() => ({ ...DEFAULT_POPUP_CONFIG }));
  const [showPopupConfig, setShowPopupConfig] = useState(false);
  const [popupGatewayConfig, setPopupGatewayConfig] = useState<IPopupGatewayConfig>(() => ({ ...DEFAULT_POPUP_GATEWAY_CONFIG }));
  // Estado para controlar a aba ativa no modal
  const [activeTab, setActiveTab] = useState<'ativos' | 'gateways'>('ativos');
  const [incrementoValor, setIncrementoValor] = useState<number>(10); // Padrão: 10 segundos
  const [incrementoUnidade, setIncrementoUnidade] = useState<'segundos' | 'minutos' | 'horas'>('segundos');
  const [interferenceLevel, setInterferenceLevel] = useState(1);
  const [showGateways, setShowGateways] = useState(true);
  const [showBeacons, setShowBeacons] = useState(true);
  const [lastSeenGateways, setLastSeenGateways] = useState<{ [gateway: string]: { data: any, beacon: IAssetBeacon } }>({});
  const [gatewayMarkers, setGatewayMarkers] = useState<{ [key: string]: L.Marker }>({});
  const [organizationColors, setOrganizationColors] = useState<{ [org: string]: string }>({});
  
  // Estados para histórico de movimento e trilhas
  const [historicoAtivos, setHistoricoAtivos] = useState<IHistoricoAtivo>({});
  const [dataHoraAtual, setDataHoraAtual] = useState<string>("");
  const [mostrarTrilhas, setMostrarTrilhas] = useState(true);
  const contadorRefreshRef = useRef<number>(0);
  const [coresTrilhas, setCoresTrilhas] = useState<{ [ativo: string]: string }>({});
  
  // Estados para geometrias dos locais (array por IdPlace para suportar múltiplos perímetros por local)
  const [locaisGeometrias, setLocaisGeometrias] = useState<{ [idLocal: number]: any[] }>({});
  const [mostrarGeometrias, setMostrarGeometrias] = useState(true);
  
  // Estados para Timeline
  const [timelineTimestampAtual, setTimelineTimestampAtual] = useState<string | null>(null);
  const [timelinePlay, setTimelinePlay] = useState(false);
  const [timelineVelocidade, setTimelineVelocidade] = useState(1); // 1x, 2x, 4x
  const timelineIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [timelineAtiva, setTimelineAtiva] = useState(false);
  const [mostrarTimeline, setMostrarTimeline] = useState(true);
  const [destaqueMarcadores, setDestaqueMarcadores] = useState(false);
  
  // Estado para controlar a aba ativa no carrossel da sidebar
  const [sidebarActiveTab, setSidebarActiveTab] = useState<'locais' | 'timeline'>('locais');
  const [sidebarExpanded, setSidebarExpanded] = useState<boolean>(true);

  const [cacheConfigLoaded, setCacheConfigLoaded] = useState(false);
  const cacheLoadedForUserIdRef = useRef<string | null>(null);

  // 1) ORDEM DE CARGA: um único GET /api/Cache/MAPA_LOCAIS por userId; aplicar popups e só depois liberar mapa/colunas
  useEffect(() => {
    if (!cacheUserId) {
      const t = setTimeout(() => setCacheConfigLoaded(true), 800);
      return () => clearTimeout(t);
    }
    // Evitar loop: só fazer GET uma vez por userId
    if (cacheLoadedForUserIdRef.current === cacheUserId) return;
    cacheLoadedForUserIdRef.current = cacheUserId;

    let cancelled = false;
    (async () => {
      try {
        const response = await httpClient.request({
          method: RequestMethod.GET,
          endpoint: `/api/Cache/${SCREEN_CODE_MAPA_LOCAIS}?userId=${cacheUserId}`,
        });
        if (cancelled) return;
        const parsed = extractCacheJson(response);
        if (parsed && typeof parsed === "object") {
          if (parsed.popupAtivo && typeof parsed.popupAtivo === "object") {
            setPopupConfig(applyPopupAtivoFromApi(parsed.popupAtivo));
          }
          if (parsed.popupGateway && typeof parsed.popupGateway === "object") {
            setPopupGatewayConfig(applyPopupGatewayFromApi(parsed.popupGateway));
          }
        }
      } catch (e) {
        console.warn("Cache dos popups indisponível.", e);
      } finally {
        if (!cancelled) setCacheConfigLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cacheUserId]); // só cacheUserId: GET direto aqui para não depender de getMapLocaisCache (evita loop)

  // Buscar resumo de locais (QuantidadeAtivos por local) – mesma API do Dashboard
  useEffect(() => {
    if (!backendUrl || !selectedOrganizations?.length || !availableOrganizations?.length) {
      setResumoLocais([]);
      setResumoAssociacao([]);
      return;
    }
    const idOrganizacoes = selectedOrganizations
      .map((name) => availableOrganizations.find((o) => o.OrganizationName === name)?.OrganizationId)
      .filter((id): id is number => id != null);
    if (idOrganizacoes.length === 0) {
      setResumoLocais([]);
      setResumoAssociacao([]);
      return;
    }
    const body = { IdOrganizacoes: idOrganizacoes };
    let cancelled = false;
    (async () => {
      try {
        const [resLocais, resAssociacao] = await Promise.all([
          fetch(`${backendUrl}/organizacoes/resumo-locais`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          }),
          fetch(`${backendUrl}/corporacoes/resumo-associacao`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          }),
        ]);
        if (cancelled) return;
        if (resLocais.ok) {
          const data: ResumoLocalItem[] = await resLocais.json();
          setResumoLocais(Array.isArray(data) ? data : []);
        } else {
          setResumoLocais([]);
        }
        if (resAssociacao.ok) {
          const data: ResumoAssociacaoItem[] = await resAssociacao.json();
          setResumoAssociacao(Array.isArray(data) ? data : []);
        } else {
          setResumoAssociacao([]);
        }
      } catch (e) {
        if (!cancelled) {
          console.error("Erro ao buscar resumos (MapLocais):", e);
          setResumoLocais([]);
          setResumoAssociacao([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [backendUrl, selectedOrganizations, availableOrganizations]);

  // Redimensionar o mapa quando o sidebar for recolhido/expandido
  useEffect(() => {
    if (mapRef.current) {
      // Aguardar a transição CSS terminar antes de redimensionar
      setTimeout(() => {
        if (mapRef.current) {
          mapRef.current.invalidateSize();
        }
      }, 350); // 300ms da transição + 50ms de margem
    }
  }, [sidebarExpanded]);

  // Usar useMemo para processar os parâmetros da URL
  const urlParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const orgParam = searchParams.get('org');
    return orgParam ? orgParam.split(',') : [];
  }, [location.search]);

  // Função para obter cor da organização
  const getOrganizationColor = useCallback((organizacao: string) => {
    if (!organizationColors[organizacao]) {
      setOrganizationColors(prev => ({
        ...prev,
        [organizacao]: generateRandomColor()
      }));
      return generateRandomColor();
    }
    return organizationColors[organizacao];
  }, [organizationColors]);

  // ✅ NOVO - Callback quando filtros mudam
  const handleFiltroChange = () => {
    console.log('🔄 Filtros mudaram, limpando dados antigos...');
    // Limpar dados antigos quando filtros mudam
    setAssetBeaconData([]);
    setGateway([]);
    
    // Limpar ativos selecionados quando corporação ou organização mudar
    setSelectedAtivos([]);
    console.log('🧹 Ativos selecionados limpos devido à mudança de filtros');

    // Limpar marcadores do grupo C (círculos e estrelas)
    if (markersGroup_C) {
      markersGroup_C.clearLayers();
    }
    
    // Limpar histórico de trilhas e geometrias
    limparHistorico();
    setLocaisGeometrias({});
  };

  const toggleCirculos = () => {
    setVerCirculos(!vercirculos);
    // Limpar círculos quando desabilitar (quando a nova estado for false)
    if (vercirculos && markersGroup_C) {
      markersGroup_C.clearLayers();
    }
  };

  useEffect(() => {
    if (!showGateways && !showBeacons && markersGroup_C) {
      markersGroup_C.clearLayers();
    }
  }, [showGateways, showBeacons, markersGroup_C]);


  interface ILocaisPayloadRequest {
    Corporacao?: string;
    Organizacoes?: string[];
    Locais?: string[];
    Gateways?: string[];
  }
  interface IExternalPayloadRequest {
    Corporacao?: string;
    Organizacoes?: string[];
    Ativos?: string[];
    Interferencia?: number;
    dtfinal?: string;
  }

  // Obter locais únicos dos dados filtrados
  const locaisUnicos = useMemo(() => {
    return Array.from(
      new Set(filteredData.map(item => item.Local).filter(Boolean))
    ).sort();
  }, [filteredData]);

  // Mapa local nome -> total de ativos (resumo da API resumo-locais)
  const quantidadeAtivosPorLocal = useMemo(() => {
    const map: Record<string, number> = {};
    resumoLocais.forEach((item) => {
      const total = (item.Perimetros ?? []).reduce((s, p) => s + (p.QuantidadeAtivos ?? 0), 0);
      map[item.Local] = (map[item.Local] ?? 0) + total;
    });
    return map;
  }, [resumoLocais]);

  // Totais do resumo de associação (Disponível = não associados, Associado)
  const resumoAssociacaoTotais = useMemo(() => {
    const disponivel = resumoAssociacao.reduce((s, i) => s + (i.QtdNaoAssociados ?? 0), 0);
    const associado = resumoAssociacao.reduce((s, i) => s + (i.QtdAssociados ?? 0), 0);
    return { disponivel, associado };
  }, [resumoAssociacao]);

  const isVwco = useMemo(() => {
    const nome = selectedCorporation ? availableCorporations.find(c => c.id === selectedCorporation)?.name : null;
    return nome === "VWCO";
  }, [selectedCorporation, availableCorporations]);

  const toggleSelectAll = () => {
    // Verificar se todos os locais únicos estão selecionados
    const todosLocaisSelecionados = locaisUnicos.length > 0 &&
      locaisUnicos.every(local => selectedLocais.includes(local));

    if (todosLocaisSelecionados) {
      // Desmarcar todos
      setSelectedLocais([]);
      setSelectedLocal([]);
    } else {
      // Marcar todos
      setSelectedLocais(locaisUnicos);
      // Selecionar todos os gateways dos locais únicos
      const todosGateways = filteredData
        .filter(item => locaisUnicos.includes(item.Local))
        .map(item => item.Gateway);
      setSelectedLocal(todosGateways);
    }
  };


  const getGatewaysProximos = (gatewayId: string, allGateways: ILocaisModel[]) => {
    // 1. Encontrar o gateway selecionado
    const gatewaySelecionado = allGateways.find(g => g.Gateway === gatewayId);

    if (!gatewaySelecionado || !gatewaySelecionado.Latitude || !gatewaySelecionado.Longitude) {
      return [];
    }

    // 2. Calcular distâncias para todos os gateways
    const gatewaysComDistancia = allGateways
      .filter(g => g.Gateway !== gatewayId && g.Latitude && g.Longitude)
      .map(g => {
        const distancia = calcularDistancia(
          gatewaySelecionado.Latitude!,
          gatewaySelecionado.Longitude!,
          g.Latitude!,
          g.Longitude!
        );
        return { ...g, distancia };
      });

    // 3. Ordenar por distância e pegar os 3 mais próximos
    const gatewaysProximos = gatewaysComDistancia
      .sort((a, b) => a.distancia - b.distancia)
      .slice(0, 3);

    // 4. Adicionar distância calculada (simulando o objeto do original)
    return gatewaysProximos.map(gw => ({
      ...gw,
      Distancia: gw.distancia * 1000, // Convertendo para metros
      Id: gw.Gateway // Usando Gateway como ID
    }));
  };

  // Função auxiliar para calcular distância entre coordenadas (fórmula de Haversine)
  const calcularDistancia = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371; // Raio da Terra em km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distância em km
  };

  // Função para verificar se um ponto está dentro de um polígono (algoritmo Ray Casting)
  // GeoJSON usa formato [longitude, latitude]
  const pontoDentroPoligono = (lat: number, lng: number, geometry: any): boolean => {
    if (!geometry || !geometry.coordinates) return false;

    try {
      // Extrair coordenadas do polígono
      let coordinates: number[][][] = [];
      
      if (geometry.type === 'Polygon') {
        coordinates = geometry.coordinates;
      } else if (geometry.type === 'MultiPolygon') {
        // Para MultiPolygon, verificar em cada polígono
        return geometry.coordinates.some((poly: number[][][]) => 
          pontoDentroPoligono(lat, lng, { type: 'Polygon', coordinates: poly })
        );
      } else {
        return false;
      }

      // Verificar em cada anel do polígono (o primeiro é o exterior, os outros são buracos)
      const exteriorRing = coordinates[0];
      if (!exteriorRing || exteriorRing.length < 3) return false;

      let inside = false;
      // GeoJSON: [longitude, latitude] = [x, y]
      for (let i = 0, j = exteriorRing.length - 1; i < exteriorRing.length; j = i++) {
        const xi = exteriorRing[i][1]; // latitude (y)
        const yi = exteriorRing[i][0]; // longitude (x)
        const xj = exteriorRing[j][1]; // latitude (y)
        const yj = exteriorRing[j][0]; // longitude (x)

        // Algoritmo Ray Casting: verificar se o raio cruza a aresta
        const intersect = ((yi > lng) !== (yj > lng)) &&
          (lat < (xj - xi) * (lng - yi) / (yj - yi) + xi);
        if (intersect) inside = !inside;
      }

      // Verificar se está dentro de algum buraco (se houver)
      for (let ringIndex = 1; ringIndex < coordinates.length; ringIndex++) {
        const hole = coordinates[ringIndex];
        let inHole = false;
        for (let i = 0, j = hole.length - 1; i < hole.length; j = i++) {
          const xi = hole[i][1]; // latitude (y)
          const yi = hole[i][0]; // longitude (x)
          const xj = hole[j][1]; // latitude (y)
          const yj = hole[j][0]; // longitude (x)

          const intersect = ((yi > lng) !== (yj > lng)) &&
            (lat < (xj - xi) * (lng - yi) / (yj - yi) + xi);
          if (intersect) inHole = !inHole;
        }
        if (inHole) {
          inside = false; // Se está em um buraco, não está dentro
          break;
        }
      }

      return inside;
    } catch (error) {
      console.error('Erro ao verificar ponto no polígono:', error);
      return false;
    }
  };

  // Função para contar ativos dentro de um perímetro
  const contarAtivosNoPerimetro = useCallback((feature: any): number => {
    if (!feature || !feature.geometry) return 0;

    let count = 0;

    // Contar beacons GPS
    assetBeaconData.forEach(beacon => {
      if (
        beacon.Latitude &&
        beacon.Longitude &&
        selectedAtivos.includes(beacon.Ativo) &&
        pontoDentroPoligono(beacon.Latitude, beacon.Longitude, feature.geometry)
      ) {
        count++;
      }
    });

    // Contar beacons trilaterados
    if (Array.isArray(gateway)) {
      gateway.forEach((asset: any) => {
        if (!selectedAtivos.includes(asset.Ativo)) return;

        let lat, lng;
        if (asset.Metodo === 'estimado' || asset.Metodo === 'centroide') {
          lat = asset.LatitudeAleatoria || asset.Latitude;
          lng = asset.LongitudeAleatoria || asset.Longitude;
        } else {
          lat = asset.Latitude;
          lng = asset.Longitude;
        }

        if (lat && lng && pontoDentroPoligono(lat, lng, feature.geometry)) {
          count++;
        }
      });
    }

    return count;
  }, [assetBeaconData, gateway, selectedAtivos]);

  // Função para incrementar data/hora com valor e unidade configuráveis
  const incrementarDataHora = (dataHoraStr: string, valor: number = 0, unidade: 'segundos' | 'minutos' | 'horas' = 'segundos'): string => {
    if (!dataHoraStr || !dataHoraStr.trim()) {
      console.error('❌ incrementarDataHora: string vazia ou inválida');
      return "";
    }
    
    try {
      const partes = dataHoraStr.trim().split(' ');
      
      // Validar que temos data e hora
      if (partes.length < 2) {
        console.error('❌ incrementarDataHora: formato inválido (falta data ou hora):', dataHoraStr);
        return "";
      }
      
      const dataParte = partes[0];
      const horaParte = partes[1];
      
      // Validar formato da data
      if (!dataParte || !dataParte.includes('-')) {
        console.error('❌ incrementarDataHora: data inválida:', dataParte);
        return "";
      }
      
      const [ano, mes, dia] = dataParte.split('-');
      const [hora, minuto, segundo] = horaParte.split(':');
      
      // Validar valores
      if (!ano || !mes || !dia || !hora || !minuto) {
        console.error('❌ incrementarDataHora: valores ausentes', { ano, mes, dia, hora, minuto, segundo });
        return "";
      }
      
      const data = new Date(
        parseInt(ano),
        parseInt(mes) - 1,
        parseInt(dia),
        parseInt(hora),
        parseInt(minuto),
        parseInt(segundo || '0')
      );
      
      // Validar se a data é válida
      if (isNaN(data.getTime())) {
        console.error('❌ incrementarDataHora: data inválida após conversão:', dataHoraStr);
        return "";
      }
      
      // Adicionar incremento baseado na unidade
      if (valor > 0) {
        switch (unidade) {
          case 'segundos':
            data.setSeconds(data.getSeconds() + valor);
            break;
          case 'minutos':
            data.setMinutes(data.getMinutes() + valor);
            break;
          case 'horas':
            data.setHours(data.getHours() + valor);
            break;
        }
      }
      
      // Formatar de volta para YYYY-MM-DD HH:mm:ss
      const pad2 = (n: number) => n.toString().padStart(2, '0');
      const novaDataHora = `${data.getFullYear()}-${pad2(data.getMonth() + 1)}-${pad2(data.getDate())} ${pad2(data.getHours())}:${pad2(data.getMinutes())}:${pad2(data.getSeconds())}`;
      
      const incrementoTexto = valor > 0 ? `${valor} ${unidade}` : '0';
      console.log(`⏰ Data incrementada: ${dataHoraStr} → ${novaDataHora} (+${incrementoTexto})`);
      return novaDataHora;
    } catch (error) {
      console.error('❌ Erro ao incrementar data/hora:', error, 'Input:', dataHoraStr);
      return "";
    }
  };

  // Subtrai 1 minuto do dtfinal para evitar erro na API (horário atual pode não ter dados ainda)
  const dtfinalMenosUmMinuto = (dataHoraStr: string): string => {
    if (!dataHoraStr || !dataHoraStr.trim()) return dataHoraStr || '';
    try {
      const partes = dataHoraStr.trim().split(' ');
      if (partes.length < 2) return dataHoraStr;
      const [ano, mes, dia] = partes[0].split('-');
      const [hora, minuto, segundo] = partes[1].split(':');
      const data = new Date(
        parseInt(ano, 10),
        parseInt(mes, 10) - 1,
        parseInt(dia, 10),
        parseInt(hora, 10),
        parseInt(minuto || '0', 10),
        parseInt(segundo || '0', 10)
      );
      if (isNaN(data.getTime())) return dataHoraStr;
      data.setMinutes(data.getMinutes() - 1);
      const pad2 = (n: number) => n.toString().padStart(2, '0');
      return `${data.getFullYear()}-${pad2(data.getMonth() + 1)}-${pad2(data.getDate())} ${pad2(data.getHours())}:${pad2(data.getMinutes())}:${pad2(data.getSeconds())}`;
    } catch {
      return dataHoraStr;
    }
  };

  // Função para adicionar posição ao histórico
  const adicionarPosicaoHistorico = (ativo: string, latitude: number, longitude: number, timestamp: string, metodo: string) => {
    // Validações
    if (!ativo || !timestamp || !timestamp.trim()) {
      console.warn('⚠️ adicionarPosicaoHistorico: dados inválidos', { ativo, timestamp });
      return;
    }
    
    if (timestamp.includes('NaN')) {
      console.warn('⚠️ adicionarPosicaoHistorico: timestamp contém NaN, ignorando', { ativo, timestamp });
      return;
    }
    
    if (typeof latitude !== 'number' || typeof longitude !== 'number' || isNaN(latitude) || isNaN(longitude)) {
      console.warn('⚠️ adicionarPosicaoHistorico: coordenadas inválidas', { ativo, latitude, longitude });
      return;
    }
    
    const key = ativo;
    
    setHistoricoAtivos(prev => {
      const historicoAtivo = prev[key] || [];
      
      // Verificar se já existe posição com mesmo timestamp
      const jaExiste = historicoAtivo.some(pos => pos.timestamp === timestamp);
      if (jaExiste) {
        console.log(`📍 Posição já registrada para ${ativo} em ${timestamp}`);
        return prev;
      }
      
      // Adicionar nova posição
      const novaPosicao: IPosicaoHistorico = {
        latitude,
        longitude,
        timestamp,
        metodo
      };
      
      const novoHistorico = [...historicoAtivo, novaPosicao];
      console.log(`✅ Nova posição registrada para ${ativo}:`, {
        total: novoHistorico.length,
        timestamp,
        lat: latitude.toFixed(5),
        lng: longitude.toFixed(5),
        metodo
      });
      
      return {
        ...prev,
        [key]: novoHistorico
      };
    });
  };

  // Função para limpar histórico
  const limparHistorico = () => {
    setHistoricoAtivos({});
    setCoresTrilhas({});
    setAssetBeaconData([]);
    setGateway([]);
    contadorRefreshRef.current = 0;
    setDataHoraAtual("");
    setTimelineTimestampAtual(null);
    setTimelineAtiva(false);
    if (timelineIntervalRef.current) {
      clearInterval(timelineIntervalRef.current);
      timelineIntervalRef.current = null;
    }
    setTimelinePlay(false);
    console.log('🧹 Histórico de posições, cores de trilhas e marcadores limpos');
  };

  // Função para limpar o mapa e retornar ao estado inicial
  const limparMapa = () => {
    // Limpar histórico e dados
    limparHistorico();
    
    // Desativar auto-refresh
    setAutoRefresh(false);
    contadorRefreshRef.current = 0;
    
    // Limpar marcadores do grupo C (círculos e estrelas)
    if (markersGroup_C) {
      markersGroup_C.clearLayers();
    }
    
    // Limpar geometrias (opcional - comentado para manter os perímetros)
    // setLocaisGeometrias({});
    
    // Ocultar timeline
    setMostrarTimeline(false);
    
    // Resetar zoom do mapa para posição inicial (se necessário)
    if (mapRef.current) {
      mapRef.current.setView([-22.0, -47.0], 6);
    }
    
    console.log('🗺️ Mapa limpo e retornado ao estado inicial');
  };

  // ✅ NOVO - Função auxiliar para verificar se todos os ativos estão selecionados
  // Se todos estiverem selecionados, retorna ["Todos"] para evitar processamento desnecessário
  const obterAtivosParaAPI = useCallback((ativosSelecionados: string[]): string[] => {
    if (ativosSelecionados.length === 0) {
      return ["Todos"];
    }

    // Obter lista de ativos únicos disponíveis
    const ativosUnicos = Array.from(
      new Set(listaAtivo.map((ativo) => ativo.Ativo && ativo.Ativo.trim()).filter(Boolean))
    );

    // Se todos os ativos disponíveis estão selecionados, retornar ["Todos"]
    if (ativosUnicos.length > 0 && ativosUnicos.every(a => ativosSelecionados.includes(a))) {
      console.log('✅ Todos os ativos selecionados - usando "Todos" para otimizar performance');
      return ["Todos"];
    }

    // Caso contrário, retornar a lista de ativos selecionados
    return ativosSelecionados;
  }, [listaAtivo]);

  // Função para limpar o mapa e recarregar dados via GatewayNred
  const limparMapaERecarregar = useCallback(async () => {
    console.log('🧹 Limpando mapa e recarregando dados...');
    
    // 1. Limpar histórico e dados do mapa
    limparHistorico();
    setAssetBeaconData([]);
    setGateway([]);
    
    // Limpar marcadores do grupo C (círculos e estrelas)
    if (markersGroup_C) {
      markersGroup_C.clearLayers();
    }
    
    // Verificar se há filtros globais selecionados
    if (selectedOrganizations.length > 0 && selectedCorporation) {
      // 2. Recarregar dados de locais (gateways no mapa)
      await fetchLocalData();
      
      // 3. Recarregar dados via GatewayNred baseado nos filtros globais
      // Se houver ativos selecionados, usar eles; caso contrário, usar "Todos"
      if (selectedAtivos.length > 0 && listaAtivo.length > 0) {
        // ✅ OTIMIZAÇÃO: Verificar se todos os ativos estão selecionados
        const ativosParaAPI = obterAtivosParaAPI(selectedAtivos);
        
        // Se retornou ["Todos"], usar diretamente sem separação
        if (ativosParaAPI.length === 1 && ativosParaAPI[0] === "Todos") {
          console.log('🔄 Recarregando dados via GatewayNred para TODOS os ativos (otimizado)');
          
          // Chamada para Tracking B (GPS) com "Todos"
          await fetchBeaconTrackingData({
            Ativos: ["Todos"],
            Interferencia: interferenceLevel,
            Organizacoes: selectedOrganizations,
          });
          
          // Chamada para Tracking G (GatewayNred) com "Todos"
          await fetchGatewayTrackingData({
            Ativos: ["Todos"],
            Interferencia: interferenceLevel,
            Organizacoes: selectedOrganizations,
          });
        } else {
          console.log('🔄 Recarregando dados via GatewayNred para ativos selecionados:', selectedAtivos);
          
          // Separar ativos por origem (B ou G)
          const ativosParaGPS: string[] = [];
          const ativosParaGateway: string[] = [];
          
          selectedAtivos.forEach(nomeAtivo => {
            const ativoObj = listaAtivo.find(a => a.Ativo === nomeAtivo);
            if (ativoObj) {
              if (ativoObj.LastlatLongOrigin === 'B') {
                ativosParaGPS.push(nomeAtivo);
              } else {
                ativosParaGateway.push(nomeAtivo);
              }
            }
          });
          
          console.log(`📊 Separação de Ativos: GPS(B): ${ativosParaGPS.length}, Gateway(G): ${ativosParaGateway.length}`);
          
          // Chamada para Tracking B (GPS)
          if (ativosParaGPS.length > 0) {
            await fetchBeaconTrackingData({
              Ativos: ativosParaGPS,
              Interferencia: interferenceLevel,
              Organizacoes: selectedOrganizations,
            });
          } else {
            setAssetBeaconData([]);
          }
          
          // Chamada para Tracking G (GatewayNred)
          if (ativosParaGateway.length > 0) {
            await fetchGatewayTrackingData({
              Ativos: ativosParaGateway,
              Interferencia: interferenceLevel,
              Organizacoes: selectedOrganizations,
            });
          } else {
            setGateway([]);
          }
        }
      } else {
        // Sem ativos selecionados, recarregar com "Todos" baseado nos filtros globais
        console.log('🔄 Recarregando dados via GatewayNred com "Todos" os ativos baseado nos filtros globais');
        
        // Chamada para GatewayNred com "Todos"
        await fetchGatewayTrackingData({
          Ativos: ["Todos"],
          Interferencia: interferenceLevel,
          Organizacoes: selectedOrganizations,
        });
        
        // Chamada para BeaconNred com "Todos"
        await fetchBeaconTrackingData({
          Ativos: ["Todos"],
          Interferencia: interferenceLevel,
          Organizacoes: selectedOrganizations,
        });
      }
      
      console.log('✅ Mapa limpo e dados recarregados com sucesso');
    } else {
      console.log('⚠️ Nenhum filtro global selecionado, apenas limpando mapa');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganizations, selectedCorporation, selectedAtivos, listaAtivo, interferenceLevel, markersGroup_C, obterAtivosParaAPI]);

  // Função para recarregar dados do mapa baseado nos filtros globais atuais
  const recarregarDadosMapa = useCallback(async () => {
    console.log('🔄 Recarregando dados do mapa baseado nos filtros globais...');
    
    // Verificar se há filtros globais selecionados
    if (selectedOrganizations.length > 0 && selectedCorporation) {
      // Recarregar dados de locais (gateways no mapa)
      await fetchLocalData();
      
      console.log('✅ Dados do mapa recarregados com sucesso');
    } else {
      console.log('⚠️ Nenhum filtro global selecionado, não é possível recarregar dados');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganizations, selectedCorporation]);


  // Função para coletar todos os timestamps únicos do histórico de ativos
  const coletarTimestampsUnicos = useMemo(() => {
    const timestampsSet = new Set<string>();
    
    Object.values(historicoAtivos).forEach(posicoes => {
      posicoes.forEach(pos => {
        if (pos.timestamp && pos.timestamp.trim()) {
          timestampsSet.add(pos.timestamp);
        }
      });
    });
    
    // Converter para array e ordenar cronologicamente
    const timestampsArray = Array.from(timestampsSet).sort((a, b) => {
      const dateA = moment(a, 'YYYY-MM-DD HH:mm:ss').valueOf();
      const dateB = moment(b, 'YYYY-MM-DD HH:mm:ss').valueOf();
      return dateA - dateB;
    });
    
    return timestampsArray;
  }, [historicoAtivos]);

  // Função para obter índice atual na timeline
  const indiceTimelineAtual = useMemo(() => {
    if (!timelineTimestampAtual) return -1;
    return coletarTimestampsUnicos.indexOf(timelineTimestampAtual);
  }, [timelineTimestampAtual, coletarTimestampsUnicos]);

  // Função para filtrar histórico de ativos até um timestamp específico
  const filtrarHistoricoPorTimestamp = useCallback((timestamp: string | null): IHistoricoAtivo => {
    if (!timestamp) return historicoAtivos;
    
    const historicoFiltrado: IHistoricoAtivo = {};
    
    Object.entries(historicoAtivos).forEach(([ativoKey, posicoes]) => {
      // Filtrar posições até o timestamp selecionado (inclusive)
      const posicoesFiltradas = posicoes.filter(pos => {
        const posTimestamp = moment(pos.timestamp, 'YYYY-MM-DD HH:mm:ss').valueOf();
        const timestampLimite = moment(timestamp, 'YYYY-MM-DD HH:mm:ss').valueOf();
        return posTimestamp <= timestampLimite;
      });
      
      if (posicoesFiltradas.length > 0) {
        historicoFiltrado[ativoKey] = posicoesFiltradas;
      }
    });
    
    return historicoFiltrado;
  }, [historicoAtivos]);

  // Histórico filtrado baseado no timestamp da timeline
  const historicoFiltradoTimeline = useMemo(() => {
    return filtrarHistoricoPorTimestamp(timelineTimestampAtual);
  }, [filtrarHistoricoPorTimestamp, timelineTimestampAtual]);

  // Função para navegar para o próximo timestamp
  const timelineProximo = useCallback(() => {
    if (indiceTimelineAtual < coletarTimestampsUnicos.length - 1) {
      const proximoIndice = indiceTimelineAtual + 1;
      setTimelineTimestampAtual(coletarTimestampsUnicos[proximoIndice]);
      // Ativar destaque dos marcadores
      setDestaqueMarcadores(true);
      setTimeout(() => setDestaqueMarcadores(false), 1000); // Desativar após 1 segundo
    }
  }, [indiceTimelineAtual, coletarTimestampsUnicos]);

  // Função para navegar para o timestamp anterior
  const timelineAnterior = useCallback(() => {
    if (indiceTimelineAtual > 0) {
      const anteriorIndice = indiceTimelineAtual - 1;
      setTimelineTimestampAtual(coletarTimestampsUnicos[anteriorIndice]);
      // Ativar destaque dos marcadores
      setDestaqueMarcadores(true);
      setTimeout(() => setDestaqueMarcadores(false), 1000); // Desativar após 1 segundo
    }
  }, [indiceTimelineAtual, coletarTimestampsUnicos]);

  // Função para ir para o início
  const timelineInicio = useCallback(() => {
    if (coletarTimestampsUnicos.length > 0) {
      setTimelineTimestampAtual(coletarTimestampsUnicos[0]);
      // Ativar destaque dos marcadores
      setDestaqueMarcadores(true);
      setTimeout(() => setDestaqueMarcadores(false), 1000);
    }
  }, [coletarTimestampsUnicos]);

  // Função para ir para o fim
  const timelineFim = useCallback(() => {
    if (coletarTimestampsUnicos.length > 0) {
      setTimelineTimestampAtual(coletarTimestampsUnicos[coletarTimestampsUnicos.length - 1]);
      // Ativar destaque dos marcadores
      setDestaqueMarcadores(true);
      setTimeout(() => setDestaqueMarcadores(false), 1000);
    }
  }, [coletarTimestampsUnicos]);

  // Função para pausar/play da timeline
  const toggleTimelinePlay = useCallback(() => {
    if (timelinePlay) {
      // Pausar
      if (timelineIntervalRef.current) {
        clearInterval(timelineIntervalRef.current);
        timelineIntervalRef.current = null;
      }
      setTimelinePlay(false);
    } else {
      // Play
      if (coletarTimestampsUnicos.length === 0) return;
      
      // Se estiver no fim, voltar ao início
      if (indiceTimelineAtual >= coletarTimestampsUnicos.length - 1) {
        timelineInicio();
      }
      
      const intervalMs = 1000 / timelineVelocidade; // 1000ms para 1x, 500ms para 2x, 250ms para 4x
      
      timelineIntervalRef.current = setInterval(() => {
        setTimelineTimestampAtual(prev => {
          if (!prev) {
            const novo = coletarTimestampsUnicos[0];
            setDestaqueMarcadores(true);
            setTimeout(() => setDestaqueMarcadores(false), 1000);
            return novo;
          }
          
          const currentIndex = coletarTimestampsUnicos.indexOf(prev);
          if (currentIndex < coletarTimestampsUnicos.length - 1) {
            const novo = coletarTimestampsUnicos[currentIndex + 1];
            setDestaqueMarcadores(true);
            setTimeout(() => setDestaqueMarcadores(false), 1000);
            return novo;
          } else {
            // Chegou ao fim, pausar
            if (timelineIntervalRef.current) {
              clearInterval(timelineIntervalRef.current);
              timelineIntervalRef.current = null;
            }
            setTimelinePlay(false);
            return prev;
          }
        });
      }, intervalMs);
      
      setTimelinePlay(true);
    }
  }, [timelinePlay, coletarTimestampsUnicos, indiceTimelineAtual, timelineVelocidade, timelineInicio]);

  // Atualizar velocidade da timeline e resetar intervalo se estiver rodando
  useEffect(() => {
    if (timelinePlay && timelineIntervalRef.current) {
      clearInterval(timelineIntervalRef.current);
      timelineIntervalRef.current = null;
      
      // Reiniciar com nova velocidade
      if (coletarTimestampsUnicos.length === 0) return;
      
      if (indiceTimelineAtual >= coletarTimestampsUnicos.length - 1) {
        timelineInicio();
      }
      
      const intervalMs = 1000 / timelineVelocidade;
      
      timelineIntervalRef.current = setInterval(() => {
        setTimelineTimestampAtual(prev => {
          if (!prev) {
            return coletarTimestampsUnicos[0];
          }
          
          const currentIndex = coletarTimestampsUnicos.indexOf(prev);
          if (currentIndex < coletarTimestampsUnicos.length - 1) {
            return coletarTimestampsUnicos[currentIndex + 1];
          } else {
            if (timelineIntervalRef.current) {
              clearInterval(timelineIntervalRef.current);
              timelineIntervalRef.current = null;
            }
            setTimelinePlay(false);
            return prev;
          }
        });
      }, intervalMs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineVelocidade]);

  // Limpar intervalo ao desmontar
  useEffect(() => {
    return () => {
      if (timelineIntervalRef.current) {
        clearInterval(timelineIntervalRef.current);
      }
    };
  }, []);

  // Ativar timeline quando houver dados
  useEffect(() => {
    if (coletarTimestampsUnicos.length > 0 && !timelineAtiva) {
      setTimelineAtiva(true);
      // Mudar automaticamente para a aba de timeline quando ativada
      setSidebarActiveTab('timeline');
      // Definir timestamp inicial como o primeiro
      if (!timelineTimestampAtual) {
        setTimelineTimestampAtual(coletarTimestampsUnicos[0]);
      }
    } else if (coletarTimestampsUnicos.length === 0) {
      setTimelineAtiva(false);
      setTimelineTimestampAtual(null);
      if (timelinePlay) {
        if (timelineIntervalRef.current) {
          clearInterval(timelineIntervalRef.current);
          timelineIntervalRef.current = null;
        }
        setTimelinePlay(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coletarTimestampsUnicos.length]);

  // Efeito para destacar marcadores quando o timestamp muda (incluindo slider)
  useEffect(() => {
    if (timelineAtiva && timelineTimestampAtual) {
      setDestaqueMarcadores(true);
      const timer = setTimeout(() => {
        setDestaqueMarcadores(false);
      }, 1000); // Desativar após 1 segundo
      return () => clearTimeout(timer);
    }
  }, [timelineTimestampAtual, timelineAtiva]);

  const handleAutoRefreshToggle = () => {
    const novoEstado = !autoRefresh;
    
    if (novoEstado) {
      // Validar se há data e hora antes de ativar
      if (!selectedDate || !selectedTime) {
        alert('⚠️ Por favor, selecione uma DATA e HORA antes de ativar o Auto-Refresh!');
        console.error('❌ Tentativa de ativar auto-refresh sem data/hora selecionada');
        return;
      }
      
      // Validar se há incremento configurado
      if (!incrementoValor || incrementoValor <= 0) {
        alert(`⚠️ Por favor, configure o INCREMENTO antes de ativar o Auto-Refresh!`);
        console.error('❌ Tentativa de ativar auto-refresh sem incremento configurado');
        return;
      }
      
      // Ao ativar, inicializar com a data/hora fornecida
      const dataInicial = `${selectedDate} ${selectedTime}`;
      
      // Validar formato
      if (!dataInicial.includes('-') || dataInicial.trim().length < 10) {
        alert('⚠️ Formato de data inválido! Use YYYY-MM-DD HH:mm:ss');
        console.error('❌ Formato de data inválido:', dataInicial);
        return;
      }
      
      setDataHoraAtual(dataInicial);
      contadorRefreshRef.current = 0;
      setAutoRefresh(true);
      console.log(`🚀 Auto-refresh ativado - Data inicial: ${dataInicial}, Incremento: ${incrementoValor} ${incrementoUnidade}`);
    } else {
      // Ao desativar, resetar contador mas manter histórico
      contadorRefreshRef.current = 0;
      setAutoRefresh(false);
      
      // Ocultar timeline quando desativar auto-refresh
      setTimelineAtiva(false);
      setMostrarTimeline(false);
      setTimelineTimestampAtual(null);
      if (timelineIntervalRef.current) {
        clearInterval(timelineIntervalRef.current);
        timelineIntervalRef.current = null;
      }
      setTimelinePlay(false);
      
      console.log('⏸️ Auto-refresh desativado - Timeline ocultada');
    }
  };

  const handleInterferenceLevelChange = (level: number) => {
    setInterferenceLevel(level);
  };

  const handleLocalSelection = (localName: string) => {
    const isSelected = selectedLocais.includes(localName);

    if (isSelected) {
      // Desmarcar local - remover todos os gateways deste local
      setSelectedLocais(prev => prev.filter(local => local !== localName));
      const gatewaysDoLocal = filteredData
        .filter(item => item.Local === localName)
        .map(item => item.Gateway);
      setSelectedLocal(prev => prev.filter(gateway => !gatewaysDoLocal.includes(gateway)));
    } else {
      // Marcar local - adicionar todos os gateways deste local
      setSelectedLocais(prev => [...prev, localName]);
      const gatewaysDoLocal = filteredData
        .filter(item => item.Local === localName)
        .map(item => item.Gateway);
      setSelectedLocal(prev => [...prev, ...gatewaysDoLocal]);
    }
  };

  // ❌ REMOVIDO - fetchUserId e fetchUserOrganizations agora são gerenciados pelo FiltrosProvider global


  // Função para obter ícone personalizado para um ativo específico
  const getCustomAtivoIcon = async (ativo: IAtivosModel, metodo?: string): Promise<L.DivIcon> => {
    console.log(`\n🔄 Iniciando busca de ícone para ativo:`, {
      nome: ativo.Ativo,
      id: ativo.IdAtivo,
      metodo: metodo || 'padrão'
    });

    try {
      if (!ativo.IdAtivo) {
        console.log(`⚠️ Ativo sem IdAtivo:`, {
          nome: ativo.Ativo,
          dadosAtivo: ativo
        });
        return createDefaultAtivoIcon(metodo);
      }

      // Verificar cache atual
      if (iconCacheRef.current) {
        const cacheKey = `${ativo.IdAtivo}_${metodo || 'default'}`;
        const cacheStatus = iconCacheRef.current.get(cacheKey);
        console.log(`📦 Status do cache para ativo ${ativo.Ativo}:`, {
          chave: cacheKey,
          temCache: cacheStatus !== undefined,
          tipoIcone: cacheStatus ? 'personalizado' : 'nenhum/padrão'
        });
      }

      // Buscar ícone personalizado
      const icones = await fetchIconsForAtivos([ativo.IdAtivo], auth, backendUrl, iconCacheRef.current, metodo);
      const customIcon = icones.get(ativo.IdAtivo);

      if (customIcon) {
        console.log(`✨ Ícone personalizado aplicado com sucesso:`, {
          ativo: ativo.Ativo,
          id: ativo.IdAtivo,
          metodo: metodo || 'padrão',
          tipo: 'personalizado'
        });
        return customIcon;
      } else {
        console.log(`ℹ️ Nenhum ícone personalizado encontrado:`, {
          ativo: ativo.Ativo,
          id: ativo.IdAtivo,
          metodo: metodo || 'padrão',
          solucao: 'usando padrão'
        });
      }
    } catch (error) {
      console.warn(`❌ Erro ao buscar ícone personalizado:`, {
        ativo: ativo.Ativo,
        id: ativo.IdAtivo,
        erro: error
      });

      // Se houver erro no cache, limpar para tentar novamente na próxima vez
      if (iconCacheRef.current && ativo.IdAtivo) {
        const cacheKey = `${ativo.IdAtivo}_${metodo || 'default'}`;
        iconCacheRef.current.delete(cacheKey);
        console.log(`🧹 Cache limpo para ativo ${ativo.Ativo} (chave ${cacheKey}) após erro`);
      }
    }

    // Retornar ícone padrão se não conseguir buscar personalizado
    console.log(`⭐ Usando ícone padrão:`, {
      ativo: ativo.Ativo,
      id: ativo.IdAtivo,
      metodo: metodo || 'padrão',
      motivo: 'fallback após tentativa de personalizado'
    });
    return createDefaultAtivoIcon(metodo);
  };

  // Função para obter ícone personalizado para um beacon baseado no nome do ativo
  const getCustomBeaconIcon = async (nomeAtivo: string, metodo?: string): Promise<L.DivIcon> => {
    console.log(`\n🔍 getCustomBeaconIcon iniciado para:`, {
      nomeAtivo,
      metodo,
      totalAtivosLista: listaAtivo.length
    });

    try {
      // Criar chave de cache que inclui o método
      const cacheKey = `${nomeAtivo}_${metodo || 'default'}`;

      // Verificar cache primeiro
      const cached = ativoIconsCache.get(cacheKey);
      if (cached) {
        console.log(`📦 Ícone encontrado no cache para ${nomeAtivo}`);
        return cached;
      }

      // Buscar o ativo na lista para obter o IdAtivo
      const ativo = listaAtivo.find(a => a.Ativo === nomeAtivo);
      console.log(`🔎 Busca de ativo na lista:`, {
        nomeAtivo,
        encontrado: !!ativo,
        idAtivo: ativo?.IdAtivo,
        totalAtivos: listaAtivo.length
      });

      if (ativo && ativo.IdAtivo) {
        // Buscar todos os ativos que precisam de ícones
        const ativosParaBuscar = listaAtivo
          .filter(a => a.IdAtivo && !ativoIconsCache.get(`${a.Ativo}_${metodo || 'default'}`))
          .map(a => a.IdAtivo!)
          .filter(Boolean);

        console.log(`📡 Iniciando busca em lote de ícones:`, {
          totalAtivos: ativosParaBuscar.length,
          ativoAtual: nomeAtivo,
          metodo
        });

        const icones = await fetchIconsForAtivos(ativosParaBuscar, auth, backendUrl, iconCacheRef.current, metodo);

        // Atualizar cache com todos os ícones recebidos
        Array.from(icones.entries()).forEach(([idAtivo, icon]) => {
          const ativoEncontrado = listaAtivo.find(a => a.IdAtivo === idAtivo);
          if (ativoEncontrado) {
            const chaveCache = `${ativoEncontrado.Ativo}_${metodo || 'default'}`;
            if (icon) {
              setAtivoIconsCache(prev => new Map(prev).set(chaveCache, icon));
            }
          }
        });

        // Retornar o ícone específico deste ativo
        const iconParaAtivo = icones.get(ativo.IdAtivo);
        if (iconParaAtivo) {
          console.log(`✨ Ícone personalizado obtido com sucesso para:`, {
            nomeAtivo,
            idAtivo: ativo.IdAtivo,
            metodo
          });
          return iconParaAtivo;
        } else {
          console.log(`⚠️ Nenhum ícone personalizado retornado para:`, {
            nomeAtivo,
            idAtivo: ativo.IdAtivo
          });
          return createDefaultAtivoIcon(metodo);
        }
      } else {
        console.warn(`❌ Ativo não encontrado na lista ou sem ID:`, {
          nomeAtivo,
          ativoEncontrado: !!ativo,
          temId: !!ativo?.IdAtivo
        });
      }
    } catch (error) {
      console.error(`❌ Erro ao buscar ícone personalizado:`, {
        nomeAtivo,
        erro: error
      });
    }

    // Retornar ícone padrão se não conseguir buscar personalizado
    console.log('🎨 Usando ícone padrão para beacon do ativo:', nomeAtivo, 'Método:', metodo);
    const defaultIcon = createDefaultAtivoIcon(metodo);
    const cacheKey = `${nomeAtivo}_${metodo || 'default'}`;
    setAtivoIconsCache(prev => new Map(prev).set(cacheKey, defaultIcon));
    return defaultIcon;
  };

  const obterListaAtivo = async (orgs?: string[]) => {
    try {
      const endpoint = orgs ? '/Gerenciamento/PowerBI/ObterAtivosByOrg' : '/Gerenciamento/PowerBI/ObterAtivos';

      const queryParams = new URLSearchParams();
      if (dataSetNamePBI) {
        queryParams.append('datasetName', dataSetNamePBI);
      }

      if (orgs) {
        orgs.forEach(org => {
          queryParams.append('organization', org);
        });
      }

      const queryString = queryParams.toString();
      const fullUrl = `${endpoint}?${queryString}`;

      const response = await httpClient.request({
        method: RequestMethod.GET,
        endpoint: fullUrl,
      });

      if (response.success && response.payload) {
        setListaAtivo(response.payload);
      }
    } catch (error) {
    }
  };

  useEffect(() => {
    // Sempre que selectedOrganizations mudar, atualiza a lista de ativos
    if (selectedOrganizations.length > 0 && selectedCorporation) {
      console.log('📋 Carregando lista de ativos para organizações:', {
        organizacoes: selectedOrganizations,
        corporacao: selectedCorporation,
        totalAtivosAtuais: listaAtivo.length
      });
      obterListaAtivo(selectedOrganizations);
    } else {
      console.log('📋 Limpando lista de ativos:', {
        motivo: !selectedOrganizations.length ? 'sem organizações' : 'sem corporação',
        organizacoes: selectedOrganizations,
        corporacao: selectedCorporation
      });
      setListaAtivo([]);
    }
  }, [selectedOrganizations, selectedCorporation]);

  // ❌ REMOVIDO - handleOrganizationChange agora é gerenciado pelo FiltroGlobal

  // ✅ MODIFICADO - fetchLocalData agora usa apenas filtros globais
  const fetchLocalData = async (filters: Partial<ILocaisPayloadRequest> = {}, page = 1, rows = 1000) => {
    try {
      // Se não houver organizações ou corporação selecionadas, não faz a requisição
      if (selectedOrganizations.length === 0 || !selectedCorporation) {
        console.log('Nenhuma organização ou corporação selecionada, não carregando dados de locais');
        setLocais([]);
        return [];
      }

      // Usar as organizações dos filtros globais
      const orgsToSend = selectedOrganizations;

      // Buscar o nome da corporação selecionada
      const corporacaoNome = selectedCorporation
        ? availableCorporations.find(corp => corp.id === selectedCorporation)?.name
        : undefined;

      const payload = {
        Corporacao: corporacaoNome,
        Organizacoes: orgsToSend,
        Locais: Array.isArray(filters.Locais) && filters.Locais.length > 0 ? filters.Locais : ["Todos"],
      };

      console.log('🔍 Buscando dados de locais com payload:', payload);
      console.log('🏢 Corporação selecionada:', selectedCorporation, '→', corporacaoNome);
      console.log('🌐 URL completa:', `${backendUrl}/api/Place/PlaceList?rows=${rows}&page=${page}`);

      const response = await fetch(`${backendUrl}/api/Place/PlaceList?rows=${rows}&page=${page}`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json-patch+json',
          'Authorization': `Bearer ${auth?.Token}`
        },
        body: JSON.stringify(payload)
      });

      console.log('📡 Resposta da API PlaceList:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Erro na API PlaceList:', response.status, errorText);
        throw new Error(`Erro HTTP ${response.status}: ${errorText}`);
      }

      const responseData = await response.json();
      console.log('📦 Dados brutos PlaceList recebidos:', responseData);

      if (!Array.isArray(responseData)) {
        throw new Error('Resposta não é um array');
      }

      const LocaisData = responseData.map(item => ({
        ...item,
      }));

      console.log('✅ Dados de locais recebidos:', LocaisData.length, 'locais carregados');
      setLocais(LocaisData);
      return LocaisData;

    } catch (error) {
      console.error("Erro na requisição:", error);
      setLocais([]);
      return [];
    }
  };

  // Função para buscar geometrias dos locais
  const fetchPlaceGeometries = async (idsPlaces: number[]) => {
    if (!idsPlaces || idsPlaces.length === 0 || !auth?.Token || !backendUrl) {
      console.log('❌ Parâmetros inválidos para buscar geometrias');
      return;
    }

    try {
      console.log('🔍 Buscando geometrias para locais:', idsPlaces);
      
      const response = await fetch(`${backendUrl}/api/PlaceGeometry/ByIdPlaces`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.Token}`
        },
        body: JSON.stringify(idsPlaces)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Erro ao buscar geometrias:', response.status, errorText);
        return;
      }

      const geometries = await response.json();
      console.log('✅ Geometrias recebidas:', geometries);

      if (Array.isArray(geometries)) {
        const geometriasMap: { [idLocal: number]: any[] } = {};
        
        geometries.forEach((geo: any) => {
          if (geo.IdPlace && geo.GeoLocation) {
            const feature = {
              type: 'Feature',
              geometry: geo.GeoLocation,
              properties: {
                id: geo.Id,
                idPlace: geo.IdPlace,
                name: geo.Name || ''
              }
            };
            if (!geometriasMap[geo.IdPlace]) {
              geometriasMap[geo.IdPlace] = [];
            }
            geometriasMap[geo.IdPlace].push(feature);
          }
        });

        setLocaisGeometrias(prev => ({ ...prev, ...geometriasMap }));
        const totalFeatures = Object.values(geometriasMap).reduce((sum, arr) => sum + arr.length, 0);
        console.log('✅ Geometrias processadas e armazenadas:', totalFeatures, 'perímetros em', Object.keys(geometriasMap).length, 'locais');
      }
    } catch (error) {
      console.error('❌ Erro ao buscar geometrias dos locais:', error);
    }
  };

  const fetchBeaconTrackingData = useCallback(async (filters: Partial<IExternalPayloadRequest> = {}) => {
    try {
      const orgsToSend = selectedOrganizations.length > 0 ? selectedOrganizations : [];
      const corporacaoNome = selectedCorporation
        ? availableCorporations.find(corp => corp.id === selectedCorporation)?.name
        : undefined;

      // Usar dtfinal do filtro se fornecido (auto-refresh), caso contrário usar selectedDate/selectedTime (subtrai 1 min para evitar erro na API)
      const dtfinalValue = filters.dtfinal || (selectedDate && selectedTime ? `${selectedDate} ${selectedTime}` : undefined);
      const dtfinalEnvio = dtfinalValue ? dtfinalMenosUmMinuto(dtfinalValue) : undefined;

      const payload = {
        Corporacao: corporacaoNome,
        Organizacao: orgsToSend,
        Ativos: Array.isArray(filters.Ativos) && filters.Ativos.length > 0 ? filters.Ativos : ["Todos"],
        Interferencia: filters.Interferencia || interferenceLevel,
        ...(dtfinalEnvio && { dtfinal: dtfinalEnvio }),
      };

      console.log('📤 [fetchBeaconTrackingData] Payload enviado:', JSON.stringify(payload, null, 2));

      const response = await fetch(`${backendUrl}/api/BeaconNred`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json-patch+json',
          'Authorization': `Bearer ${auth?.Token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        setAssetBeaconData([]);
        return [];
      }

      const responseData = await response.json();

      // 1. Tenta fazer o parse se for string
      let parsedData = responseData;
      if (typeof responseData === 'string') {
        try {
          parsedData = JSON.parse(responseData);
        } catch (e) {
          console.error("Erro ao parsear string JSON:", e);
        }
      }

      // 2. CORREÇÃO FUNDAMENTAL: Tratamento de Array Aninhado (Ex: [[dados], {metadata}])
      let dataToProcess = parsedData;
      if (Array.isArray(parsedData) && parsedData.length > 0 && Array.isArray(parsedData[0])) {
        console.log("📦 BeaconNred: Detectada estrutura de array aninhado. Extraindo índice 0.");
        dataToProcess = parsedData[0];
      }

      if (!Array.isArray(dataToProcess)) {
        console.warn("⚠️ BeaconNred: Dados processados não são um array:", dataToProcess);
        setAssetBeaconData([]);
        return [];
      }

      const processedData = dataToProcess.map((item: any) => ({
        ...item,
        Latitude: typeof item.Latitude === 'string' ? parseFloat(item.Latitude) : item.Latitude,
        Longitude: typeof item.Longitude === 'string' ? parseFloat(item.Longitude) : item.Longitude,
      }));

      console.log(`✅ BeaconNred (GPS) processado: ${processedData.length} registros.`);
      setAssetBeaconData(processedData as IAssetBeacon[]);
      return processedData as IAssetBeacon[];

    } catch (error) {
      console.error("❌ Erro em fetchBeaconTrackingData:", error);
      setAssetBeaconData([]);
      return [];
    }
  }, [selectedOrganizations, selectedAtivos, interferenceLevel, selectedCorporation, availableCorporations, selectedDate, selectedTime]);

  const fetchGatewayTrackingData = async (filters: Partial<IExternalPayloadRequest> = {}) => {
    try {
      const orgsToSend = selectedOrganizations.length > 0 ? selectedOrganizations : [];
      const corporacaoNome = selectedCorporation
        ? availableCorporations.find(corp => corp.id === selectedCorporation)?.name
        : undefined;

      // Usar dtfinal do filtro se fornecido (auto-refresh), caso contrário usar selectedDate/selectedTime (subtrai 1 min para evitar erro na API)
      const dtfinalValue = filters.dtfinal || (selectedDate && selectedTime ? `${selectedDate} ${selectedTime}` : undefined);
      const dtfinalEnvio = dtfinalValue ? dtfinalMenosUmMinuto(dtfinalValue) : undefined;

      const payload = {
        Corporacao: corporacaoNome,
        Organizacao: orgsToSend,
        Ativos: Array.isArray(filters.Ativos) && filters.Ativos.length > 0 ? filters.Ativos : ["Todos"],
        Interferencia: filters.Interferencia || interferenceLevel,
        ...(dtfinalEnvio && { dtfinal: dtfinalEnvio }),
      };

      console.log('📤 [fetchGatewayTrackingData] Payload enviado:', JSON.stringify(payload, null, 2));

      const response = await fetch(`${backendUrl}/api/GatewayNred`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json-patch+json',
          'Authorization': `Bearer ${auth?.Token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        setGateway([]);
        return [];
      }

      const responseData = await response.json();

      let parsedData = responseData;
      if (typeof responseData === 'string') {
        try {
          parsedData = JSON.parse(responseData);
        } catch (e) {
           console.error("Erro ao parsear string JSON Gateway:", e);
        }
      }

      // 2. CORREÇÃO DE SEGURANÇA: Verifica se também vem aninhado, por garantia
      let dataToProcess = parsedData;
      if (Array.isArray(parsedData) && parsedData.length > 0 && Array.isArray(parsedData[0])) {
         console.log("📦 GatewayNred: Detectada estrutura de array aninhado. Extraindo índice 0.");
         dataToProcess = parsedData[0];
      }

      if (!Array.isArray(dataToProcess)) {
        console.warn("⚠️ GatewayNred: Dados processados não são um array:", dataToProcess);
        setGateway([]);
        return [];
      }

      // Mapeamento normal
      const GatewayData = dataToProcess.map((item: any) => ({
        ...item,
        // Garantir números caso venham strings
        Latitude: typeof item.Latitude === 'string' ? parseFloat(item.Latitude) : item.Latitude,
        Longitude: typeof item.Longitude === 'string' ? parseFloat(item.Longitude) : item.Longitude,
      }));

      console.log(`✅ GatewayNred (G) processado: ${GatewayData.length} registros.`);
      setGateway(GatewayData);
      return GatewayData;

    } catch (error) {
      console.error("❌ Erro em fetchGatewayTrackingData:", error);
      setGateway([]);
      return [];
    }
  };
  const fetchLastSeenData = async (filters: Partial<IExternalPayloadRequest> = {}) => {
    try {
      // Usar apenas organizações dos filtros globais
      const orgsToSend = selectedOrganizations.length > 0 ? selectedOrganizations : [];

      // Buscar o nome da corporação selecionada
      const corporacaoNome = selectedCorporation
        ? availableCorporations.find(corp => corp.id === selectedCorporation)?.name
        : undefined;

      // Usar dtfinal do filtro se fornecido (auto-refresh), caso contrário usar selectedDate/selectedTime (subtrai 1 min para evitar erro na API)
      const dtfinalValue = filters.dtfinal || (selectedDate && selectedTime ? `${selectedDate} ${selectedTime}` : undefined);
      const dtfinalEnvio = dtfinalValue ? dtfinalMenosUmMinuto(dtfinalValue) : undefined;

      const payload = {
        Corporacao: corporacaoNome,
        Organizacao: orgsToSend,
        Ativos: Array.isArray(filters.Ativos) && filters.Ativos.length > 0 ? filters.Ativos : ["Todos"],
        Interferencia: filters.Interferencia || 3,
        ...(dtfinalEnvio && { dtfinal: dtfinalEnvio }),
      };

      const response = await fetch(`${backendUrl}/api/LastSeenNred`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json-patch+json',
          'Authorization': `Bearer ${auth?.Token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro HTTP ${response.status}: ${errorText}`);
      }

      const responseData = await response.json();

      if (!Array.isArray(responseData)) {
        throw new Error('Resposta não é um array');
      }

      const GatewayData = responseData.map(item => ({
        ...item,
      }));

      setGateway(GatewayData);
      return GatewayData;

    } catch (error) {
      console.error("Erro na requisição:", error);
      setGateway([]);
      return [];
    }
  };



  // ✅ NOVO - useEffect para monitorar mudanças nos filtros globais
  useEffect(() => {
    console.log('🔄 useEffect principal disparado - selectedOrganizations:', selectedOrganizations, 'isInitialized:', isInitialized);

    if (isInitialized && selectedOrganizations.length > 0 && selectedCorporation) {
      // Buscar dados dos gateways/locais
      fetchLocalData();

      // Buscar dados dos beacons apenas se houver ativos selecionados
      if (selectedAtivos.length > 0) {
        // ✅ OTIMIZAÇÃO: Verificar se todos os ativos estão selecionados
        const ativosParaAPI = obterAtivosParaAPI(selectedAtivos);
        
        // Se retornou ["Todos"], usar diretamente sem separação
        if (ativosParaAPI.length === 1 && ativosParaAPI[0] === "Todos") {
          console.log('🔄 Buscando dados de rastreamento para TODOS os ativos (otimizado)');
          
          // Chamada para Tracking B (GPS) com "Todos"
          fetchBeaconTrackingData({
            Ativos: ["Todos"],
            Interferencia: interferenceLevel,
            Organizacoes: selectedOrganizations,
          });
          
          // Chamada para Tracking G (Trilateração/Gateway) com "Todos"
          fetchGatewayTrackingData({
            Ativos: ["Todos"],
            Interferencia: interferenceLevel,
            Organizacoes: selectedOrganizations,
          });
        } else {
          console.log('🔄 Buscando dados de rastreamento para ativos selecionados:', selectedAtivos);

          // 🔹 SEPARAÇÃO DOS ATIVOS BASEADA NA ORIGEM (B ou G)
          const ativosParaGPS: string[] = [];
          const ativosParaGateway: string[] = [];

          selectedAtivos.forEach(nomeAtivo => {
            // Encontra o objeto completo na lista carregada anteriormente
            const ativoObj = listaAtivo.find(a => a.Ativo === nomeAtivo);
            
            if (ativoObj) {
              // Se for 'B', vai para API de GPS (BeaconNred)
              if (ativoObj.LastlatLongOrigin === 'B') {
                ativosParaGPS.push(nomeAtivo);
              } 
              // Se for 'G' (ou nulo/undefined, assumimos padrão), vai para API de Gateway (GatewayNred)
              else {
                ativosParaGateway.push(nomeAtivo);
              }
            }
          });

          console.log(`📊 Separação de Ativos: GPS(B): ${ativosParaGPS.length}, Gateway(G): ${ativosParaGateway.length}`);

          // 1. Chamada para Tracking B (GPS)
          if (ativosParaGPS.length > 0) {
            fetchBeaconTrackingData({
              Ativos: ativosParaGPS,
              Interferencia: interferenceLevel,
              Organizacoes: selectedOrganizations,
            });
          } else {
            setAssetBeaconData([]); // Limpa se não houver ativos GPS selecionados
          }

          // 2. Chamada para Tracking G (Trilateração/Gateway)
          if (ativosParaGateway.length > 0) {
            fetchGatewayTrackingData({
              Ativos: ativosParaGateway,
              Interferencia: interferenceLevel,
              Organizacoes: selectedOrganizations,
            });
          } else {
            setGateway([]); // Limpa se não houver ativos Gateway selecionados
          }
        }

      } else {
        console.log('🚫 Nenhum ativo selecionado - limpando dados de beacons');
        setAssetBeaconData([]);
        setGateway([]);
      }
    } else if (isInitialized) {
      console.log('Resetando dados - nenhuma organização selecionada');
      setLocais([]);
      setAssetBeaconData([]);
      setGateway([]);
    }
  }, [selectedOrganizations, selectedAtivos, listaAtivo, interferenceLevel, isInitialized, selectedCorporation, availableCorporations, selectedDate, selectedTime, obterAtivosParaAPI]);

  // ✅ MODIFICADO - Efeito para auto-refresh usando filtros globais e data incremental
  // - Intervalo de 10 segundos (seguindo padrão do Grafana)
  // - Atualiza gateways, beacons GPS e beacons trilaterados
  // - Incrementa a data/hora em 10 segundos a cada busca
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(async () => {
      if (selectedOrganizations.length > 0 && selectedCorporation && selectedAtivos.length > 0) {
        
        if (!selectedDate || !selectedTime) return;
        
        contadorRefreshRef.current += 1;
        
        // Validar se há incremento configurado
        if (!incrementoValor || incrementoValor <= 0) {
          console.warn('⚠️ Incremento não configurado, desativando auto-refresh');
          setAutoRefresh(false);
          return;
        }
        
        // Calcular nova data/hora usando o incremento configurável
        const dataInicial = `${selectedDate} ${selectedTime}`;
        
        // Incrementar pelo número de vezes que o refresh foi executado
        // Cada refresh adiciona o incremento configurado
        // Exemplo: se incremento = 10 segundos e contador = 3, adiciona 30 segundos à data inicial
        const valorTotal = contadorRefreshRef.current * incrementoValor;
        
        console.log(`🔄 Auto-refresh #${contadorRefreshRef.current}: Incrementando ${incrementoValor} ${incrementoUnidade} (total: +${valorTotal} ${incrementoUnidade} desde o início)`);
        console.log(`📅 Data inicial: ${dataInicial}`);
        
        // Adicionar o incremento total de uma vez
        const novaDataHora = incrementarDataHora(dataInicial, valorTotal, incrementoUnidade);
        if (!novaDataHora) {
          console.error('❌ Erro ao calcular nova data/hora, interrompendo auto-refresh');
          setAutoRefresh(false);
          return;
        }
        
        console.log(`✅ Nova data/hora calculada: ${novaDataHora} (será usada no dtfinal)`);
        setDataHoraAtual(novaDataHora);

        // ✅ OTIMIZAÇÃO: Verificar se todos os ativos estão selecionados
        const ativosParaAPI = obterAtivosParaAPI(selectedAtivos);
        
        // Preparar Promises apenas para o que é necessário
        const promises = [];

        // Se retornou ["Todos"], usar diretamente sem separação
        if (ativosParaAPI.length === 1 && ativosParaAPI[0] === "Todos") {
          console.log('🔄 Auto-refresh: Usando "Todos" para otimizar performance');
          
          // Fetch GPS com "Todos"
          promises.push(
            fetchBeaconTrackingData({
              Ativos: ["Todos"],
              Interferencia: interferenceLevel,
              Organizacoes: selectedOrganizations,
              dtfinal: novaDataHora
            })
          );
          
          // Fetch Gateway com "Todos"
          promises.push(
            fetchGatewayTrackingData({
              Ativos: ["Todos"],
              Interferencia: interferenceLevel,
              Organizacoes: selectedOrganizations,
              dtfinal: novaDataHora
            })
          );
        } else {
          // 🔹 SEPARAÇÃO DOS ATIVOS PARA O REFRESH
          const ativosParaGPS: string[] = [];
          const ativosParaGateway: string[] = [];

          selectedAtivos.forEach(nomeAtivo => {
            const ativoObj = listaAtivo.find(a => a.Ativo === nomeAtivo);
            if (ativoObj) {
              if (ativoObj.LastlatLongOrigin === 'B') ativosParaGPS.push(nomeAtivo);
              else ativosParaGateway.push(nomeAtivo);
            }
          });

          // Fetch GPS (apenas se houver ativos B)
          if (ativosParaGPS.length > 0) {
            promises.push(
              fetchBeaconTrackingData({
                Ativos: ativosParaGPS,
                Interferencia: interferenceLevel,
                Organizacoes: selectedOrganizations,
                dtfinal: novaDataHora
              })
            );
          } else {
            promises.push(Promise.resolve([])); // Placeholder
          }

          // Fetch Gateway (apenas se houver ativos G)
          if (ativosParaGateway.length > 0) {
            promises.push(
              fetchGatewayTrackingData({
                Ativos: ativosParaGateway,
                Interferencia: interferenceLevel,
                Organizacoes: selectedOrganizations,
                dtfinal: novaDataHora
              })
            );
          } else {
            promises.push(Promise.resolve([])); // Placeholder
          }
        }

        // Executar chamadas
        const [beaconsGPS, beaconsTrilaterados] = await Promise.all(promises);

        // --- LÓGICA DE ACUMULAÇÃO DO HISTÓRICO (Mantida igual, mas agora com dados corretos) ---
        
        // 1. Processar GPS (Tracking B)
        if (Array.isArray(beaconsGPS) && beaconsGPS.length > 0) {
          beaconsGPS.forEach(beacon => {
            if (beacon.Latitude && beacon.Longitude && beacon.Ativo) {
              adicionarPosicaoHistorico(
                beacon.Ativo, beacon.Latitude, beacon.Longitude, novaDataHora, 'gps'
              );
            }
          });
          
          setAssetBeaconData(prev => {
            const novos = beaconsGPS.filter(b => b.Latitude && b.Longitude);
            const beaconsComTimestamp = novos.map(b => ({
              ...b, _timestamp: novaDataHora, _uniqueKey: `${b.DeviceKey}_${novaDataHora}`
            }));
            
            // Mesclar evitando duplicatas
            const merged = [...prev];
            beaconsComTimestamp.forEach(newBeacon => {
               // Lógica de dedup...
               const exists = merged.some(b => b.DeviceKey === newBeacon.DeviceKey && (b as any)._timestamp === newBeacon._timestamp);
               if(!exists) merged.push(newBeacon);
            });
            return merged;
          });
        }

        // 2. Processar Trilateração (Tracking G)
        if (Array.isArray(beaconsTrilaterados) && beaconsTrilaterados.length > 0) {
          beaconsTrilaterados.forEach(beacon => {
            let lat, lng;
            const metodo = beacon.Metodo ? beacon.Metodo.toLowerCase() : 'trilateracao';
            // Lógica de coordenadas aleatórias vs reais
            if (metodo === 'estimado' || metodo === 'centroide') {
              lat = beacon.LatitudeAleatoria || beacon.Latitude;
              lng = beacon.LongitudeAleatoria || beacon.Longitude;
            } else {
              lat = beacon.Latitude;
              lng = beacon.Longitude;
            }

            if (lat && lng && beacon.Ativo) {
              adicionarPosicaoHistorico(
                beacon.Ativo, lat, lng, novaDataHora, metodo
              );
            }
          });

          setGateway(prev => {
             // Mesma lógica de merge do GPS aplicada ao Gateway
             const merged: any[] = Array.isArray(prev) ? [...prev] : [];
             // ... lógica de atualização do estado setGateway
             const novos = beaconsTrilaterados.filter(b => b.Ativo); // filtro básico
             const mapped = novos.map(b => ({...b, _timestamp: novaDataHora, _uniqueKey: `${b.DeviceKey}_${novaDataHora}`}));
             
             mapped.forEach(newBeacon => {
                 const exists = merged.some((b: any) => b.DeviceKey === newBeacon.DeviceKey && b._timestamp === newBeacon._timestamp);
                 if (!exists) merged.push(newBeacon);
             });
             return merged;
          });
        }
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [autoRefresh, selectedOrganizations, selectedAtivos, listaAtivo, interferenceLevel, selectedCorporation, availableCorporations, selectedDate, selectedTime, incrementoValor, incrementoUnidade, obterAtivosParaAPI]);


  // ❌ REMOVIDO - useEffect duplicado, agora gerenciado pelo useEffect principal

  // ✅ MODIFICADO - Efeito para selecionar automaticamente todos os gateways quando os dados dos locais mudarem
  useEffect(() => {
    if (Locais.length > 0 && selectedOrganizations.length > 0 && selectedCorporation) {
      // Se há organizações selecionadas e dados carregados, selecionar todos automaticamente
      const todosLocais = Array.from(
        new Set(Locais.map(item => item.Local).filter(Boolean))
      );
      const todosGateways = Locais.map(item => item.Gateway);

      // Sempre seleciona todos quando os dados mudam
      setSelectedLocais(todosLocais);
      setSelectedLocal(todosGateways);
      console.log("Auto-selecionando todos os gateways após carregamento dos dados:", todosGateways.length);
    }
  }, [Locais, selectedOrganizations, selectedCorporation]);

  // Efeito para buscar geometrias quando os locais forem carregados
  useEffect(() => {
    if (Locais.length > 0 && auth?.Token && backendUrl) {
      // Extrair IDs únicos dos locais usando IdPlace
      const idsPlaces = Locais
        .map(local => local.IdPlace)
        .filter((id): id is number => typeof id === 'number' && !isNaN(id));

      // Remover IDs duplicados
      const idsUnicos = Array.from(new Set(idsPlaces));

      // Filtrar apenas IDs que ainda não foram buscados usando uma referência estável
      const idsParaBuscar = idsUnicos.filter(id => {
        // Usar callback para acessar estado atual sem causar loop
        return !locaisGeometrias[id];
      });

      if (idsParaBuscar.length > 0) {
        console.log('🔍 Buscando geometrias para locais:', idsParaBuscar);
        fetchPlaceGeometries(idsParaBuscar);
      } else {
        console.log('✅ Todas as geometrias já estão carregadas');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Locais, auth?.Token, backendUrl]);


  // Efeito para atualizar lista de ativos únicos
  useEffect(() => {
    setAtivosUnicos(Array.from(new Set(listaAtivo.map(a => a.Ativo))).filter(Boolean));
  }, [listaAtivo]);

  // Efeito para pré-carregar ícones de gateways quando os dados forem carregados
  useEffect(() => {
    console.log('🔔 [DEBUG GATEWAY ICONS] useEffect disparado!', {
      temLocais: Locais.length > 0,
      temSelecionados: selectedLocal.length > 0,
      temAuth: !!auth,
      temBackendUrl: !!backendUrl
    });

    const carregarIconesGateways = async () => {
      console.log('🔍 [DEBUG] Verificando dados de gateways:', {
        totalLocais: Locais.length,
        totalSelecionados: selectedLocal.length,
        primeirosLocais: Locais.slice(0, 3).map(l => ({
          Gateway: l.Gateway,
          IdGateway: l.IdGateway,
          Status: l.Status,
          temIdGateway: !!l.IdGateway
        }))
      });

      if (!Locais.length) {
        console.log('📋 Sem gateways para carregar ícones');
        return;
      }

      // Filtrar apenas gateways visíveis e com IdGateway
      const gatewaysVisiveis = Locais.filter(local => 
        selectedLocal.includes(local.Gateway) && 
        local.IdGateway
      );

      console.log('🔍 [DEBUG] Filtragem de gateways:', {
        totalLocais: Locais.length,
        totalSelecionados: selectedLocal.length,
        gatewaysComId: Locais.filter(l => l.IdGateway).length,
        gatewaysVisiveis: gatewaysVisiveis.length
      });

      if (!gatewaysVisiveis.length) {
        console.log('📋 Nenhum gateway visível com IdGateway');
        console.log('🔍 [DEBUG] Detalhes:', {
          algunsLocais: Locais.slice(0, 5).map(l => ({
            gateway: l.Gateway,
            idGateway: l.IdGateway,
            selecionado: selectedLocal.includes(l.Gateway)
          }))
        });
        return;
      }

      console.log('🔄 Iniciando pré-carregamento de ícones de gateways:', {
        totalGateways: gatewaysVisiveis.length,
        gateways: gatewaysVisiveis.map(g => ({ id: g.IdGateway, nome: g.Gateway, status: g.Status }))
      });

      // Mapear IDs dos gateways que ainda não estão no cache
      const idsParaBuscar = gatewaysVisiveis
        .map(local => local.IdGateway!)
        .filter(id => !gatewayIconsCache.has(id));

      console.log('🔍 [DEBUG] Status do cache:', {
        totalNoCache: gatewayIconsCache.size,
        idsNoCache: Array.from(gatewayIconsCache.keys()),
        idsParaBuscar: idsParaBuscar.length
      });

      if (!idsParaBuscar.length) {
        console.log('✨ Todos os ícones de gateways já estão no cache');
        return;
      }

      console.log('🎯 Buscando ícones para IDs de gateways:', idsParaBuscar);

      try {
        const icones = await fetchIconsForGateways(
          idsParaBuscar,
          auth,
          backendUrl,
          gatewayIconCacheRef.current
        );

        console.log('🔍 [DEBUG] Resultado da busca de ícones:', {
          totalRetornado: icones.size,
          ids: Array.from(icones.keys()),
          temIcones: Array.from(icones.entries()).map(([id, icon]) => ({
            id,
            temIcone: !!icon,
            tipo: icon?.options?.className || 'desconhecido'
          }))
        });

        // Atualizar cache de ícones no estado
        const novoCache = new Map(gatewayIconsCache);
        icones.forEach((icon, idGateway) => {
          if (icon) {
            novoCache.set(idGateway, icon);
            console.log(`✅ [DEBUG] Ícone adicionado ao cache para gateway ID ${idGateway}`);
          } else {
            console.log(`⚠️ [DEBUG] Ícone null/undefined para gateway ID ${idGateway}`);
          }
        });

        setGatewayIconsCache(novoCache);
        console.log('✨ Ícones de gateways carregados e cache atualizado:', {
          total: icones.size,
          ids: Array.from(icones.keys()),
          tamanhoCacheNovo: novoCache.size
        });
      } catch (error) {
        console.error('❌ Erro ao carregar ícones de gateways:', error);
      }
    };

    carregarIconesGateways();
  }, [Locais, selectedLocal, auth, backendUrl]);

  // Criar/atualizar markers de gateways no efeito (nunca durante o render, para evitar loop infinito)
  useEffect(() => {
    if (!showGateways) {
      setGatewayMarkers({});
      return;
    }
    const getGatewayIcon = (local: ILocaisModel): L.Icon | L.DivIcon => {
      const isOnline = local.Status === "online";
      if (local.IdGateway && gatewayIconsCache.has(local.IdGateway)) {
        const customIcon = gatewayIconsCache.get(local.IdGateway);
        if (customIcon) return customIcon;
      }
      return createDefaultGatewayIcon(getOrganizationColor(local.Organizacao), isOnline);
    };

    const newGatewayMarkers: { [key: string]: L.Marker } = {};
    Locais
      .filter((local) =>
        typeof local.Latitude === 'number' &&
        typeof local.Longitude === 'number' &&
        selectedLocal.includes(local.Gateway)
      )
      .forEach((local) => {
        const latlong = [local.Latitude!, local.Longitude!] as [number, number];
        const IdGateway = `G_${local.IdGateway || local.Gateway}`;
        const popupcontent = renderPopupGateway(local, popupGatewayConfig);
        const popup = L.popup({ offset: [0, -10] }).setContent(popupcontent);
        const marker = L.marker(latlong, { icon: getGatewayIcon(local) }).bindPopup(popup);
        (marker as any).gatewayData = local;
        newGatewayMarkers[IdGateway] = marker;
      });

    setGatewayMarkers(newGatewayMarkers);
  }, [showGateways, Locais, selectedLocal, gatewayIconsCache, popupGatewayConfig, getOrganizationColor]);

  // Efeito para pré-carregar ícones quando ativos são selecionados/removidos
  useEffect(() => {
    const carregarIcones = async () => {
      if (!selectedAtivos.length || !listaAtivo.length) {
        return;
      }

      // Mapear nomes dos ativos para IDs
      const idsParaBuscar = listaAtivo
        .filter(ativo => selectedAtivos.includes(ativo.Ativo) && ativo.IdAtivo)
        .map(ativo => ativo.IdAtivo!)
        .filter(Boolean);

      if (!idsParaBuscar.length) {
        return;
      }

      try {
        // Buscar ícones para GPS e Trilateração
        const [iconesGPS, iconesTrilateracao] = await Promise.all([
          fetchIconsForAtivos(idsParaBuscar, auth, backendUrl, iconCacheRef.current, 'gps'),
          fetchIconsForAtivos(idsParaBuscar, auth, backendUrl, iconCacheRef.current, 'trilateracao')
        ]);

        // Atualizar cache para ambos os métodos
        for (const ativo of listaAtivo) {
          if (selectedAtivos.includes(ativo.Ativo) && ativo.IdAtivo) {
            const iconGPS = iconesGPS.get(ativo.IdAtivo);
            const iconTrilateracao = iconesTrilateracao.get(ativo.IdAtivo);

            if (iconGPS) {
              setAtivoIconsCache(prev => new Map(prev).set(`${ativo.Ativo}_gps`, iconGPS));
            }
            if (iconTrilateracao) {
              setAtivoIconsCache(prev => new Map(prev).set(`${ativo.Ativo}_trilateracao`, iconTrilateracao));
            }
          }
        }
      } catch (error) {
        console.error('❌ Erro ao carregar ícones:', error);
      }
    };

    carregarIcones();
  }, [selectedAtivos, listaAtivo, auth, backendUrl]);

  useEffect(() => {
    if (Locais.length > 0 && selectedLocal.length > 0) {
      const filtered = Locais.filter((local) =>
        selectedLocal.includes(local.Gateway)
      );
      setFilteredData(filtered);

      if (mapRef.current && filtered.length > 0) {
        const validCoordinates = filtered.filter(
          (g) => g.Latitude !== null && g.Longitude !== null
        );

        if (validCoordinates.length > 0) {
          const newBounds = L.latLngBounds(
            validCoordinates.map((g) => [g.Latitude!, g.Longitude!])
          );
          mapRef.current.fitBounds(newBounds);
        }
      }
    } else {
      setFilteredData(Locais);
    }
  }, [Locais, selectedLocal]);

  // Efeito para buscar ícones quando a seleção de ativos muda
  useEffect(() => {
    const buscarIcones = async () => {
      if (!selectedAtivos.length) {
        return;
      }

      // Buscar IDs dos ativos selecionados
      const ativosParaBuscar = listaAtivo
        .filter(a => a.IdAtivo && selectedAtivos.includes(a.Ativo))
        .map(a => a.IdAtivo!)
        .filter(Boolean);

      if (ativosParaBuscar.length > 0) {
        try {
          // Buscar ícones para GPS e Trilateração em paralelo
          const [iconesGPS, iconesTrilateracao] = await Promise.all([
            fetchIconsForAtivos(ativosParaBuscar, auth, backendUrl, iconCacheRef.current, 'gps'),
            fetchIconsForAtivos(ativosParaBuscar, auth, backendUrl, iconCacheRef.current, 'trilateracao')
          ]);

          // Atualizar cache para ambos os métodos
          selectedAtivos.forEach(ativoNome => {
            const ativo = listaAtivo.find(a => a.Ativo === ativoNome);
            if (ativo?.IdAtivo) {
              const iconGPS = iconesGPS.get(ativo.IdAtivo);
              const iconTrilateracao = iconesTrilateracao.get(ativo.IdAtivo);

              if (iconGPS) {
                setAtivoIconsCache(prev => new Map(prev).set(`${ativoNome}_gps`, iconGPS));
              }
              if (iconTrilateracao) {
                setAtivoIconsCache(prev => new Map(prev).set(`${ativoNome}_trilateracao`, iconTrilateracao));
              }
            }
          });
        } catch (error) {
          console.error('❌ Erro ao buscar ícones:', error);
        }
      }
    };

    buscarIcones();
  }, [selectedAtivos, listaAtivo, auth, backendUrl]);

  // Efeito para ajustar o mapa quando ativos são selecionados
  useEffect(() => {
    if (mapRef.current && (assetBeaconData.length > 0 || gateway.length > 0)) {
      let allBeacons: any[] = [];

      // Adicionar beacons GPS
      let beaconsToShow = assetBeaconData;
      if (selectedAtivos.length > 0) {
        beaconsToShow = assetBeaconData.filter(beacon =>
          selectedAtivos.includes(beacon.Ativo)
        );
      }
      allBeacons = [...allBeacons, ...beaconsToShow];

      // Adicionar beacons trilaterados por gateway
      let gatewayTrackingData: any[] = [];
      try {
        if (typeof gateway === 'string') {
          gatewayTrackingData = JSON.parse(gateway);
        } else if (Array.isArray(gateway)) {
          gatewayTrackingData = gateway;
        }

        if (selectedAtivos.length > 0) {
          gatewayTrackingData = gatewayTrackingData.filter(beacon =>
            selectedAtivos.includes(beacon.Ativo)
          );
        }
        allBeacons = [...allBeacons, ...gatewayTrackingData];
      } catch (error) {
        console.error("Erro ao processar dados do gateway para ajustar mapa:", error);
      }

      // Filtra beacons com coordenadas válidas
      const validBeacons = allBeacons.filter(beacon =>
        typeof beacon.Latitude === 'number' &&
        typeof beacon.Longitude === 'number' &&
        !isNaN(beacon.Latitude) &&
        !isNaN(beacon.Longitude) &&
        beacon.Latitude !== 0 &&
        beacon.Longitude !== 0
      );

      if (validBeacons.length > 0) {
        const bounds = L.latLngBounds(
          validBeacons.map(beacon => [beacon.Latitude, beacon.Longitude])
        );

        // Adiciona um pequeno padding ao bounds para melhor visualização
        mapRef.current.fitBounds(bounds, { padding: [20, 20] });
      }
    }
  }, [selectedAtivos, assetBeaconData, gateway]);

  const handleLocalFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setLocalFilter(value);

    if (value) {
      const filtered = Locais.filter((local) =>
        local.Local.toLowerCase().includes(value.toLowerCase()) ||
        local.Gateway.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(Locais);
    }
  };

  const bounds =
    filteredData.length > 0
      ? L.latLngBounds(
        filteredData
          .filter((g) => g.Latitude !== null && g.Longitude !== null)
          .map((g) => [g.Latitude!, g.Longitude!])
      )
      : null;



  // ✅ Estado de loading enquanto filtros inicializam
  if (!isInitialized) {
    return (
      <main style={{ height: "100vh", width: "100%", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "20px", textAlign: "center" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
            <i className="ui loading spinner icon"></i>
            <span>Carregando filtros...</span>
          </div>
        </div>
      </main>
    );
  }

  // ✅ ORDEM DE CARGA: só exibir mapa/colunas após carregar e aplicar o cache (popups + colunas)
  if (!cacheConfigLoaded) {
    return (
      <main style={{ height: "100vh", width: "100%", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "20px", textAlign: "center" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
            <i className="ui loading spinner icon"></i>
            <span>Carregando configuração (cache)...</span>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main style={{ height: "100vh", width: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <style>{`
        @media (max-width: 768px) {
          .locais-filtros-container {
            flex-direction: column !important;
            gap: 15px !important;
          }
          .locais-filtros-container > div {
            width: 100% !important;
            max-width: 100% !important;
          }
          .locais-sidebar {
            width: 100% !important;
            max-height: 250px !important;
            border-right: none !important;
            border-bottom: 2px solid #ccc !important;
          }
          .locais-map-container {
            flex-direction: column !important;
          }
          .locais-sidebar ul {
            height: 150px !important;
            max-height: 150px !important;
          }
          .locais-timeline-container {
            padding: 12px !important;
          }
          .locais-timeline-controls {
            flex-wrap: wrap !important;
            gap: 8px !important;
          }
          .locais-timeline-controls button {
            padding: 6px 10px !important;
            font-size: 12px !important;
            min-width: auto !important;
          }
          .locais-timeline-info {
            flex-direction: column !important;
            gap: 8px !important;
          }
          .locais-ativo-filter {
            flex-direction: column !important;
            max-width: 100% !important;
          }
          .locais-ativo-filter > div {
            max-width: 100% !important;
            width: 100% !important;
          }
        }
        
        /* Estilos para scrollbar do react-select no filtro de ativos */
        .locais-ativo-filter .react-select__value-container {
          max-height: 72px !important;
          overflow-y: auto !important;
          overflow-x: hidden !important;
        }
        
        .locais-ativo-filter .react-select__value-container::-webkit-scrollbar {
          width: 6px;
        }
        
        .locais-ativo-filter .react-select__value-container::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 3px;
        }
        
        .locais-ativo-filter .react-select__value-container::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 3px;
        }
        
        .locais-ativo-filter .react-select__value-container::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        
        /* Firefox */
        .locais-ativo-filter .react-select__value-container {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e0 #f1f5f9;
        }
      `}</style>
      <div style={{ padding: "10px", borderBottom: "1px solid #ccc" }}>
        <div className="locais-filtros-container" style={{ display: "flex", gap: "10px", alignItems: "flex-end" }}>
          {/* ✅ NOVO - Filtros globais */}
          <FiltroGlobal
            onCorporationChange={handleFiltroChange}
            onOrganizationChange={handleFiltroChange}
            style={{
              corporation: { width: "300px" },
              organization: { width: "300px" }
            }}
          />
          <AtivoFilter
            availableAtivos={listaAtivo}
            selectedAtivos={selectedAtivos}
            onAtivoChange={setSelectedAtivos}
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            selectedTime={selectedTime}
            onTimeChange={setSelectedTime}
            autoRefresh={autoRefresh}
            onAutoRefreshToggle={handleAutoRefreshToggle}
            incrementoValor={incrementoValor}
            incrementoUnidade={incrementoUnidade}
            onIncrementoValorChange={setIncrementoValor}
            onIncrementoUnidadeChange={setIncrementoUnidade}
          />
          {/* Card Resumo de Associação (apenas quando corporação = VWCO) */}
          {isVwco && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                padding: "10px 14px",
                minWidth: "160px",
                border: "1px solid #dee2e6",
                borderRadius: "6px",
                backgroundColor: "var(--bs-body-bg)",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              }}
            >
              <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--bs-secondary-color)", marginBottom: "6px" }}>
                Resumo de associação
              </div>
              <div style={{ display: "flex", gap: "12px", fontSize: "0.85rem" }}>
                <span>Disponível: <strong>{resumoAssociacaoTotais.disponivel}</strong></span>
                <span>Associado: <strong>{resumoAssociacaoTotais.associado}</strong></span>
              </div>
            </div>
          )}
        </div>
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
        {/* Conteúdo do Mapa - sempre visível */}
          <div className="locais-map-container" style={{ display: "flex", flex: 1, gap: sidebarExpanded ? "10px" : "0", minHeight: 0, overflow: "hidden", height: "100%", position: "relative" }}>
            {/* Botão Toggle - Posicionado na borda entre sidebar e mapa */}
            <button
              onClick={() => setSidebarExpanded(!sidebarExpanded)}
              style={{
                position: "absolute",
                top: "50%",
                left: sidebarExpanded ? "calc(15% - 16px)" : "0px",
                transform: "translateY(-50%)",
                zIndex: 1001,
                backgroundColor: "#007bff",
                color: "white",
                border: "2px solid #fff",
                borderRadius: sidebarExpanded ? "50% 0 0 50%" : "0 50% 50% 0",
                padding: "0",
                cursor: "pointer",
                fontSize: "16px",
                fontWeight: "bold",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                transition: "left 0.3s ease, background-color 0.2s ease, border-radius 0.3s ease, transform 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "64px",
                lineHeight: "1"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#0056b3";
                e.currentTarget.style.transform = "translateY(-50%) scale(1.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#007bff";
                e.currentTarget.style.transform = "translateY(-50%) scale(1)";
              }}
              title={sidebarExpanded ? "Recolher sidebar" : "Expandir sidebar"}
            >
              {sidebarExpanded ? "◄" : "►"}
            </button>
            
            <div
              className="locais-sidebar sidebar"
              style={{
                width: sidebarExpanded ? "15%" : "0",
                minWidth: sidebarExpanded ? "200px" : "0",
                borderRight: sidebarExpanded ? "1px solid #ccc" : "none",
                borderBottom: sidebarExpanded ? "1px solid #ccc" : "none",
                padding: "0",
                overflow: "hidden",
                backgroundColor: "#fff",
                borderRadius: "5px",
                boxShadow: sidebarExpanded ? "0 4px 8px rgba(0, 0, 0, 0.1)" : "none",
                display: sidebarExpanded ? "flex" : "none",
                flexDirection: "column",
                height: "100%",
                flexShrink: 0,
                transition: "width 0.3s ease, min-width 0.3s ease, opacity 0.3s ease",
                opacity: sidebarExpanded ? 1 : 0,
              }}
            >
              {/* Menu Hambúrguer e Carrossel */}
              <div style={{ 
                display: "flex", 
                borderBottom: "1px solid #ccc",
                backgroundColor: "#f8f9fa"
              }}>
                <button
                  onClick={() => setSidebarActiveTab('locais')}
                  style={{
                    flex: 1,
                    padding: "10px",
                    border: "none",
                    backgroundColor: sidebarActiveTab === 'locais' ? "#007bff" : "transparent",
                    color: sidebarActiveTab === 'locais' ? "white" : "#333",
                    cursor: "pointer",
                    fontWeight: sidebarActiveTab === 'locais' ? "bold" : "normal",
                    transition: "all 0.3s ease",
                    borderRight: "1px solid #ccc",
                  }}
                  title="Locais"
                >
                  📍 Locais
                </button>
                <button
                  onClick={() => setSidebarActiveTab('timeline')}
                  style={{
                    flex: 1,
                    padding: "10px",
                    border: "none",
                    backgroundColor: sidebarActiveTab === 'timeline' ? "#007bff" : "transparent",
                    color: sidebarActiveTab === 'timeline' ? "white" : "#333",
                    cursor: "pointer",
                    fontWeight: sidebarActiveTab === 'timeline' ? "bold" : "normal",
                    transition: "all 0.3s ease",
                  }}
                  title="Linha do Tempo"
                >
                  ⏱️ Timeline
                </button>
              </div>

              {/* Conteúdo do Carrossel */}
              <div style={{ 
                flex: 1, 
                overflowY: "auto",
                overflowX: "hidden",
                padding: "10px",
                minHeight: 0,
              }}>
                {/* Aba de Locais */}
                {sidebarActiveTab === 'locais' && (
                  <div>
                    <div style={{ marginBottom: "10px", textAlign: "center" }}>
                      <h4 style={{ marginBottom: "10px", textAlign: "left" }}>Lista de Locais</h4>
                      <input
                        type="text"
                        placeholder="Filtrar por local"
                        value={localFilter}
                        onChange={handleLocalFilter}
                        style={{
                          width: "100%",
                          padding: "5px",
                          borderRadius: "5px",
                          border: "1px solid var(--bs-border-color)",
                          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                          backgroundColor: "var(--bs-body-bg)",
                          color: "var(--bs-body-color)",
                        }}
                      />
                      <button
                        onClick={toggleSelectAll}
                        style={{
                          marginTop: "10px",
                          padding: "5px 10px",
                          borderRadius: "5px",
                          border: "1px solid #ccc",
                          backgroundColor: "#f0f0f0",
                          cursor: "pointer",
                        }}
                      >
                        {locaisUnicos.length > 0 && locaisUnicos.every(local => selectedLocais.includes(local)) ? "Desmarcar Todos" : "Marcar Todos"}
                      </button>
                    </div>
                    <ul className="sidebar" style={{ height: "64vh", listStyleType: "none", padding: 0, overflowY: "auto" }}>
                      {locaisUnicos
                        .filter(localName =>
                          localFilter === "" ||
                          localName.toLowerCase().includes(localFilter.toLowerCase())
                        )
                        .map((localName: string) => {
                          const gatewaysDoLocal = filteredData.filter(item => item.Local === localName);
                          const quantidadeGateways = gatewaysDoLocal.length;
                          const quantidadeAtivos = quantidadeAtivosPorLocal[localName] ?? 0;

                          return (
                            <li
                              key={localName}
                              style={{
                                padding: "10px",
                                marginBottom: "8px",
                                border: "1px solid #ddd",
                                borderRadius: "5px",
                                backgroundColor: selectedLocais.includes(localName) ? "var(--bs-secondary-bg)" : "var(--bs-body-bg)",
                                color: "var(--bs-body-color)",
                                fontWeight: selectedLocais.includes(localName) ? "bold" : "normal",
                                cursor: "pointer",
                                transition: "background-color 0.3s ease, color 0.3s ease",
                                textAlign: "center",
                              }}
                              onClick={() => handleLocalSelection(localName)}
                            >
                              <div>{localName}</div>
                              <small style={{ color: "var(--bs-secondary-color)", fontSize: "0.8em" }}>
                                {isVwco ? (
                                  <>{quantidadeAtivos} Prisma{quantidadeAtivos !== 1 ? "s" : ""}</>
                                ) : (
                                  <>
                                    {quantidadeAtivos} ativo{quantidadeAtivos !== 1 ? "s" : ""}
                                    {quantidadeGateways > 0 && ` · ${quantidadeGateways} gateway${quantidadeGateways !== 1 ? "s" : ""}`}
                                  </>
                                )}
                              </small>
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                )}

                {/* Aba de Timeline */}
                {sidebarActiveTab === 'timeline' && (() => {
                  // Detectar tema atual
                  const isDarkTheme = document.documentElement.getAttribute('data-bs-theme') === 'dark';
                  
                  // Estilos baseados no tema
                  const cardStyle: React.CSSProperties = {
                    marginBottom: "10px",
                    padding: "10px",
                    backgroundColor: isDarkTheme ? '#1f242d' : "#f8f9fa",
                    borderRadius: "5px",
                    border: `1px solid ${isDarkTheme ? '#2f3542' : '#dee2e6'}`
                  };
                  
                  const labelStyle: React.CSSProperties = {
                    display: "block",
                    marginBottom: "6px",
                    fontSize: "12px",
                    fontWeight: "bold",
                    color: isDarkTheme ? '#f7f9ff' : '#000000'
                  };
                  
                  const inputStyle = (hasError: boolean): React.CSSProperties => ({
                    flex: 1,
                    padding: "6px",
                    borderRadius: "4px",
                    border: `2px solid ${hasError ? '#ff4444' : (isDarkTheme ? '#394052' : '#ccc')}`,
                    fontSize: "11px",
                    backgroundColor: hasError 
                      ? (isDarkTheme ? '#4a1f26' : '#fff5f5')
                      : (isDarkTheme ? '#151922' : 'white'),
                    color: isDarkTheme ? '#f7f9ff' : '#000000'
                  });
                  
                  const errorTextStyle: React.CSSProperties = {
                    color: '#ff4444',
                    fontSize: '9px',
                    display: 'block'
                  };
                  
                  return (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      height: '100%',
                    }}>
                      {/* Controles de Data e Hora Final */}
                      <div style={cardStyle}>
                        <label style={labelStyle}>
                          Data e hora inicial na linha do tempo {!selectedDate && <span style={{ color: 'red' }}>*</span>}
                        </label>
                        <div style={{ display: "flex", gap: "5px", marginBottom: "6px" }}>
                          <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            style={inputStyle(!selectedDate)}
                            placeholder="Selecione uma data"
                            title="Data é obrigatória para Auto-Refresh"
                          />
                          <input
                            type="time"
                            step="1"
                            value={selectedTime}
                            onChange={(e) => setSelectedTime(e.target.value)}
                            style={inputStyle(!selectedTime)}
                            placeholder="Selecione uma hora"
                            title="Hora é obrigatória para Auto-Refresh"
                          />
                        </div>
                        {(!selectedDate || !selectedTime) && (
                          <small style={errorTextStyle}>
                            ⚠️ Data e hora são obrigatórias para Auto-Refresh
                          </small>
                        )}
                      </div>

                      {/* Controles de Incremento e Auto Refresh */}
                      <div style={cardStyle}>
                        <h5 style={{ 
                          marginBottom: "8px", 
                          fontSize: "13px", 
                          fontWeight: "bold",
                          color: isDarkTheme ? '#f7f9ff' : '#000000'
                        }}>
                          Incremento {autoRefresh && (!incrementoValor || incrementoValor <= 0) && <span style={{ color: 'red' }}>*</span>}
                        </h5>
                        <div style={{ display: "flex", gap: "5px", marginBottom: "8px" }}>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={incrementoValor || ""}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 0;
                              setIncrementoValor(Math.max(1, value));
                            }}
                            placeholder="Ex: 10"
                            style={inputStyle(autoRefresh && (!incrementoValor || incrementoValor <= 0))}
                            title="Valor do incremento a ser adicionado ao dtfinal a cada refresh (a cada 10 segundos)"
                          />
                          <select
                            value={incrementoUnidade}
                            onChange={(e) => setIncrementoUnidade(e.target.value as 'segundos' | 'minutos' | 'horas')}
                            style={{
                              ...inputStyle(autoRefresh && (!incrementoValor || incrementoValor <= 0)),
                              cursor: "pointer"
                            }}
                            title="Unidade do incremento"
                          >
                            <option value="segundos">Seg</option>
                            <option value="minutos">Min</option>
                            <option value="horas">Hor</option>
                          </select>
                        </div>
                        {autoRefresh && (!incrementoValor || incrementoValor <= 0) && (
                          <small style={{ ...errorTextStyle, marginBottom: '8px' }}>
                            ⚠️ Incremento obrigatório
                          </small>
                        )}
                        
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
                          <label style={{ 
                            fontSize: "11px", 
                            fontWeight: "bold", 
                            flex: 1,
                            color: isDarkTheme ? '#f7f9ff' : '#000000'
                          }}>
                            Auto Refresh
                          </label>
                          {autoRefresh && incrementoValor > 0 && (
                            <i 
                              className="ui icon info circle" 
                              style={{ 
                                color: isDarkTheme ? '#23c067' : '#28a745', 
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                              title={`✓ Incrementando ${incrementoValor} ${incrementoUnidade === 'segundos' ? 'seg' : incrementoUnidade === 'minutos' ? 'min' : 'hor'} a cada 10s`}
                            />
                          )}
                          <button
                            onClick={handleAutoRefreshToggle}
                            style={{
                              padding: "6px 12px",
                              borderRadius: "4px",
                              border: "2px solid",
                              borderColor: autoRefresh 
                                ? (isDarkTheme ? '#23c067' : "#28a745") 
                                : "#dc3545",
                              backgroundColor: autoRefresh 
                                ? (isDarkTheme ? '#23c067' : "#28a745") 
                                : "#dc3545",
                              color: "white",
                              cursor: "pointer",
                              fontSize: "11px",
                              fontWeight: "bold",
                              minWidth: "60px",
                              transition: "all 0.3s ease"
                            }}
                            title={autoRefresh && incrementoValor > 0 
                              ? `✓ Incrementando ${incrementoValor} ${incrementoUnidade === 'segundos' ? 'seg' : incrementoUnidade === 'minutos' ? 'min' : 'hor'} a cada 10s`
                              : undefined
                            }
                          >
                            {autoRefresh ? "ON" : "OFF"}
                          </button>
                        </div>
                      </div>

                      {/* Botão Limpar Mapa */}
                      <div style={{ 
                        marginBottom: "10px"
                      }}>
                        <button
                          onClick={limparMapa}
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            borderRadius: "4px",
                            border: "2px solid #dc3545",
                            backgroundColor: "#dc3545",
                            color: "white",
                            cursor: "pointer",
                            fontSize: "12px",
                            fontWeight: "bold",
                            transition: "all 0.2s ease",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "6px"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#c82333";
                            e.currentTarget.style.borderColor = "#c82333";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "#dc3545";
                            e.currentTarget.style.borderColor = "#dc3545";
                          }}
                          title="Limpar todos os marcadores e dados do mapa"
                        >
                          🗑️ Limpar Mapa
                        </button>
                      </div>

                      {/* Timeline Controls - só aparece se timeline estiver ativa */}
                      {timelineAtiva && coletarTimestampsUnicos.length > 0 && (
                        <>
                          {/* Cabeçalho da Timeline */}
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            marginBottom: '4px' 
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ 
                                fontWeight: 'bold', 
                                fontSize: '14px', 
                                color: isDarkTheme ? '#f7f9ff' : '#333' 
                              }}>
                                ⏱️ Linha do Tempo
                              </span>
                              <span style={{ 
                                fontSize: '12px', 
                                color: isDarkTheme ? '#9aa3ba' : '#666' 
                              }}>
                                {indiceTimelineAtual + 1} de {coletarTimestampsUnicos.length} timestamps
                              </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <select
                                value={timelineVelocidade}
                                onChange={(e) => setTimelineVelocidade(Number(e.target.value))}
                                style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  border: `1px solid ${isDarkTheme ? '#394052' : '#ccc'}`,
                                  backgroundColor: isDarkTheme ? '#151922' : 'white',
                                  color: isDarkTheme ? '#f7f9ff' : '#000000',
                                  fontSize: '12px',
                                  cursor: 'pointer'
                                }}
                              >
                                <option value={1}>1x</option>
                                <option value={2}>2x</option>
                                <option value={4}>4x</option>
                              </select>
                            </div>
                          </div>

                          {/* Controles de Navegação */}
                          <div className="locais-timeline-controls" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              {/* Botão Início */}
                              <button
                                onClick={timelineInicio}
                                disabled={indiceTimelineAtual <= 0}
                                style={{
                                  padding: '6px 10px',
                                  backgroundColor: indiceTimelineAtual <= 0 
                                    ? (isDarkTheme ? '#394052' : '#ccc') 
                                    : (isDarkTheme ? '#23c067' : '#007bff'),
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: indiceTimelineAtual <= 0 ? 'not-allowed' : 'pointer',
                                  fontSize: '12px',
                                  opacity: indiceTimelineAtual <= 0 ? 0.5 : 1
                                }}
                                title="Ir para o início"
                              >
                                ⏮️
                              </button>

                              {/* Botão Anterior */}
                              <button
                                onClick={timelineAnterior}
                                disabled={indiceTimelineAtual <= 0}
                                style={{
                                  padding: '6px 10px',
                                  backgroundColor: indiceTimelineAtual <= 0 
                                    ? (isDarkTheme ? '#394052' : '#ccc') 
                                    : (isDarkTheme ? '#23c067' : '#007bff'),
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: indiceTimelineAtual <= 0 ? 'not-allowed' : 'pointer',
                                  fontSize: '12px',
                                  opacity: indiceTimelineAtual <= 0 ? 0.5 : 1
                                }}
                                title="Timestamp anterior"
                              >
                                ⏪
                              </button>

                              {/* Botão Play/Pause */}
                              <button
                                onClick={toggleTimelinePlay}
                                disabled={coletarTimestampsUnicos.length === 0}
                                style={{
                                  padding: '8px 16px',
                                  backgroundColor: timelinePlay 
                                    ? '#dc3545' 
                                    : (isDarkTheme ? '#23c067' : '#28a745'),
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: coletarTimestampsUnicos.length === 0 ? 'not-allowed' : 'pointer',
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                  flex: 1,
                                  minWidth: '80px'
                                }}
                                title={timelinePlay ? 'Pausar animação' : 'Reproduzir animação'}
                              >
                                {timelinePlay ? '⏸️' : '▶️'}
                              </button>

                              {/* Botão Próximo */}
                              <button
                                onClick={timelineProximo}
                                disabled={indiceTimelineAtual >= coletarTimestampsUnicos.length - 1}
                                style={{
                                  padding: '6px 10px',
                                  backgroundColor: indiceTimelineAtual >= coletarTimestampsUnicos.length - 1 
                                    ? (isDarkTheme ? '#394052' : '#ccc') 
                                    : (isDarkTheme ? '#23c067' : '#007bff'),
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: indiceTimelineAtual >= coletarTimestampsUnicos.length - 1 ? 'not-allowed' : 'pointer',
                                  fontSize: '12px',
                                  opacity: indiceTimelineAtual >= coletarTimestampsUnicos.length - 1 ? 0.5 : 1
                                }}
                                title="Próximo timestamp"
                              >
                                ⏩
                              </button>

                              {/* Botão Fim */}
                              <button
                                onClick={timelineFim}
                                disabled={indiceTimelineAtual >= coletarTimestampsUnicos.length - 1}
                                style={{
                                  padding: '6px 10px',
                                  backgroundColor: indiceTimelineAtual >= coletarTimestampsUnicos.length - 1 
                                    ? (isDarkTheme ? '#394052' : '#ccc') 
                                    : (isDarkTheme ? '#23c067' : '#007bff'),
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: indiceTimelineAtual >= coletarTimestampsUnicos.length - 1 ? 'not-allowed' : 'pointer',
                                  fontSize: '12px',
                                  opacity: indiceTimelineAtual >= coletarTimestampsUnicos.length - 1 ? 0.5 : 1
                                }}
                                title="Ir para o fim"
                              >
                                ⏭️
                              </button>
                            </div>

                            {/* Slider de Timeline */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <input
                                type="range"
                                min={0}
                                max={Math.max(0, coletarTimestampsUnicos.length - 1)}
                                value={Math.max(0, indiceTimelineAtual)}
                                onChange={(e) => {
                                  const newIndex = parseInt(e.target.value);
                                  if (newIndex >= 0 && newIndex < coletarTimestampsUnicos.length) {
                                    setTimelineTimestampAtual(coletarTimestampsUnicos[newIndex]);
                                    if (timelinePlay) {
                                      toggleTimelinePlay();
                                    }
                                  }
                                }}
                                style={{
                                  flex: 1,
                                  height: '6px',
                                  borderRadius: '3px',
                                  outline: 'none',
                                  cursor: 'pointer',
                                  WebkitAppearance: 'none',
                                  appearance: 'none',
                                  background: coletarTimestampsUnicos.length > 1 
                                    ? `linear-gradient(to right, ${isDarkTheme ? '#23c067' : '#007bff'} 0%, ${isDarkTheme ? '#23c067' : '#007bff'} ${(indiceTimelineAtual / (coletarTimestampsUnicos.length - 1)) * 100}%, ${isDarkTheme ? '#394052' : '#e0e0e0'} ${(indiceTimelineAtual / (coletarTimestampsUnicos.length - 1)) * 100}%, ${isDarkTheme ? '#394052' : '#e0e0e0'} 100%)`
                                    : (isDarkTheme ? '#23c067' : '#007bff')
                                }}
                              />
                            </div>
                          </div>

                          {/* Informações do Timestamp Atual */}
                          {timelineTimestampAtual && (
                            <div className="locais-timeline-info" style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '8px',
                              padding: '8px 12px',
                              backgroundColor: isDarkTheme ? '#1f242d' : '#f8f9fa',
                              borderRadius: '6px',
                              fontSize: '12px',
                              border: `1px solid ${isDarkTheme ? '#2f3542' : '#dee2e6'}`,
                              color: isDarkTheme ? '#f7f9ff' : '#000000'
                            }}>
                              <div>
                                <strong>Timestamp Atual:</strong>{' '}
                                <span style={{ 
                                  color: isDarkTheme ? '#23c067' : '#007bff', 
                                  fontFamily: 'monospace', 
                                  fontSize: '11px' 
                                }}>
                                  {timelineTimestampAtual}
                                </span>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span>
                                  <strong>Ativos visíveis:</strong>{' '}
                                  <span style={{ color: isDarkTheme ? '#23c067' : '#28a745' }}>
                                    {Object.keys(historicoFiltradoTimeline).length}
                                  </span>
                                </span>
                                {coletarTimestampsUnicos.length > 0 && (
                                  <>
                                    <span>
                                      <strong>Primeiro:</strong>{' '}
                                      <span style={{ 
                                        color: isDarkTheme ? '#9aa3ba' : '#666', 
                                        fontSize: '10px' 
                                      }}>
                                        {moment(coletarTimestampsUnicos[0], 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY HH:mm:ss')}
                                      </span>
                                    </span>
                                    <span>
                                      <strong>Último:</strong>{' '}
                                      <span style={{ 
                                        color: isDarkTheme ? '#9aa3ba' : '#666', 
                                        fontSize: '10px' 
                                      }}>
                                        {moment(coletarTimestampsUnicos[coletarTimestampsUnicos.length - 1], 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY HH:mm:ss')}
                                      </span>
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {/* Mensagem quando timeline não está ativa mas controles de data/hora estão visíveis */}
                      {!timelineAtiva || coletarTimestampsUnicos.length === 0 ? (
                        <div style={{ 
                          padding: '15px', 
                          textAlign: 'center', 
                          color: isDarkTheme ? '#9aa3ba' : '#666',
                          fontSize: '12px',
                          backgroundColor: isDarkTheme ? '#1f242d' : '#f8f9fa',
                          borderRadius: '5px',
                          marginTop: '10px',
                          border: `1px solid ${isDarkTheme ? '#2f3542' : '#dee2e6'}`
                        }}>
                          <p>⏱️ Configure a data e hora inicial acima para ativar a linha do tempo.</p>
                        </div>
                      ) : null}
                    </div>
                  );
                })()}
              </div>
            </div>
            <section style={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', minHeight: 0, height: "100%", width: sidebarExpanded ? "calc(85% - 5px)" : "100%", transition: "width 0.3s ease" }}>
              <MapContainer
                center={[-22.0, -47.0]}
                zoom={6}
                minZoom={5}
                maxZoom={20}
                style={{ width: "100%", height: "100%", flex: 1, minHeight: 0 }}
                ref={mapRef}
              >
                {/* Inicializar markersGroup_C */}
                <MapGroupInitializer markersGroupCRef={markersGroupCRef} />

                {/* Adicionar controle de escala */}
                <MapScaleControl />

                {/* Camada padrão - Satélite Google (igual ao MenuAtivoMapa) */}
                <TileLayer
                  url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
                  maxZoom={20}
                  maxNativeZoom={20}
                  tileSize={256}
                  zoomOffset={0}
                  updateWhenZooming={false}
                  keepBuffer={2}
                  updateWhenIdle={true}
                  eventHandlers={{
                    tileerror: (e) => {
                      console.warn('⚠️ Erro ao carregar tile:', e);
                      // Não fazer nada - o Leaflet já trata automaticamente
                    }
                  }}
                />

                {/* Controle de Camadas Base (igual ao MenuAtivoMapa) */}
                <LayersControl position="topright">
                  <LayersControl.BaseLayer checked name="Satélite Google">
                    <TileLayer
                      url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
                      maxZoom={20}
                      maxNativeZoom={20}
                      tileSize={256}
                      zoomOffset={0}
                      updateWhenZooming={false}
                      keepBuffer={2}
                      updateWhenIdle={true}
                    />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="Viário Google">
                    <TileLayer
                      url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
                      maxZoom={20}
                      maxNativeZoom={20}
                      tileSize={256}
                      zoomOffset={0}
                      updateWhenZooming={false}
                      keepBuffer={2}
                      updateWhenIdle={true}
                    />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="OpenStreetMap">
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      maxZoom={18}
                      maxNativeZoom={18}
                      tileSize={256}
                      zoomOffset={0}
                      updateWhenZooming={false}
                      keepBuffer={2}
                      updateWhenIdle={true}
                    />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="Mapa Escuro">
                    <TileLayer
                      url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                      attribution="&copy; <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors &copy; <a href='https://carto.com/attributions'>CARTO</a>"
                      maxZoom={18}
                      maxNativeZoom={18}
                      tileSize={256}
                      zoomOffset={0}
                      updateWhenZooming={false}
                      keepBuffer={2}
                      updateWhenIdle={true}
                    />
                  </LayersControl.BaseLayer>
                </LayersControl>
                {/* Botão para Mostrar/Ocultar Timeline */}
                {timelineAtiva && coletarTimestampsUnicos.length > 0 && (
                  <div className="leaflet-top leaflet-right" style={{ marginTop: '72px', marginRight: '52px', zIndex: 1002 }}>
                    <div className="leaflet-control leaflet-bar">
                      <button
                        title={mostrarTimeline ? "Ocultar Linha do Tempo" : "Mostrar Linha do Tempo"}
                        onClick={() => setMostrarTimeline(!mostrarTimeline)}
                        style={{
                          width: '40px', height: '40px', display: 'flex', alignItems: 'center',
                          justifyContent: 'center', fontSize: '1.2em', backgroundColor: mostrarTimeline ? '#007bff' : 'white',
                          color: mostrarTimeline ? 'white' : 'black',
                          border: '2px solid rgba(0,0,0,0.2)', borderRadius: '4px', cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {mostrarTimeline ? '⏱️' : '⏱️'}
                      </button>
                    </div>
                  </div>
                )}
                {/* Menu de Configurações */}
                <div className="leaflet-top leaflet-right" style={{ marginTop: '72px', marginRight: '2px', zIndex: 1002 }}>
                  <div className="leaflet-control leaflet-bar">
                    <button
                      title="Configurações do Mapa"
                      onClick={() => {
                        const configMenu = document.getElementById('configMenuMap');
                        if (configMenu) {
                          configMenu.style.display = configMenu.style.display === 'block' ? 'none' : 'block';
                        }
                      }}
                      style={{
                        width: '40px', height: '40px', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontSize: '1.2em', backgroundColor: 'white',
                        border: '2px solid rgba(0,0,0,0.2)', borderRadius: '4px', cursor: 'pointer'
                      }}
                    >
                      ⚙️
                    </button>
                  </div>
                </div>
                {/* Botão para Configurar Popup de Ativos */}
                <div className="leaflet-top leaflet-right" style={{ marginTop: '120px', marginRight: '2px', zIndex: 1002 }}>
                  <div className="leaflet-control leaflet-bar">
                    <button
                      title="Configurar Campos do Popup de Ativos"
                      onClick={() => setShowPopupConfig(true)}
                      style={{
                        width: '40px', height: '40px', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontSize: '1.2em', backgroundColor: 'white',
                        border: '2px solid rgba(0,0,0,0.2)', borderRadius: '4px', cursor: 'pointer'
                      }}
                    >
                      📋
                    </button>
                  </div>
                </div>


                {/* Menu de Configurações */}
                <div
                  id="configMenuMap"
                  style={{
                    display: 'none', position: 'absolute', top: '132px', right: '10px',
                    backgroundColor: 'white', color: 'black', padding: '15px', borderRadius: '5px',
                    boxShadow: '0 0 10px rgba(0,0,0,0.2)', zIndex: 1001, width: '220px'
                  }}
                >
                  <h4 style={{ marginTop: 0, color: 'black', marginBottom: '10px' }}>Config. Mapa</h4>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={showGateways}
                        onChange={() => setShowGateways(v => !v)}
                        style={{ marginRight: '8px' }}
                      />
                      Exibir Gateways
                    </label>
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={showBeacons}
                        onChange={() => setShowBeacons(v => !v)}
                        style={{ marginRight: '8px' }}
                      />
                      Exibir Beacons
                    </label>
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={vercirculos}
                        onChange={toggleCirculos}
                        style={{ marginRight: '8px' }}
                      />
                      Círculos Interferência
                    </label>
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={mostrarTrilhas}
                        onChange={() => setMostrarTrilhas(v => !v)}
                        style={{ marginRight: '8px' }}
                      />
                      Exibir Trilhas
                    </label>
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={mostrarGeometrias}
                        onChange={() => setMostrarGeometrias(v => !v)}
                        style={{ marginRight: '8px' }}
                      />
                      Exibir Perímetros
                    </label>
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <button
                      onClick={limparHistorico}
                      style={{
                        padding: '8px 12px',
                        width: '100%',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      🗑️ Limpar Histórico
                    </button>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px' }}>Nível Interferência:</label>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      {[1, 2, 3, 4].map((level) => (
                        <button
                          key={level}
                          onClick={() => handleInterferenceLevelChange(level)}
                          style={{
                            padding: '5px 0', // Ajustado para melhor visualização
                            width: '22%', // Para caber 4 botões
                            backgroundColor: interferenceLevel === level ? '#0078A8' : '#f0f0f0',
                            color: interferenceLevel === level ? 'white' : 'black',
                            border: '1px solid #ccc', // Adicionada borda sutil
                            borderRadius: '3px',
                            cursor: 'pointer'
                          }}>
                          {level}
                        </button>
                      ))}
                    </div>
                  </div>
                  {dataHoraAtual && (
                    <div style={{ marginTop: '10px', padding: '8px', backgroundColor: '#f0f0f0', borderRadius: '3px' }}>
                      <small style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Data Atual:</small>
                      <small style={{ display: 'block' }}>{dataHoraAtual}</small>
                      <small style={{ display: 'block', marginTop: '4px', color: '#666' }}>
                        Buscas: {contadorRefreshRef.current}
                      </small>
                    </div>
                  )}

                </div>

                {/* Marcadores para Gateways (Locais) - criados no useEffect, só leitura aqui para evitar loop */}
                {showGateways && Object.entries(gatewayMarkers).map(([IdGateway, marker]) => {
                  const gatewayData = (marker as any).gatewayData;
                  if (!gatewayData) return null;
                  return (
                    <Marker
                      key={`gateway-${IdGateway}`}
                      position={marker.getLatLng()}
                      icon={marker.getIcon()}
                    >
                      <Popup>
                        <div style={{ textAlign: 'left' }} dangerouslySetInnerHTML={{ __html: renderPopupGateway(gatewayData, popupGatewayConfig) }} />
                      </Popup>
                    </Marker>
                  );
                })}

                {/* Marcadores para Ativos (Beacons GPS) */}
                {showBeacons && assetBeaconData && selectedAtivos.length > 0 && (() => {
                  const filteredAssets = assetBeaconData.filter(asset => {
                    // Primeiro verifica se o ativo está selecionado
                    const isSelected = selectedAtivos.includes(asset.Ativo);

                    // Depois verifica se tem coordenadas válidas
                    const hasValidCoords = typeof asset.Latitude === 'number' &&
                      typeof asset.Longitude === 'number' &&
                      !isNaN(asset.Latitude) &&
                      !isNaN(asset.Longitude) &&
                      asset.Latitude !== 0 &&
                      asset.Longitude !== 0;

                    return isSelected && hasValidCoords;
                  });

                  return filteredAssets;
                })().map((asset) => (
                  <Marker
                    key={(asset as any)._uniqueKey || `beacon-${asset.DeviceKey}`}
                    position={[asset.Latitude, asset.Longitude]}
                    icon={(() => {
                      // Determinar método baseado na origem da coordenada
                      const metodo = asset.lat_long_origin === 'B' ? 'gps' : 'trilateracao';
                      const cacheKey = `${asset.Ativo || ''}_${metodo}`;

                      // Tentar usar ícone personalizado baseado no ativo
                      const cachedIcon = ativoIconsCache.get(cacheKey);
                      if (cachedIcon) {
                        // Aumentar tamanho do ícone quando destaque está ativo
                        if (destaqueMarcadores && timelineAtiva) {
                          // Verificar se é L.Icon (tem iconUrl) ou L.DivIcon (tem html)
                          if (cachedIcon.options && 'iconUrl' in cachedIcon.options && cachedIcon.options.iconUrl) {
                            const destaqueIcon = new L.Icon({
                              iconUrl: cachedIcon.options.iconUrl,
                              iconSize: [28, 28], // Aumentar tamanho
                              iconAnchor: [14, 28],
                              popupAnchor: [0, -28]
                            });
                            return destaqueIcon;
                          }
                          // Se for DivIcon, retornar o ícone original (não podemos aumentar facilmente)
                          // O destaque será aplicado apenas aos ícones padrão
                        }
                        return cachedIcon;
                      }

                      // Buscar ícone personalizado de forma assíncrona para próximas renderizações
                      if (asset.Ativo) {
                        getCustomBeaconIcon(asset.Ativo, metodo).then(() => {
                          // Force re-render quando ícone for carregado
                          setAtivoIconsCache(prev => new Map(prev));
                        });
                      }

                      // Usar ícone padrão enquanto personalizado não carrega
                      const baseIcon = asset.lat_long_origin === 'B' ? createBeaconGPSIcon() : createBeaconMethodIcon('trilateracao');
                      
                      // Aumentar tamanho do ícone quando destaque está ativo
                      if (destaqueMarcadores && timelineAtiva) {
                        // Verificar se baseIcon tem iconUrl (é L.Icon)
                        if (baseIcon.options && 'iconUrl' in baseIcon.options && baseIcon.options.iconUrl) {
                          const destaqueIcon = new L.Icon({
                            iconUrl: baseIcon.options.iconUrl,
                            iconSize: [28, 28], // Aumentar de 18x18 para 28x28
                            iconAnchor: [14, 28],
                            popupAnchor: [0, -28]
                          });
                          return destaqueIcon;
                        }
                      }
                      
                      return baseIcon;
                    })()}
                    eventHandlers={{
                      click: () => {
                        if (markersGroup_C) {
                          markersGroup_C.clearLayers();
                        }
                        if (vercirculos && Locais && Locais.length > 0 && markersGroup_C) {
                          if (asset.lat_long_origin !== 'B') {
                            const gatewaysComDistancia = Locais
                              .filter(gw => typeof gw.Latitude === 'number' && typeof gw.Longitude === 'number')
                              .map(gw => {
                                const distancia = calcularDistancia(
                                  asset.Latitude,
                                  asset.Longitude,
                                  gw.Latitude!,
                                  gw.Longitude!
                                );
                                return { ...gw, distancia };
                              });

                            const gatewaysProximos = gatewaysComDistancia
                              .sort((a, b) => a.distancia - b.distancia)
                              .slice(0, 3);

                            gatewaysProximos.forEach(gw => {
                              if (
                                typeof gw.Latitude === 'number' &&
                                typeof gw.Longitude === 'number' &&
                                typeof gw.distancia === 'number' &&
                                markersGroup_C
                              ) {
                                L.circle([gw.Latitude, gw.Longitude], {
                                  radius: gw.distancia * 1000,
                                  color: 'blue',
                                  fillColor: '#30f',
                                  fillOpacity: 0.3
                                }).addTo(markersGroup_C);

                                L.marker([gw.Latitude, gw.Longitude], {
                                  icon: createStarIcon()
                                }).addTo(markersGroup_C);
                              }
                            });
                          }
                        }
                      },
                      mouseover: async () => {
                        // Limpar estrelas anteriores igual ao Grafana
                        if (markersGroup_C) {
                          markersGroup_C.clearLayers();
                        }

                        // Calcular a data com base no LastSeen do beacon (igual ao Grafana)
                        const calculateDateFrom = (dateString: string): string => {
                          try {
                            console.log(`📅 Processando data: ${dateString}`);

                            // 1. Quebra a string original (formato: DD-MM-YYYY HH:mm)
                            const [dia, mes, resto] = dateString.split("-");
                            const [ano, horaMinuto] = resto.split(" ");
                            const [hora, minuto] = horaMinuto.split(":");

                            // 2. Constrói a string ISO com segundos = 00
                            const dataISO = `${ano}-${mes}-${dia}T${hora}:${minuto}:00`;

                            // 3. Cria objeto Date e subtrai 10 segundos
                            const data = new Date(dataISO);
                            data.setSeconds(data.getSeconds() - 10);

                            // 4. Função para adicionar zero à esquerda
                            const pad2 = (n: number) => n.toString().padStart(2, '0');

                            // 5. Formata no padrão YYYY-MM-DD HH:mm:ss
                            const diaF = pad2(data.getDate());
                            const mesF = pad2(data.getMonth() + 1);
                            const anoF = data.getFullYear();
                            const horaF = pad2(data.getHours());
                            const minutoF = pad2(data.getMinutes());
                            const segundoF = pad2(data.getSeconds());

                            const resultado = `${anoF}-${mesF}-${diaF} ${horaF}:${minutoF}:${segundoF}`;
                            console.log(`✅ Data calculada: ${resultado}`);
                            return resultado;
                          } catch (error) {
                            console.error("❌ Erro ao processar data:", error);
                            return dateString;
                          }
                        };

                        const dtHr = calculateDateFrom(asset.LastSeen);

                        // Payload exatamente igual ao Grafana
                        const payload = {
                          Ativo: asset.Ativo,
                          Organizacao: asset.Organizacao,
                          DtHr: dtHr
                        };

                        console.log(`📦 Payload LastSeen:`, payload);

                        try {
                          const response = await fetch(`${backendUrl}/api/LastSeenNred`, {
                            method: 'POST',
                            headers: {
                              'accept': 'application/json',
                              'Content-Type': 'application/json-patch+json',
                              'Authorization': `Bearer ${auth?.Token}`
                            },
                            body: JSON.stringify(payload)
                          });

                          console.log(`📡 Status resposta: ${response.status}`);

                          if (response.ok) {
                            let result = await response.json();

                            // Parse duplo se necessário
                            if (typeof result === 'string') {
                              result = JSON.parse(result);
                            }

                            console.log(`📋 Resultado LastSeen:`, result);

                            if (Array.isArray(result)) {
                              console.log(`📈 Total gateways encontrados: ${result.length}`);

                              // Implementação igual ao Grafana (linha 1310-1330)
                              result.forEach((row: any, index: number) => {
                                console.log(`🏢 Processando gateway ${index}:`, {
                                  IdGateway: row.IdGateway,
                                  nomeGateway: row.nomeGateway,
                                  LastSeen: row.LastSeen
                                });

                                // Buscar marker do gateway igual ao Grafana: markers[`G_${row.IdGateway}`]
                                const gatewayKey = `G_${row.IdGateway}`;
                                const gatewayMarker = gatewayMarkers[gatewayKey];

                                console.log(`🔍 Buscando gateway: ${gatewayKey}`);
                                console.log(`📍 Gateway encontrado:`, !!gatewayMarker);

                                if (gatewayMarker) {
                                  // Criar tooltip igual ao Grafana
                                  const conteudoTooltip = `
                                    <b>Captura:</b> ${row.LastSeen}<br>
                                    <b>Gateway:</b> ${row.nomeGateway}<br>
                                  `;

                                  // Obter posição exata do gateway para sobrepor
                                  const latlng_gw = gatewayMarker.getLatLng();
                                  console.log(`⭐ Criando estrela em:`, latlng_gw);

                                  // Criar ícone de estrela igual ao Grafana
                                  const svgStar = '<svg version="1.0" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 64 64" enable-background="new 0 0 64 64" xml:space="preserve" fill="#000000"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path fill="#daa30b" d="M63.893,24.277c-0.238-0.711-0.854-1.229-1.595-1.343l-19.674-3.006L33.809,1.15 C33.479,0.448,32.773,0,31.998,0s-1.48,0.448-1.811,1.15l-8.815,18.778L1.698,22.935c-0.741,0.113-1.356,0.632-1.595,1.343 c-0.238,0.71-0.059,1.494,0.465,2.031l14.294,14.657L11.484,61.67c-0.124,0.756,0.195,1.517,0.822,1.957 c0.344,0.243,0.747,0.366,1.151,0.366c0.332,0,0.666-0.084,0.968-0.25l17.572-9.719l17.572,9.719c0.302,0.166,0.636,0.25,0.968,0.25 c0.404,0,0.808-0.123,1.151-0.366c0.627-0.44,0.946-1.201,0.822-1.957l-3.378-20.704l14.294-14.657 C63.951,25.771,64.131,24.987,63.893,24.277z"></path> </g></svg>';

                                  const iconeEstrela = L.icon({
                                    iconUrl: "data:image/svg+xml;base64," + btoa(svgStar),
                                    iconSize: [20, 20], // Tamanho menor que o gateway
                                    iconAnchor: [10, 20] // Âncora ajustada proporcionalmente
                                  });

                                  // Criar marker da estrela igual ao Grafana
                                  const marker_gw = L.marker(latlng_gw, {
                                    icon: iconeEstrela,
                                    zIndexOffset: 1000 // Garante que fique acima do gateway
                                  })
                                    .bindTooltip(conteudoTooltip, {
                                      permanent: true,
                                      direction: 'top',
                                      opacity: 0.9,
                                      offset: [0, -50] // Move o tooltip 50px para cima para não sobrepor a estrela
                                    }).openTooltip();

                                  // Adicionar ao grupo C igual ao Grafana
                                  if (markersGroup_C) {
                                    markersGroup_C.addLayer(marker_gw);
                                    console.log(`✨ Estrela adicionada para gateway: ${row.nomeGateway}`);
                                  }
                                } else {
                                  console.log(`❌ Gateway não encontrado: ${gatewayKey}`);
                                  console.log(`🔑 Gateways disponíveis:`, Object.keys(gatewayMarkers));
                                }
                              });
                            }
                          }
                        } catch (error) {
                          console.error("❌ Erro ao buscar última visualização:", error);
                        }
                      },
                      mouseout: async () => {
                        console.log(`🐭 Mouseout do beacon: ${asset.DeviceKey}`);
                        // Limpar estrelas igual ao Grafana (linha 1332-1334)
                        if (markersGroup_C) {
                          markersGroup_C.clearLayers();
                          console.log(`🧹 Estrelas removidas`);
                        }
                      }
                    }}
                    zIndexOffset={1200}
                  >
                    <Popup>
                      {renderPopupAtivo(asset, popupConfig)}
                    </Popup>
                  </Marker>
                ))}

                {/* Marcadores para Ativos (Beacons Trilaterados por Gateway) */}
                {/* Marcadores para Ativos (Beacons Trilaterados por Gateway) 
                 * Implementação adaptada do Grafana para suportar beacons 4.2 com diferentes métodos:
                 * - trilateracao: 3 gateways (ícone cinza)
                 * - centroide: 2 gateways (ícone dourado)  
                 * - estimado: 1 gateway (ícone amarelo)
                 */}
                {(() => {
                  if (!showBeacons || !gateway || !selectedAtivos.length) {
                    return null;
                  }

                  // Processar dados do gateway
                  let gatewayTrackingData: any[] = [];

                  try {
                    if (typeof gateway === 'string') {
                      gatewayTrackingData = JSON.parse(gateway);
                    } else if (Array.isArray(gateway)) {
                      gatewayTrackingData = gateway;
                    }
                  } catch (error) {
                    console.error("❌ Erro ao processar dados do gateway:", error);
                    return null;
                  }

                  if (!Array.isArray(gatewayTrackingData) || gatewayTrackingData.length === 0) {
                    return null;
                  }

                  const filteredGatewayAssets = gatewayTrackingData.filter(asset => {
                    // Primeiro verifica se o ativo está selecionado
                    const isSelected = selectedAtivos.includes(asset.Ativo);
                    if (!isSelected) {
                      return false;
                    }

                    // Verificar coordenadas baseado no método
                    let lat, lng;
                    if (asset.Metodo === 'estimado' || asset.Metodo === 'centroide') {
                      lat = asset.LatitudeAleatoria || asset.Latitude;
                      lng = asset.LongitudeAleatoria || asset.Longitude;
                    } else {
                      lat = asset.Latitude;
                      lng = asset.Longitude;
                    }

                    const hasValidCoords = typeof lat === 'number' &&
                      typeof lng === 'number' &&
                      !isNaN(lat) &&
                      !isNaN(lng) &&
                      lat !== 0 &&
                      lng !== 0;

                    return hasValidCoords;
                  });

                  return filteredGatewayAssets.map((asset: any) => {
                    // Determinar as coordenadas baseado no método (igual ao Grafana)
                    let coordinates: [number, number];
                    if (asset.Metodo === 'estimado' || asset.Metodo === 'centroide') {
                      // Usar coordenadas aleatórias para métodos estimado e centroide
                      coordinates = [
                        asset.LatitudeAleatoria || asset.Latitude,
                        asset.LongitudeAleatoria || asset.Longitude
                      ];
                    } else {
                      // Usar coordenadas normais para trilateração
                      coordinates = [asset.Latitude, asset.Longitude];
                    }

                    const metodo = asset.Metodo ? asset.Metodo.toLowerCase() : 'trilateracao';

                    return (
                      <Marker
                        key={asset._uniqueKey || `beacon-gateway-${asset.DeviceKey}`}
                        position={coordinates}
                        icon={(() => {
                          // Usar o método real do asset
                          const cacheKey = `${asset.Ativo || ''}_${metodo}`;

                          // Tentar usar ícone personalizado baseado no ativo
                          const cachedIcon = ativoIconsCache.get(cacheKey);
                          if (cachedIcon) {
                            // Aumentar tamanho do ícone quando destaque está ativo
                            if (destaqueMarcadores && timelineAtiva) {
                              // Verificar se é L.Icon (tem iconUrl) ou L.DivIcon (tem html)
                              if (cachedIcon.options && 'iconUrl' in cachedIcon.options && cachedIcon.options.iconUrl) {
                                const destaqueIcon = new L.Icon({
                                  iconUrl: cachedIcon.options.iconUrl,
                                  iconSize: [28, 28], // Aumentar tamanho
                                  iconAnchor: [14, 28],
                                  popupAnchor: [0, -28]
                                });
                                return destaqueIcon;
                              }
                              // Se for DivIcon, retornar o ícone original (não podemos aumentar facilmente)
                              // O destaque será aplicado apenas aos ícones padrão
                            }
                            return cachedIcon;
                          }

                          // Buscar ícone personalizado de forma assíncrona para próximas renderizações
                          if (asset.Ativo) {
                            getCustomBeaconIcon(asset.Ativo, metodo).then(() => {
                              // Force re-render quando ícone for carregado
                              setAtivoIconsCache(prev => new Map(prev));
                            });
                          }


                          // Usar ícone padrão enquanto personalizado não carrega
                          const baseIcon = createBeaconMethodIcon(metodo);
                          
                          // Aumentar tamanho do ícone quando destaque está ativo
                          if (destaqueMarcadores && timelineAtiva) {
                            // Verificar se baseIcon tem iconUrl (é L.Icon)
                            if (baseIcon.options && 'iconUrl' in baseIcon.options && baseIcon.options.iconUrl) {
                              const destaqueIcon = new L.Icon({
                                iconUrl: baseIcon.options.iconUrl,
                                iconSize: [28, 28], // Aumentar de 18x18 para 28x28
                                iconAnchor: [14, 28],
                                popupAnchor: [0, -28]
                              });
                              return destaqueIcon;
                            }
                          }
                          
                          return baseIcon;
                        })()}
                        eventHandlers={{
                          click: () => {
                            if (markersGroup_C) {
                              markersGroup_C.clearLayers();
                            }

                            if (vercirculos && markersGroup_C) {

                              // Lógica de círculos baseada no método
                              // NOTA: Todos os círculos são azuis (seguindo padrão do Grafana)
                              if (metodo === 'trilateracao' && asset.GW1 && asset.GW2 && asset.GW3) {
                                // 3 gateways para trilateração
                                const gw1 = Locais.find(gw => gw.IdGateway === asset.GW1.Id);
                                const gw2 = Locais.find(gw => gw.IdGateway === asset.GW2.Id);
                                const gw3 = Locais.find(gw => gw.IdGateway === asset.GW3.Id);

                                const gateways = [
                                  { gw: gw1, dist: asset.GW1.Distancia },
                                  { gw: gw2, dist: asset.GW2.Distancia },
                                  { gw: gw3, dist: asset.GW3.Distancia }
                                ];

                                gateways.forEach(({ gw, dist }) => {
                                  if (gw && gw.Latitude && gw.Longitude) {
                                    const circle = L.circle([gw.Latitude, gw.Longitude], {
                                      radius: parseFloat(dist),
                                      color: 'blue',
                                      fillColor: '#30f',
                                      fillOpacity: 0.3
                                    });

                                    const star = L.marker([gw.Latitude, gw.Longitude], {
                                      icon: createStarIcon()
                                    });

                                    markersGroup_C.addLayer(circle);
                                    markersGroup_C.addLayer(star);
                                  }
                                });
                              } else if (metodo === 'centroide' && asset.GW1 && asset.GW2) {
                                // 2 gateways para centroide
                                const gateways = [
                                  { gw: Locais.find(gw => gw.IdGateway === asset.GW1.Id), dist: asset.GW1.Distancia },
                                  { gw: Locais.find(gw => gw.IdGateway === asset.GW2.Id), dist: asset.GW2.Distancia }
                                ];

                                gateways.forEach(({ gw, dist }) => {
                                  if (gw && gw.Latitude && gw.Longitude) {
                                    const circle = L.circle([gw.Latitude, gw.Longitude], {
                                      radius: parseFloat(dist),
                                      color: 'blue',
                                      fillColor: '#30f',
                                      fillOpacity: 0.3
                                    });

                                    const star = L.marker([gw.Latitude, gw.Longitude], {
                                      icon: createStarIcon()
                                    });

                                    markersGroup_C.addLayer(circle);
                                    markersGroup_C.addLayer(star);
                                  }
                                });
                              } else if (metodo === 'estimado' && asset.GW1) {
                                // 1 gateway para estimado
                                const gw = Locais.find(gw => gw.IdGateway === asset.GW1.Id);

                                if (gw && gw.Latitude && gw.Longitude) {
                                  const circle = L.circle([gw.Latitude, gw.Longitude], {
                                    radius: parseFloat(asset.GW1.Distancia),
                                    color: 'blue',
                                    fillColor: '#30f',
                                    fillOpacity: 0.3
                                  });

                                  const star = L.marker([gw.Latitude, gw.Longitude], {
                                    icon: createStarIcon()
                                  });

                                  markersGroup_C.addLayer(circle);
                                  markersGroup_C.addLayer(star);
                                }
                              }
                            }
                          }
                        }}
                        zIndexOffset={1500}
                      >
                        <Popup>
                          {renderPopupAtivo(asset, popupConfig, coordinates)}
                        </Popup>
                      </Marker>
                    );
                  });
                })()}

                {/* Perímetros dos Locais (Geometrias) - exibe todos os perímetros de cada local */}
                {mostrarGeometrias && Object.keys(locaisGeometrias).length > 0 && (() => {
                  return Object.entries(locaisGeometrias).flatMap(([idPlace, features]) => {
                    const local = Locais.find(l => l.IdPlace === parseInt(idPlace));
                    if (!local) return [];
                    const isSelected = selectedLocal.includes(local.Gateway);
                    if (!isSelected) return [];
                    // Suporta tanto array (múltiplos perímetros) quanto objeto único (compatibilidade)
                    const featuresArray = Array.isArray(features) ? features : [features];
                    return featuresArray.map((feature: any, idx: number) => {
                      const quantidadeAtivos = contarAtivosNoPerimetro(feature);
                      return (
                        <GeoJSON
                          key={`geometria-${idPlace}-${idx}`}
                          data={feature}
                          style={{
                            color: '#3388ff',
                            weight: 3,
                            opacity: 0.7,
                            fillColor: '#3388ff',
                            fillOpacity: 0.2
                          }}
                          pathOptions={{
                            color: '#3388ff',
                            weight: 3,
                            opacity: 0.7,
                            fillColor: '#3388ff',
                            fillOpacity: 0.2
                          }}
                        >
                          <Popup>
                            <div style={{ textAlign: 'left' }}>
                              <b>Local:</b> {feature.properties?.name || local.Local || 'Sem nome'}<br />
                              <b>ID:</b> {idPlace}<br />
                              <b>Gateway:</b> {local.Gateway}<br />
                              <b style={{ color: quantidadeAtivos > 0 ? '#28a745' : '#666' }}>
                                Ativos dentro do perímetro:
                              </b> <span style={{ 
                                color: quantidadeAtivos > 0 ? '#28a745' : '#666',
                                fontWeight: 'bold',
                                fontSize: '14px'
                              }}>{quantidadeAtivos}</span>
                            </div>
                          </Popup>
                        </GeoJSON>
                      );
                    });
                  });
                })()}

                {/* Trilhas de Movimento dos Ativos */}
                {mostrarTrilhas && Object.keys(timelineAtiva ? historicoFiltradoTimeline : historicoAtivos).length > 0 && (() => {
                  const historicoParaExibir = timelineAtiva ? historicoFiltradoTimeline : historicoAtivos;
                  return Object.entries(historicoParaExibir).map(([ativoKey, posicoes]) => {
                    if (posicoes.length < 2) {
                      return null;
                    }

                    // Filtrar apenas ativos selecionados
                    if (selectedAtivos.length > 0 && !selectedAtivos.includes(ativoKey)) {
                      return null;
                    }

                    // Obter ou gerar cor consistente para o ativo
                    let corTrilha = coresTrilhas[ativoKey];
                    if (!corTrilha) {
                      corTrilha = generateRandomColor();
                      setCoresTrilhas(prev => ({ ...prev, [ativoKey]: corTrilha }));
                    }

                    // Converter posições para formato do Leaflet
                    const coordenadas: [number, number][] = posicoes.map(pos => [pos.latitude, pos.longitude]);

                    return (
                      <React.Fragment key={`trilha-${ativoKey}`}>
                        {/* Linha da trilha */}
                        <Polyline
                          positions={coordenadas}
                          pathOptions={{
                            color: corTrilha,
                            weight: 3,
                            opacity: 0.7,
                            dashArray: '10, 5'
                          }}
                        >
                          <Popup>
                            <div style={{ textAlign: 'left' }}>
                              <b>Ativo:</b> {ativoKey}<br />
                              <b>Total de Posições:</b> {posicoes.length}<br />
                              <b>Primeira Posição:</b> {posicoes[0].timestamp}<br />
                              <b>Última Posição:</b> {posicoes[posicoes.length - 1].timestamp}
                            </div>
                          </Popup>
                        </Polyline>

                        {/* Marcadores nos pontos intermediários (opcional) */}
                        {posicoes.slice(0, -1).map((pos, idx) => (
                          <Marker
                            key={`ponto-trilha-${ativoKey}-${idx}`}
                            position={[pos.latitude, pos.longitude]}
                            icon={L.divIcon({
                              className: 'trilha-ponto',
                              html: `<div style="
                                width: 8px;
                                height: 8px;
                                background: ${corTrilha};
                                border: 2px solid white;
                                border-radius: 50%;
                                box-shadow: 0 0 4px rgba(0,0,0,0.5);
                              "></div>`,
                              iconSize: [8, 8],
                              iconAnchor: [4, 4]
                            })}
                            zIndexOffset={1100}
                          >
                            <Popup>
                              <div style={{ textAlign: 'left' }}>
                                <b>Ativo:</b> {ativoKey}<br />
                                <b>Posição:</b> {idx + 1} de {posicoes.length}<br />
                                <b>Timestamp:</b> {pos.timestamp}<br />
                                <b>Método:</b> {pos.metodo}<br />
                                <span><i className="globe icon"></i> {pos.latitude.toFixed(5)}, {pos.longitude.toFixed(5)}</span>
                              </div>
                            </Popup>
                          </Marker>
                        ))}
                      </React.Fragment>
                    );
                  });
                })()}
              </MapContainer>
            </section>
          </div>
      </div>
      
      {/* Modal de Configuração do Popup de Ativos */}
      {showPopupConfig && (() => {
        // Detectar tema atual
        const isDarkTheme = document.documentElement.getAttribute('data-bs-theme') === 'dark';
        
        // Estilos baseados no tema
        const modalOverlayStyle: React.CSSProperties = {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: isDarkTheme ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        };
        
        const modalContentStyle: React.CSSProperties = {
          backgroundColor: isDarkTheme ? '#1f242d' : '#ffffff',
          padding: '20px',
          borderRadius: '8px',
          maxWidth: '600px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: isDarkTheme ? '0 4px 6px rgba(0, 0, 0, 0.3)' : '0 4px 6px rgba(0, 0, 0, 0.1)'
        };
        
        const titleStyle: React.CSSProperties = {
          margin: 0,
          color: isDarkTheme ? '#f7f9ff' : '#000000'
        };
        
        const textStyle: React.CSSProperties = {
          fontSize: '14px',
          color: isDarkTheme ? '#cdd5e3' : '#666',
          marginBottom: '15px'
        };
        
        const closeButtonStyle: React.CSSProperties = {
          background: 'none',
          border: 'none',
          fontSize: '24px',
          cursor: 'pointer',
          color: isDarkTheme ? '#cdd5e3' : '#666'
        };
        
        const getFieldLabelStyle = (isChecked: boolean): React.CSSProperties => ({
          display: 'flex',
          alignItems: 'center',
          padding: '10px',
          marginBottom: '8px',
          cursor: 'pointer',
          borderRadius: '4px',
          backgroundColor: isChecked 
            ? (isDarkTheme ? '#2d3748' : '#e8f5e9')
            : (isDarkTheme ? '#151922' : '#f5f5f5'),
          transition: 'background-color 0.2s',
          color: isDarkTheme ? '#f7f9ff' : '#000000'
        });
        
        const buttonSecondaryStyle: React.CSSProperties = {
          padding: '10px 20px',
          backgroundColor: isDarkTheme ? '#394052' : '#6c757d',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px'
        };
        
        const buttonPrimaryStyle: React.CSSProperties = {
          padding: '10px 20px',
          backgroundColor: isDarkTheme ? '#23c067' : '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px'
        };
        
        // Estilos para as abas
        const tabsContainerStyle: React.CSSProperties = {
          display: 'flex',
          borderBottom: `2px solid ${isDarkTheme ? '#2f3542' : '#e0e0e0'}`,
          marginBottom: '20px'
        };
        
        const tabStyle = (isActive: boolean): React.CSSProperties => ({
          padding: '12px 24px',
          cursor: 'pointer',
          border: 'none',
          background: 'none',
          color: isActive 
            ? (isDarkTheme ? '#23c067' : '#007bff')
            : (isDarkTheme ? '#9aa3ba' : '#666'),
          borderBottom: isActive 
            ? `3px solid ${isDarkTheme ? '#23c067' : '#007bff'}`
            : '3px solid transparent',
          fontWeight: isActive ? '600' : '400',
          fontSize: '15px',
          transition: 'all 0.2s',
          marginBottom: '-2px'
        });
        
        const tabContentStyle: React.CSSProperties = {
          minHeight: '300px',
          maxHeight: '400px',
          overflowY: 'auto',
          paddingRight: '5px'
        };
        
        return (
          <div style={modalOverlayStyle} onClick={() => setShowPopupConfig(false)}>
            <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={titleStyle}>Configurar Campos dos Popups</h3>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowPopupConfig(false);
                  }}
                  style={closeButtonStyle}
                >
                  ×
                </button>
              </div>
              
              {/* Abas */}
              <div style={tabsContainerStyle}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab('ativos');
                  }}
                  style={tabStyle(activeTab === 'ativos')}
                >
                  Ativos
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab('gateways');
                  }}
                  style={tabStyle(activeTab === 'gateways')}
                >
                  Gateways
                </button>
              </div>
              
              {/* Conteúdo das Abas */}
              <div style={tabContentStyle}>
                {activeTab === 'ativos' && (
                  <div>
                    <p style={textStyle}>
                      Selecione quais campos deseja exibir no popup dos ativos no mapa:
                    </p>
                    
                    {[
                      { key: 'organizacao', label: 'Organização' },
                      { key: 'beacon', label: 'Beacon' },
                      { key: 'ativo', label: 'Ativo' },
                      { key: 'data', label: 'Data' },
                      { key: 'timestampCaptura', label: 'Timestamp Captura' },
                      { key: 'coordenadas', label: 'Coordenadas' },
                      { key: 'origemCoordenada', label: 'Origem da Coordenada' },
                      { key: 'metodo', label: 'Método' }
                    ].map((field) => (
                      <label
                        key={field.key}
                        style={getFieldLabelStyle(popupConfig[field.key as keyof IPopupAtivoConfig])}
                      >
                        <input
                          type="checkbox"
                          checked={popupConfig[field.key as keyof IPopupAtivoConfig]}
                          onChange={(e) => {
                            const newConfig = {
                              ...popupConfig,
                              [field.key]: e.target.checked
                            };
                            setPopupConfig(newConfig);
                          }}
                          style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '14px' }}>{field.label}</span>
                      </label>
                    ))}
                  </div>
                )}
                
                {activeTab === 'gateways' && (
                  <div>
                    <p style={textStyle}>
                      Selecione quais campos deseja exibir no popup dos gateways no mapa:
                    </p>
                    
                    {[
                      { key: 'organizacao', label: 'Organização' },
                      { key: 'local', label: 'Local' },
                      { key: 'gateway', label: 'Gateway' },
                      { key: 'ultimaAtualizacao', label: 'Última Atualização' },
                      { key: 'latitude', label: 'Latitude' },
                      { key: 'longitude', label: 'Longitude' },
                      { key: 'status', label: 'Status' }
                    ].map((field) => (
                      <label
                        key={field.key}
                        style={getFieldLabelStyle(popupGatewayConfig[field.key as keyof IPopupGatewayConfig])}
                      >
                        <input
                          type="checkbox"
                          checked={popupGatewayConfig[field.key as keyof IPopupGatewayConfig]}
                          onChange={(e) => {
                            const newConfig = {
                              ...popupGatewayConfig,
                              [field.key]: e.target.checked
                            };
                            setPopupGatewayConfig(newConfig);
                          }}
                          style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '14px' }}>{field.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px', paddingTop: '20px', borderTop: `1px solid ${isDarkTheme ? '#2f3542' : '#e0e0e0'}` }}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setPopupConfig({ ...DEFAULT_POPUP_CONFIG });
                    setPopupGatewayConfig({ ...DEFAULT_POPUP_GATEWAY_CONFIG });
                  }}
                  style={buttonSecondaryStyle}
                >
                  Restaurar Padrão
                </button>
                <button
                  type="button"
                  onClick={async (e) => {
                    try {
                      e.preventDefault();
                      console.log("💾 Salvar clicado – enviando popupAtivo e popupGateway para /api/Cache (MAPA_LOCAIS)");
                      await saveMapLocaisCache({
                        popupAtivo: popupConfig,
                        popupGateway: popupGatewayConfig,
                      });
                      setShowPopupConfig(false);
                    } catch (err) {
                      console.error("❌ Erro ao salvar configuração dos popups no cache:", err);
                      alert("Não foi possível salvar. Veja o console (F12) para detalhes.");
                    }
                  }}
                  style={buttonPrimaryStyle}
                >
                  Salvar
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </main>
  );
};

export default LocaisMapPage;